// Portions of this file were produced with the help of a code generation tool and
// subsequently reviewed and edited by a human.
// While some of the code was created by an AI assisted service, manual adjustments
// have been made to ensure correctness, readability, functionality and compliance
// to Avalara's coding standards and guidelines.
// Any future modifications should be approached with caution to avoid
// overwriting the manual changes.

import asyncjs from 'async';
import dateformat from 'dateformat';
import moment from 'moment';
import QueryString from 'query-string';
import _ from 'underscore';
import lodash from 'lodash';
import xml2js, { parseString } from 'xml2js';
import { FilingType } from './enums';
import { APIHelper, FileValidationHelper } from './helpers';
import APIEndpoints from './apiEndpoints';
import encodeDecode from './encodeDecode';
import ENUMS, { ReturnDisplayType, ECListingFlowEnum, ECListingFlowPerTransactionTypesEnum, FilingTypeEnum, IntrastatFlowEnum, LocalListingFlowEnum, TransmissionStatusEnum, VATBookFlowEnum, ExtrastatFlowEnum, PromiseStatus } from './enums';
import ErrorDocumentUtil from './errorDetails/errorDocumentUtil';
import { HTTPError, RestClientInvocationError, TransactionActionError } from './errors';
import fallbackComments from './fallbackData/comments.json';
import fallbackContent from './fallbackData/content.json';
import mockFilingData from './fallbackData/filing/MockFilingData.json';
import HeaderDetails from './headerDetails';
import jsonTransform from './jsonTransform/jsonTransformers';
import Logger, { ALL_MESSAGES, inAppMessage, LOG_LEVELS } from './logger';
import { classifyMtdSubmissionResponse } from './mtdSubmissionResponse';
import fetch from '../utils/fetch-wrapper';
import { fetchWithOnlyJwtAuthHeader } from './fetch-wrapper';
import getClientConfig from './getClientConfig';
import { stripLocalTime } from './dateUtils';
import string from '../utils/stringFunctions';
import apiEndpoints from './apiEndpoints';

const DropDownValueRE = new RegExp(/\[([^\[\]]+)\]/);
const log = new Logger('VR-REST-CLIENT', LOG_LEVELS.DEBUG);
const xmlBuilder = new xml2js.Builder({
    cdata: true
});

const ReportURLValues = 'values';
const ReportURLCsv = 'Csv';
const ReportUrl = 'data';
const CorrectionsUrl = 'data/corrections';
const CorrectionsPeriodsUrl = 'data/corrections/periods';
const LineDetailsUrl = 'lines/detail';
const GuidLength = 34;//This has GUID length 32 and GUID delimiter length at suffix(.) and prefix(_).
const { vrRestApiHost, iamApiHost } = getClientConfig();

function errorHandler(response) {
    if (!response.ok) {
        if ([500, 503, 504].includes(response.status) || !response.status) {
            log.setDictionaryMessage(ALL_MESSAGES.CONNECTION_ERROR);
        }
        //On session expired/logged out, redirect use to login page
        if (response.status === 401) {
            //redirect to login page, this.props.history.push is not working since prop is undefined
            window.open("/", "_self");
        }
        throw new HTTPError(response.status, response.statusText);
    }
    return response;
}

function transactionErrorHandler(responseJson) {
    if (!_.isEmpty(responseJson.failures)) {
        throw new TransactionActionError(409, '409', responseJson.failures);
    }
    return responseJson;
}

function statusChecks(response, statusToErrorCodesMap) {
    if (response.ok) {
        log.debug('statusChecks: responseOK! ', typeof response, response);
        return response;
    }
    if (statusToErrorCodesMap) {
        let match = statusToErrorCodesMap.mapping.find((mapping) => { return mapping.statusCode === response.status; });
        if (match) {
            log.setDictionaryMessage(match.error);
            return Promise.reject(new inAppMessage(match.error));
        }
        else if (statusToErrorCodesMap.default) {
            log.setDictionaryMessage(statusToErrorCodesMap.default);
            return Promise.reject(new inAppMessage(statusToErrorCodesMap.default));
        }
    }
    log.setDictionaryMessage(ALL_MESSAGES.CONNECTION_ERROR);
    return Promise.reject(new inAppMessage(ALL_MESSAGES.CONNECTION_ERROR));
}

/*
 * The response from the request to retrieve filing obligations is very structured, due to using a default Delphi Object serialiser.
 * It looks like:
  {
    countries: [
      { countryCode1: [ {companyId: 1, vatNumbers: [ {vatNumber: "sdf", returns: [ {<filingType>:  {...}]} ]}, ...] },
      ...
    ]
  }


 * This makes it difficult to search, sort, filter, group etc.
 * So instead we denormalise it, so that for each filingType, for each companyId, for each countryCode, we have a single line in an array.
 */
function denormaliseOpenReturns(openReturns) {

    log.debug('denormaliseOpenReturns', openReturns);

    return openReturns.countries.reduce((accumulator, countryEntry) => {
        var countryCodeToSet = countryEntry.contryCode
        log.debug('denormaliseOpenReturns countryEntry', countryEntry, countryCodeToSet);

        accumulator.companies[countryEntry.companyId] = countryEntry.companyName;

        accumulator.summaries.push(_.extend({}, {
            companyName: countryEntry.companyName,
            companyId: countryEntry.companyId,
            companyCode: countryEntry.companyCode,
            countryCode: countryCodeToSet,
            countryCodeLong: countryEntry.countryCodeLong,
            amountSummary: countryEntry.amountSummary,
            errorCount: countryEntry.errorCount
        }));
        _.each(countryEntry.vatNumbers, (vatNumberEntry) => {
            _.each(vatNumberEntry.returns, (filingEntry) => {
                log.debug('denormaliseOpenReturns filingEntry', filingEntry);

                // filter out magic dates from VR
                //Converting 1899 to UTC format in SaaS API 1899 gets converted to 19999 so including 1999 as well
                if (!(!filingEntry.dueDate.startsWith('1899') && !filingEntry.dueDate.startsWith('1999'))) {
                    return;
                }
                log.debug('denormaliseOpenReturns filingEntry', filingEntry.dueDate);
                //The date is in ISO Format examaple
                accumulator.filings.push(_.extend({}, filingEntry, {
                    filingPeriod: {
                        dueDate: filingEntry.dueDate.substr(0, 10)
                    },
                    filingName: ENUMS.fixFilingName(filingEntry.filingTypeId),
                    filingTypeId: ENUMS.getFilingTypeIdFromName(filingEntry.filingTypeId),
                    companyName: countryEntry.companyName,
                    companyId: countryEntry.companyId,
                    companyCode: countryEntry.companyCode,
                    countryCode: countryCodeToSet,
                    countryCodeLong: countryEntry.countryCodeLong,
                    vatNumber: vatNumberEntry.vatNumber,
                    errorCount: countryEntry.errorCount,
                }));
            });
        });
        log.debug('denormaliseOpenReturns accumulator', accumulator);
        return accumulator;
    }, { filings: [], summaries: [], companies: {} })

}


function getMimeTypeFromDocumentType(type) {
    type = typeof type === 'string' ? type.toLowerCase() : 'pdf';
    switch (type) {
        case 'pdf':
            return 'application/pdf';
        case 'xml':
            return 'application/xml';
        case 'json':
            return 'application/json';
        default:
            return 'text/plain';
    }
}

function parseDropDownValues(dropDownValues) {
    _.each(dropDownValues, (value, index) => {
        let key = '';
        let optionValue = value + '';
        //if displayString contains [] we will be using value within the square brackets
        let matches = DropDownValueRE.exec(optionValue);
        //No matches, use displayString as is
        if (matches == null) {
            key = optionValue;
        }
        else if (matches && matches.length > 1) {
            //Get the group 1
            key = matches[1];
        }
        if (key === '') {
            dropDownValues[index] = { displayString: optionValue };
        }
        else {
            dropDownValues[index] = { displayString: optionValue, key: key };
        }
    });
    return dropDownValues;
}

// The meta data now contains the DB questions too.
// These are a similar structure to the User Questions, but they have no GroupName.
// To limit the impact, we will collapse them all into one, with the DB questions having an GroupName of 'Database Questions'
// Then we will let the React component separate them before making the request for the filing.
// A further nuance with DB question: different boxes can have the same question. The question should only be displayed once, and applied to all boxes.
// To deal with this, we will change the database questions so that the BoxId is an array of all the BoxIds the question applies to.

function restructureUserAndDbQuestions(userQuestions, databaseQuestions) {
    let dateformatLength = 10;
    userQuestions = _.isArray(userQuestions) ? userQuestions : [];
    let groupNames = userQuestions.reduce((groupNameArray, question) => {
        const groupName = typeof question.GroupName === 'string' ? question.GroupName : '';
        if (groupNameArray.indexOf(groupName) < 0) {
            groupNameArray.push(groupName);
        }
        return groupNameArray;
    }, []);
    groupNames.sort();
    // We want the DB questions at the end.
    groupNames.push('Database Fields');
    databaseQuestions = _.isArray(databaseQuestions) ? databaseQuestions : [];
    const modifiedDbQuestions = databaseQuestions.map((question) => {
        question.GroupName = 'Database Fields';
        question.BoxID = [question.BoxID];

        //Below condition is regarding ticket SVR-5324, Here time part has been removed from creationDate ,
        //because VR API is sending CreationDate in ISO datetime format.
        //This fields is visible on UI and we have html date calendar it supports date only, not dateTime.
        //Hence below changes required.

        //While sending the same data back to VR API during filing document creation
        //we are attaching current local system time to CreationDate and converting it to ISO format.
        //You can find that logic in filingPage.jsx with covertCreationDateToISOFormat function
        //Same logic is present in Desktop for handling creationDate
        //CreationDate in ISO datetime format is required for ECL and VAT for LU country filing.
        //It generates FileReference field based on CreationDate.

        if (question.BoxDbID === "CreationDate" && question.Visible && question.BoxValue && question.BoxValue.length > dateformatLength) {
            question.BoxValue = question.BoxValue.slice(0, dateformatLength);
        }

        return question;
    });

    const combinedQuestions = userQuestions.concat(Object.values(modifiedDbQuestions).map((question) => {
        question.BoxID = JSON.stringify(question.BoxID);
        return question;
    }));

    return combinedQuestions.reduce((accumulator, question) => {
        const groupName = typeof question.GroupName === 'string' ? question.GroupName : '';

        if (question.FieldType === 0 && _.isArray(question.DropDownValues)) {
            question.DropDownValues = parseDropDownValues(question.DropDownValues);
        }
        else {
            question.DropDownValues = undefined;
        }

        accumulator[groupName].push(question);
        return accumulator;
    }, groupNames.reduce((initialAccumulator, groupName) => {
        initialAccumulator[groupName] = [];
        return initialAccumulator;
    }, {}));
}

function restructureFilingOptions(json) {

    return json.reduce((accumulator, item) => {
        const filingOption = JSON.parse(JSON.stringify(item));
        if (!_.isArray(filingOption.correctionFilterId) || filingOption.correctionFilterId.length <= 0) {
            filingOption.correctionFilterId = [0];
        }
        const baseDetails = Object.assign({}, filingOption, {
            periodId: undefined,
            filingTypeId: undefined,
            validFrom: (typeof filingOption.validFrom === 'string' && !filingOption.validFrom.startsWith('1899')) ? filingOption.validFrom : undefined,
            validUntil: (typeof filingOption.validUntil === 'string' && !filingOption.validUntil.startsWith('1899')) ? filingOption.validUntil : undefined,
        });
        filingOption.periodId.forEach((periodId) => {
            filingOption.filingTypeId.forEach((filingTypeId) => {
                let filingType = ENUMS.ValueToEnum(filingTypeId, FilingTypeEnum, 'filingOptionValue');
                const restructuredDetail = Object.assign({}, baseDetails, {
                    filingTypeId: filingType,
                    filingName: ENUMS.getFilingNameFromFilingType(filingType),
                    periodId,
                    periodName: ENUMS.getPeriodNameFromPeriodId(periodId),
                    outputType: ENUMS.getOutputTypeFromId(filingOption.outputTypeId),
                    validFromTs: baseDetails.validFrom ? new Date(baseDetails.validFrom).getTime() : undefined,
                    validUntilTs: baseDetails.validUntil ? new Date(baseDetails.validUntil).getTime() : undefined,
                });
                accumulator.push(restructuredDetail);
            });
        });
        return accumulator;
    }, []);
}

function normaliseFilingTypeIds(json) {
    return json.reduce((accumulator, item) => {
        const filingOption = JSON.parse(JSON.stringify(item));

        filingOption.filingTypeId.forEach((filingTypeId) => {
            let filingType = ENUMS.ValueToEnum(filingTypeId, FilingTypeEnum, 'filingOptionValue');
            const restructuredDetail = Object.assign({}, filingOption, {
                filingTypeId: filingType,
                filingName: ENUMS.getFilingNameFromFilingType(filingType)
            });
            accumulator.push(restructuredDetail);
        });
        return accumulator;
    }, []);
}

function filterFilingOptionsByCountryAndTypeId(filingOptions, filterParams) {
    filingOptions = filingOptions || [];
    filterParams = filterParams || {};
    return filingOptions.filter(filingOption => (
        (!filterParams.filingTypeId || filingOption.filingTypeId === filterParams.filingTypeId) &&
        (!filterParams.countryCode || filingOption.countryCode === filterParams.countryCode)
    ));
}

function getFilingOptionGroupingKey(filingOption) {
    return [filingOption.countryCode, filingOption.outputType, filingOption.filingTypeId, filingOption.periodId].join('-');
}

function getGroupedCalculatedValidUntilDates(filingOptions) {
    let groupedByKey = filingOptions.reduce((accumulator, filingOption) => {
        const groupingKey = getFilingOptionGroupingKey(filingOption);
        const validFrom = filingOption.validFrom;
        if (!accumulator[groupingKey]) {
            accumulator[groupingKey] = {
                validFromList: [],
                matchingOptions: []
            };
        }
        accumulator[groupingKey].matchingOptions.push(filingOption);
        if (accumulator[groupingKey].validFromList.indexOf(validFrom) < 0) {
            accumulator[groupingKey].validFromList.push(validFrom);
        }

        return accumulator
    }, {});

    for (let key in groupedByKey) {
        groupedByKey[key].validFromList.sort();
        groupedByKey[key].validFromToMap = {};
        groupedByKey[key].validFromList.forEach((validFromDate, idx) => {
            const nextValidFromDate = groupedByKey[key].validFromList[idx + 1];
            groupedByKey[key].validFromToMap[validFromDate] = nextValidFromDate ? moment(nextValidFromDate).add(-1, 'd').format('YYYY-MM-DD') : undefined;
        });
    }

    return groupedByKey;

}

function getFixedValidUntilDateFilingOptions(filingOptions) {
    const groupedCalculatedValidUntilDates = getGroupedCalculatedValidUntilDates(filingOptions);
    return filingOptions.map((filingOption) => {
        const groupKey = getFilingOptionGroupingKey(filingOption);
        let calculatedValidUntil = groupedCalculatedValidUntilDates[groupKey].validFromToMap[filingOption.validFrom];
        const validUntil = (typeof filingOption.validUntil === 'string' && !filingOption.validUntil.startsWith('1899')) ? filingOption.validUntil : undefined;

        // TODO Remove this horrible code !!!
        // The grouping removes the valid 353 returns because there are later VAT Return forms of this format
        // getFilingOptionGroupingKey builds options by filingOption.countryCode-filingOption.outputType-filingOption.filingTypeId-filingOption.periodId
        // Examples:
        //   ES-PDF-VATReturn-1
        //   ES-FixedFormat-VATReturn-1
        //   ES-PDF-VATReturn-0
        //   ES-FixedFormat-VATReturn-0
        //
        // Because the VAT Group return, also has filingTypeId of "VATReturn" and same outputType and periodId and an early validFrom it is getting
        // given a calculated validUntil which is the date of the next return starting; this is hiding it from the options available
        //
        // This is a very bad way to solve this and we should look at better options, but this is specific to try and reduce
        // unwanted impact to other returns
        if (groupKey.includes("ES-") && groupKey.includes("VATReturn") && filingOption.name.includes('353')) {
            calculatedValidUntil = undefined
        }

        return Object.assign({}, filingOption, {
            _validUntil: filingOption.validUntil,
            _normalisedValidUntil: validUntil,
            _calculatedValidUntil: calculatedValidUntil,
            validUntil: validUntil || calculatedValidUntil
        });
    })
}

//Filter filingOptions by Correction Type
//To avoid grouping by correction types which are not used in SaaS
function filterFilingOptionsCorrectionType(filingOptions, filterParams) {
    filingOptions = filingOptions || [];
    return filingOptions
        .filter((filingOption) => {
            if (!_.isArray(filingOption.correctionFilterId)) {
                if (typeof filterParams.correctionFilterId === 'number') {
                    return filingOption.correctionFilterId === filterParams.correctionFilterId;
                }
                return filingOption.correctionFilterId == filterParams.correctionFilterId;
            }
            return filingOption.correctionFilterId.reduce((match, correctionFilterId) => {
                return match || filterParams.correctionFilterId.indexOf(correctionFilterId) >= 0 || filterParams.correctionFilterId.indexOf(correctionFilterId + '') >= 0
            }, false);
        })
}
// TODO: required until the end point supports the filter params.
function filterFilingOptions(filingOptions, filterParams) {
    filingOptions = filingOptions || [];
    filterParams = filterParams || {};
    return filingOptions
        // Doing the filtering until the endpoint supports the query parameters
        .filter((filingOption) => {
            return filterParams.frequency ? filingOption.periodId == filterParams.frequency : true;
        })
        .filter((filingOption) => {
            return filterParams.filingTypeId ? filingOption.filingTypeId == filterParams.filingTypeId : true;
        })
        .filter((filingOption) => {
            return filterParams.countryCode ? filingOption.countryCode == filterParams.countryCode : true;
        })
        .filter((filingOption) => {
            if (filingOption.filingTypeId === FilingTypeEnum.ECLISTING) {
                return (filterParams.flow && filingOption.ecListingFlow) ? filingOption.ecListingFlow.includes(ECListingFlowEnum.props[filterParams.flow].value) : true;
            }
            if ((filingOption.filingTypeId === FilingTypeEnum.LOCALLISTING) && (filingOption.localListingFlow)) {
                return filterParams.flow ? filingOption.localListingFlow.includes(LocalListingFlowEnum.props[filterParams.flow].value) : true;
            }
            if ((filingOption.filingTypeId === FilingTypeEnum.REVERSECHARGELISTING) && (filingOption.localListingFlow)) //In formxml's the local listing enum is also used for reverse charge listings
            {
                return filterParams.flow ? filingOption.localListingFlow.includes(LocalListingFlowEnum.props[filterParams.flow].value) : true;
            }
            if (filingOption.filingTypeId === FilingTypeEnum.INTRASTAT) {
                return (filterParams.flow && filingOption.intrastatFlow) ? filingOption.intrastatFlow.includes(IntrastatFlowEnum.props[filterParams.flow].value) : true;
            }
            if (filingOption.filingTypeId === FilingTypeEnum.VATBOOKS) {
                return (filterParams.flow && filingOption.vatBookFlow) ? filingOption.vatBookFlow.includes(VATBookFlowEnum.props[filterParams.flow].value) : true;
            }
            return true;
        })
        .filter((filingOption) => {
            if (!filterParams.activeOnDate) {
                return true;
            }
            if (filingOption.validFrom && filterParams.activeOnDate < filingOption.validFrom) {
                return false;
            }

            if (filingOption.validUntil && filterParams.activeOnDate > filingOption.validUntil) {
                return false;
            }
            return true;
        });
}

function sortOnKeys(dict) {
    let sortedDict = {};
    const sortedKeys = Object.keys(dict).sort();
    sortedKeys.forEach((key) => {
        sortedDict[key] = dict[key];
    });
    return sortedDict;
}

function filterAndRestructureErrorsAndWarnings(errorsAndWarnings, filterParams) {
    filterParams = filterParams || {};

    const filtered = errorsAndWarnings
        .filter((item) => {
            const hideErrors = typeof filterParams.hideErrors === 'string' ? filterParams.hideErrors.toLowerCase() === 'true' : false;
            return !(hideErrors && item.type.toLowerCase() === 'error');
        })
        .filter((item) => {
            const hideWarnings = typeof filterParams.hideWarnings === 'string' ? filterParams.hideWarnings.toLowerCase() === 'true' : false;
            return !(hideWarnings && item.type.toLowerCase() === 'warning');
        })
        .filter((item) => {
            //Ignore filter if 'All' selected in error category
            return filterParams.errorCategory && filterParams.errorCategory != 2 ? filterParams.errorCategory == item.errorCategory : true;
        })
        .filter((item) => {
            const includeHidden = typeof filterParams.includeHidden === 'string' ? filterParams.includeHidden.toLowerCase() === 'true' : false;
            return includeHidden || !item.hidden;
        });
    const restructured = filtered.reduce((restructured, item) => {
        restructured[item.type] = restructured[item.type] || { name: item.type, count: 0, codes: {} };
        restructured[item.type].count++;
        restructured[item.type].codes[item.code] = restructured[item.type].codes[item.code] || { name: item.code, description: item.codeDescription, count: 0, subTypes: {} };
        restructured[item.type].codes[item.code].count++;
        restructured[item.type].codes[item.code].subTypes[item.subType] = restructured[item.type].codes[item.code].subTypes[item.subType] || { name: item.subType, errorCategory: item.errorCategory, items: [] };
        restructured[item.type].codes[item.code].subTypes[item.subType].items.push(item);

        return restructured;
    }, {});

    let sorted = sortOnKeys(restructured);
    for (let type in sorted) {
        sorted[type] = sortOnKeys(sorted[type])
    }
    return { asList: filtered, asStructure: sorted };
}

function enrichGetHistoryResponse(json, additionalInfo, request) {
    return new Promise((resolve, reject) => {
        log.debug('enrichGetHistoryResponse - json: ', json);
        log.debug('enrichGetHistoryResponse - additionalInfo: ', additionalInfo);
        log.debug("enrichGetHistoryResponse - request: ", request)

        let enrichedJson = {};

        const filingType = additionalInfo.filingType;

        let genericResultObject = {};

        switch (filingType) {
            // API is returning a 'reports' element for VAT Return
            // dates are badly formed
            case FilingTypeEnum.VATRETURN:

                // Build a standard object reading from reports element of response
                genericResultObject = Object.assign([], json.reports);

                // For each row add the filing type
                let jsonBuilder = [];
                let vatBoxesBuilder = [];
                let taxableBasisBoxesBuilder = [];

                genericResultObject.forEach((item) => {
                    // Correct everything we have a problem with
                    item.filingType = additionalInfo.filingType;
                    item.returnPeriodicity = item.declarationType;
                    item.periodStartDate = moment.utc(item.periodStartDate).format('YYYY-MM-DD');
                    item.periodEndDate = moment.utc(item.periodEndDate).format('YYYY-MM-DD');
                    item.returnType = item.vatType;

                    // Reset builders for each item
                    vatBoxesBuilder = [];
                    taxableBasisBoxesBuilder = [];

                    // Create grouped by box type to simplify the react layer
                    item.boxes.forEach(box => {
                        switch (box.boxType) {
                            case 0:
                                taxableBasisBoxesBuilder.push(box);
                                break;
                            case 1:
                                vatBoxesBuilder.push(box);
                                break;
                            default:
                                log.debug("Unhandled boxType: ", box.boxType)
                        }
                    });
                    item.vatBoxes = Object.assign([], vatBoxesBuilder);
                    item.taxableBasisBoxes = Object.assign([], taxableBasisBoxesBuilder);

                    // Update the builder array
                    jsonBuilder.push(item);

                });

                // Create new object with the combined results
                enrichedJson = Object.assign([], jsonBuilder, genericResultObject);
                break;

            // API is return a 'lines' element for ECL + Intra with a different response body
            // dates are called different things
            case FilingTypeEnum.ECLISTING:
            case FilingTypeEnum.LOCALLISTING:
            case FilingTypeEnum.INTRASTAT:
            case FilingTypeEnum.OSSIMPORT:
            case FilingTypeEnum.OSSUNION:
            case FilingTypeEnum.OSSNONUNION:
            case FilingTypeEnum.VATBOOKS:
                genericResultObject = Object.assign([], json.lines);
                log.debug("genericResultObject", genericResultObject);

                let lineBuilder = [];
                genericResultObject.forEach((item) => {
                    item.filingType = additionalInfo.filingType;
                    item.countryCode = item.country || additionalInfo.countryCode;
                    item.returnPeriodicity = item.returnPeriodType || item.declarationType;
                    item.periodStartDate = moment.utc(item.returnStartDate || item.periodStartDate).format('YYYY-MM-DD');
                    item.periodEndDate = moment.utc(item.returnEndDate || item.periodEndDate).format('YYYY-MM-DD');
                    item.canReopen = true;
                    delete item.returnStartDate;
                    delete item.returnEndDate;

                    lineBuilder.push(item)

                });
                enrichedJson = Object.assign([], lineBuilder, genericResultObject);
                break;
            default:
                log.debug('Unhandled return type: ', filingType);
                enrichedJson = json;
        }
        const sortedEnrichedJson = enrichedJson.sort(function (a, b) {
            return new Date(a.periodStartDate) - new Date(b.periodStartDate)
        });
        return resolve(sortedEnrichedJson);
    });
}

function restructureVATCodeSimulatorResponse(json) {
    log.debug('restrctureVATCodeSimulatorReponse: json: ', json);

    // Create an element containing what we want to show in ux
    let jsonBuilder = {};

    if (!json.VATCode) {
        json.VATCode = 'Error';
        jsonBuilder = { Response: 'Unknown VAT Code' };
    } else {
        jsonBuilder = {
            "VAT Return": json['VAT Return'],
            Intrastat: json.Intrastat,
            "EC Listing": json['EC Listing'],
            "Local Listing": json['Local Listing'],
            Ledger: json.Ledger
        };
    }

    json.title = json.VATCode;
    if (json.GenericVATCode) {
        json.title += ' - as generic code: ' + json.GenericVATCode;
    }
    json.ux = jsonBuilder;
    return (json);
}

function restructureSearchResults(json) {

    return json.transactionDocuments.map((item) => {

        const invoiceNumber = (item.invoice && item.invoice.invoiceProperties && item.invoice.invoiceProperties.invoiceNumber) ||
            (item.deleteLog && item.deleteLog.invoiceNumber) ||
            (item.errors && item.errors.invoiceNumber) ||
            (item.warnings && item.warnings.invoiceNumber) ||
            (item.documentsOnHold && item.documentsOnHold.invoiceNumber);

        const id = (item.invoice && item.invoice.id) || null;

        const displayData = { appearsIn: [], summary: [], messages: [], doc: {} };

        if (item.invoice) {
            displayData.appearsIn.push('Documents');
            displayData.invoice = item.invoice;
            displayData.doc.invoiceDate = item.invoice.invoiceProperties.invoiceDate;
            displayData.doc.transactionDate = item.invoice.transactionDate;
            displayData.doc.supplier = item.invoice.invoiceProperties.supplier;
            displayData.doc.customer = item.invoice.invoiceProperties.customer;
            displayData.doc.supplierInvoice = item.invoice.invoiceProperties.invoiceNumberSupplier;
            displayData.doc.total = item.invoice.invoiceProperties.totalValue;
            displayData.doc.currency = item.invoice.invoiceProperties.currency;
            displayData.doc.type = item.invoice.invoiceProperties.documentType_str;
        }

        if (item.deleteLog) {
            displayData.appearsIn.push('Delete Log');
            displayData.deleteLog = item.deleteLog.deleteLogDetails;
            displayData.summary = item.deleteLog.deleteLogDetails.map((each) => {
                return 'Deleted by "' + each.initials + '" on ' + dateformat(each.deletionDate, 'yyyy-mm-dd HH:MM:ss') + '. ' + each.reason;
            });
        }

        if (item.errors) {
            displayData.appearsIn.push('Errors');
            displayData.errors = item.errors.errorDetails;

            item.errors.errorDetails.map((each) => {
                displayData.messages.push("Error: " + each.errorMessage);
                return each.errorMessage;
            });
        }

        if (item.warnings) {
            displayData.appearsIn.push('Warnings');
            displayData.warnings = item.warnings.warningDetails;

            item.warnings.warningDetails.map((each) => {
                displayData.messages.push("Warning: " + each.errorMessage);
                return each.errorMessage;
            });
        }

        if (item.documentsOnHold) {
            displayData.appearsIn.push('Documents On Hold');
            displayData.documentsOnHold = item.documentsOnHold;
            displayData.summary = item.documentsOnHold.documentsOnHoldDetails.map((each) => {
                return each.country + ' on hold until ' + each.noProcessingBefore;
            });
        }

        displayData.invoiceNumber = invoiceNumber;
        item.display = displayData;
        item.id = id;

        return item;
    })
}
function restructureRates(json) {
    log.debug('restructureRates: json: ', json);
    let jsonBuilder = [];

    json.forEach((row) => {
        let elementBuilder = {};
        elementBuilder.type = Object.keys(row)[0];
        elementBuilder.value = row[elementBuilder.type];
        jsonBuilder.push(elementBuilder)
    });
    return jsonBuilder
}

function restructureReportingDocumentXML(json) {
    log.debug('restructureReportingDocumentXML: json: ', json);

    parseString(json.documentData, { explicitArray: false }, function (err, result) {
        log.debug('restructureReportingDocumentXML: xml->json', result);
        json.documentJson = result;
    });

    json.documentJson.flatFieldMap = jsonTransform.doObjectMapToFlatReference(json.documentJson, 'documentJson');

    return json;
}

function filterVatCodeResponse(response = [], filterParams = []) {

    // here for 'All' company search  filter params comes as {userCode: 'All', companyId: -1, companyCode: 'All', companyList:[{},{}...]}
    // for a specific company search , the filter params comes as {userCode: 'DEZ1', companyId: 2703, companyCode: 'DEZ1'}

    log.debug('filterVatCodeResponse() params: ', filterParams);

    // by pass the userCode validation in case of All company
    return response
        .filter((row) => {
            return filterParams.userCode ? (row.userCode === filterParams.userCode || (filterParams.companyId === -1 && filterParams.userCode === 'All' && filterParams.companyCode === 'All')) : true;
        })
        .filter((row) => {
            if (filterParams.vatCode) {
                const arrFilterParams = filterParams.vatCode.split(';').map(vatCode => vatCode.trim());
                for (let idx in arrFilterParams) {
                    const item = arrFilterParams[idx];
                    if (row.vatCode.toLowerCase().includes(item.toLowerCase())) {
                        return true;
                    }
                }
                return false;
            }
            return true;
        })
        .filter((row) => {
            if (filterParams.filterCountryCodes) {
                let filterCountryCodes = filterParams.filterCountryCodes.split(';').map(code => code.toLowerCase().trim());
                return (filterCountryCodes.includes(row.country.toLowerCase()));
            }
            return true;
        })
        .filter((row) => {
            // First check if it's an "All" company search
            if (filterParams.companyId === -1 && filterParams.userCode === 'All' && filterParams.companyCode === 'All') {
                // If companyList is provided, filter by reporterId
                if (filterParams.companyList && filterParams.companyList.length > 0) {
                    // Create a Set of company IDs for faster lookup
                    const companyIds = new Set(
                        filterParams.companyList.map(company => company.id.toString())
                    );
                    // Always keep rows with userCode "Generic"
                    if (row.userCode.toLowerCase() === "generic") {
                        return true;
                    }
                    // For non-Generic rows, filter by reporterId
                    return companyIds.has(row.reporterId.toString());
                }
                // If no companyList provided, return all results
                return true;
            }
            // In case of specific company search, return all results in this filter
            return true;
        })
}

function cleanAndFilterVatCodeResponse(json, params) {
    // Filter results by filterParams if there are params
    log.debug('cleanAndFilterVatCodeResponse() json; params: ', json, params);
    if (params !== null) { json = filterVatCodeResponse(json, params) }

    // Modify data based on display rules
    return json.map((row) => {

        if (row.userCode === 'Generic') {
            row.country = undefined;
            row.combinationIDInvoice = undefined;
            row.combinationIDCredit = undefined;
            row.vatRate = undefined;
        }
        row.deductibleVAT = Number(row.deductibleVAT).toFixed(2);
        row.proRata = Number(row.proRata).toFixed(2);
        row.vatRate = Number(row.vatRate).toFixed(2);
        row.withholdingRate = Number(row.withholdingRate).toFixed(2);
        row.startDate = (row.startDate.startsWith('1899')) ? undefined : (dateformat(stripLocalTime(row.startDate), 'yyyy-mm-dd'));
        row.endDate = (row.endDate.startsWith('1899')) ? undefined : (dateformat(stripLocalTime(row.endDate), 'yyyy-mm-dd'));

        return row;
    });
}

function addAttachmentUri(aJson) {
    let uri = vrRestApiHost + '/api/transaction/attachments/download/' + aJson.attachmentFileUrl;
    aJson.attachmentUri = uri;
    return aJson;
}

function filterGenericReportingCombinationHistory(reportingCombinationHistory) {
    log.debug('filterGenericReportingCombinationHistory', reportingCombinationHistory);
    reportingCombinationHistory.data.forEach(element => {
        if (element.userCode === "-101" || element.userCode === "-100") {
            element.userCode = 0;
        };
    });
    return reportingCombinationHistory;
}

/**
 * Builds a repeated query parameter suffix, e.g. '&status=Failed&status=Processing'.
 * Returns an empty string when there are no values so existing URIs stay unchanged.
 */
function buildRepeatedQueryParams(name, values) {
    const list = [].concat(values || []).filter((value) => value !== undefined && value !== null && `${value}` !== '');
    return list.length ? `&${QueryString.stringify({ [name]: list })}` : '';
}

function getBlob(uri, token, mimeType, onSuccess, onError) {
    onSuccess = typeof onSuccess === 'function' ? onSuccess : (json, resolve, reject) => resolve(json);
    onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);

    return new Promise((resolve, reject) => {
        return fetch(uri, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'content-type': mimeType
            },
            cache: 'no-cache'
        })
            .then(errorHandler)
            .then((response) => {
                return response.blob();
            })
            .then((blob) => {
                log.debug('getBlob: success: ', uri, blob);
                onSuccess(blob, resolve, reject);
            })
            .catch((error) => {
                log.debug('getBlob: error:', uri, error);
                onError(error, resolve, reject);
            });
    });
}

function get(uri, token, onSuccess, onError) {
    onSuccess = typeof onSuccess === 'function' ? onSuccess : (json, resolve, reject) => resolve(json);
    onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
    let statusCode;
    return new Promise((resolve, reject) => {
        return fetch(uri, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then(errorHandler)
            .then((response) => {
                statusCode = response.status;
                return response.json();
            })
            .then((json) => {
                log.debug('get: success: ', uri, json);
                onSuccess(json, resolve, reject);
            })
            .catch((error) => {
                //response.json will throw an error in case empty response is sent from the API
                if (statusCode >= 200 && statusCode < 300) {
                    onSuccess({}, resolve, reject);
                } else {
                    log.debug('get: error:', uri, error);
                    onError(error, resolve, reject);
                }
            });
    });
}

function post(uri, token, body, onSuccess, onError, rawData = false) {
    onSuccess = typeof onSuccess === 'function' ? onSuccess : (json, resolve, reject) => resolve(json);
    onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
    let statusCode;
    let headers = {};
    if (!rawData) {
        headers['Content-Type'] = 'application/json';
    }
    return new Promise((resolve, reject) => {

        return fetch(uri, {
            method: 'POST',
            mode: 'cors',
            headers: headers,
            body: rawData ? body : JSON.stringify(body),
            cache: 'no-cache'
        })
            .then(errorHandler)
            .then((response) => {
                statusCode = response.status;
                return response.json();
            })
            .then((json) => {
                log.debug('post: success: ', uri, json);
                onSuccess(json, resolve, reject);
            })
            .catch((error) => {
                //response.json will throw an error in case empty response is sent from the API
                if (statusCode >= 200 && statusCode < 300) {
                    onSuccess({}, resolve, reject);
                } else {
                    log.debug('post: error:', uri, error);
                    onError(error, resolve, reject);
                }
            });
    });
}

function put(uri, token, body, onSuccess, onError) {
    onSuccess = typeof onSuccess === 'function' ? onSuccess : (response, resolve, reject) => resolve(response);
    onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
    let statusCode;
    return new Promise((resolve, reject) => {
        return fetch(uri, {
            method: 'PUT',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache',
            body: JSON.stringify(body)
        })
            .then(errorHandler)
            .then((response) => {
                statusCode = response.status;
                return response.json();
            })
            .then(json => {
                onSuccess(json, resolve, reject);
            })
            .catch((error) => {
                if (statusCode >= 200 && statusCode < 300) {
                    onSuccess({}, resolve, reject);
                } else {
                    log.debug('put: error:', uri, error);
                    onError(error, resolve, reject);
                }
            });
    });
}

function getClosedReturn(filingType, params) {
    let returnType = '';
    switch (filingType) {
        case FilingTypeEnum.VATRETURN:
            returnType = FilingType.getString(FilingType.VAT);
            break;
        case FilingTypeEnum.ECLISTING:
            returnType = FilingType.getString(FilingType.EC_LISTING);
            break;
        case FilingTypeEnum.LOCALLISTING:
            returnType = FilingType.getString(FilingType.LOCAL_LISTING);
            break;
        case FilingTypeEnum.INTRASTAT:
            returnType = FilingType.getString(FilingType.INTRASTAT);
            break;
        case FilingTypeEnum.OSSIMPORT:
            returnType = FilingType.getString(FilingType.OSSIMPORT);
            break;
        case FilingTypeEnum.OSSUNION:
            returnType = FilingType.getString(FilingType.OSSUNION);
            break;
        case FilingTypeEnum.OSSNONUNION:
            returnType = FilingType.getString(FilingType.OSSNONUNION);
            break;
        default:
            returnType = 'UNKNOWN'
    }

    let additionalInfo = { filingType: filingType, countryCode: params.countryCode };

    return new Promise((resolve, reject) => {
        let url = '';
        if (filingType === FilingTypeEnum.VATBOOKS) {
            const { featureFlags } = getClientConfig();
            if (!featureFlags?.EnableReopenVATBook) {
                return reject();
            }
            url = `${vrRestApiHost}/api/v4/filing/types/vatbook/${params.companyId}/history/${params.countryCode}/${params.startDate}/${params.endDate}`;
        } else {
            let uri = vrRestApiHost + '/api/reports/' + returnType + '/history';
            let queryParams = Object.assign({}, params, { country: params.countryCode });
            url = uri + '?' + QueryString.stringify(queryParams);
        }
        log.debug('getClosedReturn - URL : ', url);

        fetch(url, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then((response) => {
                if (response.status === 200) {
                    response.json().then((json) => {
                        log.debug('getClosedReturn: json: ', json);
                        let enrichResponsePromise = enrichGetHistoryResponse(json, additionalInfo, url);
                        log.debug('getClosedReturn: Enrich response promise status: ', enrichResponsePromise);
                        resolve(enrichResponsePromise);
                    });
                } else {
                    reject({ status: response.status, message: `Request failed with status ${response.status}` });
                }
            })
            .catch((error) => {
                reject(error);
            })
    })
}

function deletePromise(uri, token, onSuccess, onError) {
    onSuccess = typeof onSuccess === 'function' ? onSuccess : (response, resolve, reject) => resolve(response);
    onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
    let statusCode;
    return new Promise((resolve, reject) => {
        return fetch(uri, {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache'
        })
            .then((response) => {
                statusCode = response.status;
                return response.json();
            })
            .then((json) => {
                log.debug('delete: success: ', uri, json);
                onSuccess(json, resolve, reject);
            })
            .catch((error) => {
                //response.json will throw an error in case empty response is sent from the API
                if (statusCode >= 200 && statusCode < 300) {
                    onSuccess({}, resolve, reject);
                } else {
                    log.debug('delete: error:', uri, error);
                    onError(error, resolve, reject);
                }
            });
    });
}

function getBlobType(uri, token, mimeType, onSuccess, onError) {

    onSuccess = typeof onSuccess === 'function' ? onSuccess : (json, resolve, reject) => resolve(json);
    onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);

    return new Promise((resolve, reject) => {

        return fetch(uri, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'content-type': mimeType
            },
            cache: 'no-cache'
        })
            .then(errorHandler)
            .then((response) => {
                return response.blob();
            })
            .then((blob) => {
                log.debug('get: success: ', uri, blob);
                onSuccess(blob, resolve, reject);
            })
            .catch((error) => {
                log.debug('get: error:', uri, error);
                onError(error, resolve, reject);
            });
    });
}

function getFilingTransactionsDummy(returnDisplayType) {
    switch (+returnDisplayType) {
        case ReturnDisplayType.SHOW_ALL.value:
            return Promise.resolve(mockFilingData.returnsByTransactions.all);
        case ReturnDisplayType.ONLY_NON_CORRECTIONS.value:
            return Promise.resolve(mockFilingData.returnsByTransactions.onlyNonCorrections);
        case ReturnDisplayType.CORRECTIONS_IN_THIS_PERIOD.value:
            return Promise.resolve(mockFilingData.returnsByTransactions.onlyCorrections);
        case ReturnDisplayType.SHOW_CORRECTIONS_GROUP_BY_PERIOD.value:
            return Promise.resolve(mockFilingData.returnsByTransactions.correctionsGroupedByPeriod);
        case ReturnDisplayType.SHOW_CORRECTIONS_GROUP_BY_PERIOD_INCLUDING_ORIGINAL_DOCUMENTS.value:
            return Promise.resolve(mockFilingData.returnsByTransactions.correctionsGroupedByPeriodIncludingDocs);
        default:
            return Promise.reject("UNEXPECTED FILTER TYPE");
    }
}

function getFilingBoxValuesDummy(returnDisplayType) {
    switch (+returnDisplayType) {
        case ReturnDisplayType.SHOW_ALL.value:
            return Promise.resolve(mockFilingData.returnsBoxValues.all);
        case ReturnDisplayType.ONLY_NON_CORRECTIONS.value:
            return Promise.resolve(mockFilingData.returnsBoxValues.onlyNonCorrections);
        case ReturnDisplayType.CORRECTIONS_IN_THIS_PERIOD.value:
            return Promise.resolve(mockFilingData.returnsBoxValues.onlyCorrections);
        case ReturnDisplayType.SHOW_CORRECTIONS_GROUP_BY_PERIOD.value:
            return Promise.resolve(mockFilingData.returnsBoxValues.correctionsGroupedByPeriod);
        case ReturnDisplayType.SHOW_CORRECTIONS_GROUP_BY_PERIOD_INCLUDING_ORIGINAL_DOCUMENTS.value:
            return Promise.resolve(mockFilingData.returnsBoxValues.correctionsGroupedByPeriodIncludingDocs);
        default:
            return Promise.reject("UNEXPECTED FILTER TYPE");
    }
}

function buildDynamicReportExportPayload(params) {
    const { companyIds, customerId, userName, columnSchema, ...rest } = params;
    const payload = { ...rest };
    if (columnSchema !== undefined) {
        payload.filters = columnSchema;
    }
    return payload;
}

function getSuperApiErrorMessage(data) {
    return data?.error?.details?.[0]?.description
        || data?.error?.details?.[0]?.message
        || data?.error?.message
        || 'An error occurred during export';
}

function buildDynamicReportExportHeaders(enableAvalaraIdentity, enableAvalaraIdentityJwt) {
    const headers = {
        accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Avalara-Client': '1',
        'avalara-version': '3.0',
        'X-Correlation-ID': `${Date.now()}-${Math.random().toString(36).slice(2)}`
    };

    if (!(enableAvalaraIdentity || enableAvalaraIdentityJwt)) {
        headers.Authorization = sessionStorage.token;
    }

    return headers;
}

function makeRequestForExport(url, methodType, params, logComponent, additionalHeaders) {
    return new Promise((resolve, reject) => {
        return fetch(url, {
            method: methodType,
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
                ...additionalHeaders
            },
            body: methodType.toLowerCase() === "post" ? JSON.stringify(params) : null,
            cache: "no-cache",
        })
            .then((response) => response.json())
            .then((json) => {
                log.debug(logComponent + ": response:", json);
                resolve(json);
            })
            .catch((error) => {
                log.debug(logComponent + ": error:", error);
                reject(error);
            });
    });
}

async function makeRequestForExportV2(url, methodType, params, logComponent, additionalHeaders) {
    try {
        const apiResponse = await fetch(url, {
            method: methodType,
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
                ...additionalHeaders
            },
            body: methodType.toLowerCase() === "post" ? JSON.stringify(params) : null,
            cache: "no-cache",
        });

        const httpStatus = apiResponse.status;
        const isSuccess = apiResponse.ok;
        log.debug(logComponent + ": response:", { httpStatus, isSuccess });

        const response = {
            isSuccess,
            status: httpStatus,
            error: null
        };

        if (!isSuccess) {
            try {
                const data = await apiResponse.json();
                response.error = {
                    type: 'error',
                    message: data.detail || data.title || 'An error occurred during export',
                    status: httpStatus,
                    data
                };
            } catch {
                response.error = {
                    type: 'error',
                    message: 'An error occurred during export',
                    status: httpStatus
                };
            }
            throw response.error;
        }

        return response;
    } catch (error) {
        log.debug(logComponent + ": error:", error);
        throw error;
    }
}

function getRequestConfig(params) {
    const filingTypeConfig = {};
    filingTypeConfig[FilingTypeEnum.VATRETURN] = { paramName: "vatReturnType", resourceName: "vat" };
    filingTypeConfig[FilingTypeEnum.ECLISTING] = { paramName: "ecListingReportType", resourceName: "eclisting" };
    filingTypeConfig[FilingTypeEnum.LOCALLISTING] = { paramName: "localListingReportType", resourceName: "locallisting" };
    filingTypeConfig[FilingTypeEnum.EXTRASTAT] = { paramName: "extrastatReportType", resourceName: "extrastat" };
    filingTypeConfig[FilingTypeEnum.INTRASTAT] = { paramName: "intrastatReportType", resourceName: "intrastat" };
    filingTypeConfig[FilingTypeEnum.OSSIMPORT] = { paramName: "ossReportType", resourceName: FilingTypeEnum.OSSIMPORT };
    filingTypeConfig[FilingTypeEnum.OSSUNION] = { paramName: "ossReportType", resourceName: FilingTypeEnum.OSSUNION };
    filingTypeConfig[FilingTypeEnum.OSSNONUNION] = { paramName: "ossReportType", resourceName: FilingTypeEnum.OSSNONUNION };

    if (params.filingType === FilingTypeEnum.VATBOOKS) {
        const featureFlags = getClientConfig().featureFlags;
        if (!featureFlags?.EnableReopenVATBook) {
            return null;
        }
        return {
            requestResourceName: "vatbook",
            apiVersion: '/v4',
            requestBody: {
                companyId: params.companyId,
                countryCode: params.countryCode,
                declarationType: params.declarationType,
                startDate: params.periodStartDate,
                endDate: params.periodEndDate,
                returnType: params.vatType
            }
        };
    }

    const config = filingTypeConfig[params.filingType] || {};
    return {
        requestParameterName: config.paramName || '',
        requestResourceName: config.resourceName || '',
        apiVersion: '',
        requestBody: {}
    };
};

function buildRequestUri(config, params) {
    const queryParams = {
        companyId: params.companyId,
        country: params.country || params.countryCode,
        periodicity: params.returnPeriodicity,
        startDate: params.periodStartDate,
        endDate: params.periodEndDate,
        fileName: params.fileName
    };

    if (config.requestParameterName) {
        queryParams[config.requestParameterName] = params.returnType;
    }

    const queryString = params.filingType !== FilingTypeEnum.VATBOOKS
        ? '?' + QueryString.stringify(queryParams)
        : '';

    return vrRestApiHost + '/api' + config.apiVersion + '/reports/' + config.requestResourceName + '/reopen' + queryString;
};

function buildRequestInit(requestBody) {
    return {
        method: "PUT",
        mode: "cors",
        cache: "no-cache",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
    };
};

function handleError(error, suppressError) {
    if (!suppressError) {
        if (error.status === 409 || error.status === 400) {
            log.setDictionaryMessage(ALL_MESSAGES.FAILED_REOPEN_REPORT);
        } else if (error.status === 404) {
            log.setDictionaryMessage(ALL_MESSAGES.REPORT_NOT_FOUND);
        }
    }
};

const API_CLIENT = {

    getAccountSettings() {
        log.debug('getAccountSettings()');
        const uri = vrRestApiHost + '/api/settings';
        log.debug('getAccountSettings: fetching from: ', uri);
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then(errorHandler)
                .then((response) => {
                    return response.json();
                })
                .then((json) => {
                    log.debug('getAccountSettings: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('getAccountSettings: error:', error);
                    reject(error);
                });
        });
    },

    updateAccountSetting(name, value) {
        log.debug('updateAccountSetting(): name: %s, value: %s', name, value);
        const uri = vrRestApiHost + '/api/settings';
        const body = JSON.stringify({ name, value, owner: "", type: "0" });
        log.debug('updateAccountSetting(): PUT %s, body', uri, body);

        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'PUT',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                cache: 'no-cache',
                body: body
            })
                .then((response) => {
                    if (response.status >= 400 && response.status < 500) {
                        reject(response);
                    }
                    try {
                        const res = JSON.parse(response)
                        return res.json();
                    } catch (err) {
                        log.debug("Did not receive JSON, instead received: " + response)
                        return response;
                    }
                })
                .then((json) => {
                    log.debug('updateAccountSetting: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    reject(error);
                });

        });
    },
    // TODO: Check if this is used.
    getComments() {
        log.debug('getComments ');
        return fetch(vrRestApiHost + '/api/comments', {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then((response) => {
                return response.json();
            })
            .then((json) => {
                log.debug('getComments: success: ', json);
                return json;
                // return fallbackComments;
            })
            .catch((error) => {
                log.debug('getComments: error:', error);
                return fallbackComments;
            });
    },

    getCompanyDetailsById(id) {
        log.debug("getCompanyDetailsById()");
        const enableLegalRep = getClientConfig().featureFlags?.EnableLegalRepTab;
        const enableItalyLookups = getClientConfig().featureFlags?.EnableItalyLookups;
        const urlEndpoint = enableLegalRep || enableItalyLookups ? APIEndpoints.getCompanyDetailsV4 : APIEndpoints.getCompanyDetails;
        let URL = APIHelper.getURL(urlEndpoint, null, {
            id: `${id}`
        });
        log.debug("getCompanyDetailsById() - URL: ", URL);
        return APIHelper.get(URL, sessionStorage.token);
    },

    // Fetch companies summary without filtering based on the user's company grants
    getCompaniesSummaryV1() {
        log.debug('getCompaniesSummaryV1()');
        const url = APIHelper.getURL(APIEndpoints.getCompanies, { summary: "true" });
        log.debug('getCompaniesSummaryV1() - url: ', url);
        return APIHelper.get(url, sessionStorage.token);
    },

    // A 404 means the customer is not linked to an IAM tenant; any other failure is rethrown
    async getIamTenantInfo() {
        const url = APIHelper.getURL(APIEndpoints.getIamTenantInfo);
        log.debug('getIamTenantInfo() - url: ', url);
        try {
            const json = await APIHelper.get(url, sessionStorage.token);
            return { iamTenantId: json?.iamTenantId ?? null };
        } catch (error) {
            if (error?.status === 404) {
                return { iamTenantId: null };
            }
            throw error;
        }
    },

    // Fetch companies summary filtered based on the user's company grants
    getCompaniesSummaryV3() {
        log.debug('getCompaniesSummaryV3()');
        const url = APIHelper.getURL(APIEndpoints.getCompaniesSummaryV3);
        log.debug('getCompaniesSummaryV3() - url: ', url);
        return APIHelper.get(url, sessionStorage.token);
    },

    doValidateVatNumber(vatnumber) {
        const uri = '/api/VATNumbers/' + vatnumber + '/technicalCheck';
        return new Promise((resolve, reject) => {
            return fetch(vrRestApiHost + uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            }).then((response) => {
                log.debug('validatevatNumber OK' + response);
                return response.json();
            })
                .then((json) => {
                    log.debug('validatevatNumber: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('validatevatNumber: error:', error);
                    reject(error);
                });
        });
    },
    doSaveEsSIICertificate: (esSIICertificateDetails) => {
        return new Promise((resolve, reject) => {
            const uri = `/api/efiling/es/saveCertificate/${esSIICertificateDetails.vatNumberId}`;
            const formData = new FormData();
            formData.append("certificatePassword", btoa(esSIICertificateDetails.certificatePassword));
            formData.append("certificate", esSIICertificateDetails.filesToBeSent);

            fetch(vrRestApiHost + uri, {
                method: 'POST',
                mode: 'cors',
                body: formData,
                cache: 'no-cache'
            }).then((response) => {
                log.debug('doSaveEsSIICertificate response' + response);
                return response;
            }).then((response) => {
                if (response.ok) {
                    log.debug('doSaveEsSIICertificate - success: ', response);
                    resolve();
                } else {
                    log.debug('doSaveEsSIICertificate - error: ', response);
                    response.json().then((json) => { reject(json); });
                }
            }).catch((error) => {
                log.debug('doSaveEsSIICertificate - error: ', error);
                reject(error);
            });
        });
    },
    isSiiCertificatePresent(vatNumberId) {
        const uri = `${vrRestApiHost}/api/efiling/es/getCertificate/${vatNumberId}`;
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            }).then((response) => {
                if (response.status === 200) {
                    response.json().then((json) => { resolve(json); });
                    log.debug('doGetSiiCertificate - 200: ', response);
                } else if (response.status === 404) {
                    log.debug('doGetSiiCertificate - 404: ', response);
                    resolve({ thumbPrint: "" });
                } else {
                    response.json().then((json) => { reject(json); });
                }
            })
                .catch((error) => {
                    log.debug('doGetSiiCertificate: error:', error);
                    reject(error);
                });
        });
    },
    doValidateSiiCertificate: (certificateFile, password) => {
        return new Promise((resolve, reject) => {
            const uri = `/api/efiling/es/validatecertificate`;
            const formData = new FormData();
            formData.append("certificatePassword", btoa(password));
            formData.append("certificate", certificateFile);

            fetch(vrRestApiHost + uri, {
                method: 'POST',
                mode: 'cors',
                body: formData,
                cache: 'no-cache'
            }).then((response) => {
                log.debug('ValidateSiiCertificate response' + response);
                return response;
            }).then((response) => {
                if (response.ok) {
                    log.debug('ValidateSiiCertificate - success: ', response);
                    response.json().then((json) => { resolve(json); });
                } else {
                    log.debug('ValidateSiiCertificate - error: ', response);
                    response.json().then((json) => { reject(json); });
                }
            }).catch((error) => {
                log.debug('ValidateSiiCertificate - error: ', error);
                reject(error);
            });
        });
    },
    doDeleteSiiCertificate(vatNumberId) {
        return new Promise((resolve, reject) => {
            log.debug('doDeleteSiiCertificate ');
            const uri = `${vrRestApiHost}/api/efiling/es/deleteCertificate/${vatNumberId}`
            return fetch(uri, {
                method: 'DELETE',
                mode: 'cors',
                cache: 'no-cache'
            }).then((response) => {
                log.debug('doDeleteSiiCertificate' + response);
                return response;
            }).then((response) => {
                if (response.ok) {
                    log.debug('doDeleteSiiCertificate - success: ', response);
                    resolve();
                } else {
                    log.debug('doDeleteSiiCertificate - error: ', response);
                    response.json().then((json) => { reject(json); });
                }
            })
                .catch((error) => {
                    log.debug('doDeleteSiiCertificate: error:', error);
                    reject(error);
                });
        });
    },
    getVATNumberRules() {
        const uri = '/api/Content/VatNumberRules';
        return new Promise((resolve, reject) => {
            return fetch(vrRestApiHost + uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            }).then((response) => {
                log.debug('getVATNumberRules OK' + response);
                return response.json();
            })
                .then((json) => {
                    log.debug('getVATNumberRules: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('getVATNumberRules: error:', error);
                    reject(error);
                });
        });
    },


    getReportPeriodicityOptions() {
        const uri = '/api/Content/ReportPeriodicityOptions';
        return new Promise((resolve, reject) => {
            return fetch(vrRestApiHost + uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then(statusChecks)
                .then((response) => {
                    log.debug('getReportPeriodicityOptions OK' + response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('getReportPeriodicityOptions: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('getReportPeriodicityOptions: error:', error);
                    reject(error);
                });
        });

    },

    createCompany(company) {
        log.debug('createCompany ', company);
        log.debug('createCompany REST client ', JSON.stringify(company));
        return new Promise((resolve, reject) => {
            return fetch(vrRestApiHost + '/api/Companies', {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(company),
                cache: 'no-cache'
            })
                .then(errorHandler)
                .then((response) => {
                    log.debug('createCompany OK' + response);
                    return response;
                })
                .then((json) => {
                    log.debug('createCompany: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('createCompany: error:', error);
                    reject(error);
                });
        });
    },

    exportCompanies(callback) {
        log.debug('exportCompanies()')
        const URL = `${vrRestApiHost}/api/Companies/export`
        return getBlob(
            URL,
            sessionStorage.token,
            'application/xml',
            (blob, resolve, reject) => {
                resolve(blob);
            },
            (error, resolve, reject) => {
                reject(error);
            });
    },

    updateCompany(company, companyId) {
        log.debug('updateCompany ' + companyId);
        const enableLegalRep = getClientConfig().featureFlags?.EnableLegalRepTab;
        const enableItalyLookups = getClientConfig().featureFlags?.EnableItalyLookups;
        const version = enableLegalRep || enableItalyLookups ? 'V4/' : '';
        const URL = `${vrRestApiHost}/api/${version}Companies/${encodeURIComponent(companyId)}`;
        return put(URL, sessionStorage.token, company);
    },

    updateRdiTenantId() {
        log.debug('updateRdiTenantId() ');
        const uri = vrRestApiHost + '/bffapi/v2/RdiTenant';
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'PUT',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
            })
                .then((response) => {
                    if (response.status === 200) {
                        return resolve(response);
                    }
                    response.text()
                        .then((responseText) => {
                            return reject({ statusText: responseText });
                        });
                })
                .catch((error) => {
                    log.debug('updateRdiTenantId: error:', error);
                    reject(error);
                });
        });
    },

    getAccessToken() {
        log.debug('getAccessToken()');
        const uri = vrRestApiHost + '/bffapi/v2/accesstoken';
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    return response.text();
                })
                .then((text) => {
                    log.debug('getAccessToken: success: ', text);
                    resolve(text);
                })
                .catch((error) => {
                    log.debug('getAccessToken: error:', error);
                    reject(error);
                });
        });
    },

    deleteCompany(companyId) {
        const relUri = string.format(apiEndpoints.deleteCompany, companyId);
        const uri = `${vrRestApiHost}${relUri}`;

        return fetch(uri, {
            method: 'DELETE',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then((response) => {
                log.debug('deleteCompany OK' + response);
                return response;
            })
            .then((json) => {
                log.debug('deleteCompany: success: ', json);
                return json;
            })
            .catch((error) => {
                log.debug('deleteCompany: error:', error);
                return error;
            });
    },

    deleteManyCompanies(companyIds) {
        log.debug('deleteManyCompanies(): companyIds: ', companyIds);
        return new Promise((resolve, reject) => {
            const deleteTasks = companyIds.map((companyId) => {
                const relUri = string.format(apiEndpoints.deleteCompany, companyId);
                const uri = `${vrRestApiHost}${relUri}`;
                // const url = `${vrRestApiHost}/api/Companies/${encodeURIComponent(companyId)}`;
                return (callback) => {
                    deletePromise(
                        uri,
                        sessionStorage.token,
                        (json, resolve, reject) => {
                            log.debug('deleteManyCompanies():  deletePromise: success:', uri, json);
                            callback(null, json);
                        },
                        (error, resolve, reject) => {
                            log.debug('deleteManyCompanies():  deletePromise: error:', uri, error);
                            callback(error, null);
                        }
                    )
                };
            });
            asyncjs.parallelLimit(deleteTasks, 3, (error, data) => {
                if (error) {
                    log.debug('deleteManyCompanies(): parallelLimit.callback: error: ', error);
                    return reject(error);
                }
                log.debug('deleteManyCompanies(): parallelLimit.callback: data: ', data);
                const deleteCompaniesResponse = Array.isArray(data) ? data : [];
                log.debug('deleteManyCompanies().parallelLimit.callback: deleteCompaniesResponse: ', deleteCompaniesResponse);
                return resolve(deleteCompaniesResponse);
            });
        });
    },

    importCompanies(files = []) {
        const URL = `${vrRestApiHost}/api/Companies/import`;
        if (!Array.isArray(files)) {
            files = [files];
        }
        let formData = new FormData();
        files.forEach((file) => {
            formData.append(file.name, file, file.name);
        })

        log.debug("importCompanies() - URL : ", URL);

        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                body: formData
            })
                .then((response) => {
                    log.debug("importCompanies: response", response)
                    return response
                })
                .then((response) => {
                    if (response.status === 200) {
                        log.debug('importCompanies: success', response);
                        resolve(response);
                    } else {
                        log.debug('importCompanies: error:', URL, response);
                        reject(response);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    importExchangeRateXml(files = [], removeExistingRates) {
        const URL = `${vrRestApiHost}/api/ExchangeRates/import/${removeExistingRates}`;
        if (!Array.isArray(files)) {
            files = [files];
        }
        let formData = new FormData();
        files.forEach((file) => {
            formData.append(file.name, file, file.name);
        })

        log.debug("importExchangeRateXml() - URL : ", URL);

        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                body: formData
            })
                .then((response) => {
                    log.debug("importExchangeRateXml: response", response)
                    return response
                })
                .then((response) => {
                    if (response.status === 200) {
                        log.debug('importExchangeRateXml: success', response);
                        return response.json();
                    } else {
                        log.debug('importExchangeRateXml: error:', URL, response);
                        reject(response);
                    }
                }).then((json) => {
                    log.debug("importExchangeRateXml Json:", json);
                    resolve(json);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    AddVATGroupParents(companyId, vatGroup) {
        log.debug('AddVATGroupParents ', JSON.stringify(vatGroup));
        const uri = vrRestApiHost + '/api/Companies/' + encodeURIComponent(companyId) + '/VatGroup/parents'
        return fetch(uri, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(vatGroup),
            cache: 'no-cache'
        })
            .then((response) => {
                log.debug('AddVATGroupParents OK' + response);
                return response;
            })
            .then((json) => {
                log.debug('AddVATGroupParents: success: ', json);
                return json;
            })
            .catch((error) => {
                log.debug('AddVATGroupParents: error:', error);
                return error;
            });
    },

    AddVATGroupChildren(companyId, vatGroup) {
        log.debug('AddVATGroupChildren ', vatGroup, companyId);
        const uri = vrRestApiHost + '/api/Companies/' + encodeURIComponent(companyId) + '/VatGroup/children'
        return fetch(uri, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(vatGroup),
            cache: 'no-cache'
        })
            .then((response) => {
                log.debug('AddVATGroupChildren OK' + response);
                return response;
            })
            .then((json) => {
                log.debug('AddVATGroupChildren: success: ', json);
                return json;
            })
            .catch((error) => {
                log.debug('AddVATGroupChildren: error:', error);
                return error;
            });
    },

    getVATGroups() {
        log.debug('getVATGroups()');
        const uri = vrRestApiHost + '/api/VatGroups';
        log.debug('getVATGroups: fetching from: ', uri);
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then(errorHandler)
                .then((response) => {
                    return response.json();
                })
                .then((json) => {
                    log.debug('getVATGroups: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('getVATGroups: error:', error);
                    reject(error);

                });

        });
    },
    deleteVATGroupChildren(companyId) {
        log.debug('deleteVATGroupChildren ');
        const uri = vrRestApiHost + '/api/Companies/' + encodeURIComponent(companyId) + '/VatGroup/children'
        return fetch(uri, {
            method: 'DELETE',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then((response) => {
                log.debug('deleteVATGroupChildren OK' + response);
                return response;
            })
            .then((json) => {
                log.debug('deleteVATGroupChildren: success: ', json);
                return json;
            })
            .catch((error) => {
                log.debug('deleteVATGroupChildren: error:', error);
                return error;
            });
    },
    deleteVATGroupParents(companyId) {
        log.debug('deleteVATGroupChildren ');
        const uri = vrRestApiHost + '/api/Companies/' + encodeURIComponent(companyId) + '/VatGroup/parents'
        return fetch(uri, {
            method: 'DELETE',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then((response) => {
                log.debug('deleteVATGroupParents OK' + response);
                return response;
            })
            .then((json) => {
                log.debug('deleteVATGroupParents: success: ', json);
                return json;
            })
            .catch((error) => {
                log.debug('deleteVATGroupParents: error:', error);
                return error;
            });
    },

    getOpenReturns(filterParams) {
        log.debug('getOpenReturns(): filterParams: ', filterParams);
        let URL = APIHelper.getURL(APIEndpoints.getOpenReturns, filterParams);
        log.debug('getOpenReturns(): URL: ', URL);
        let restructureOpenReturns = (json = {}) => {
            if (_.isEmpty(json.filingObligations)) {
                return json;
            }
            let filingObligations = json.filingObligations;
            let reportMapper = (report) => {
                let dueDate = `${report.dueDate}`.substr(0, 10);
                return {
                    "companyCode": report.companyCode,
                    "companyId": report.companyId,
                    "companyName": report.companyName,
                    "countryCode": report.country,
                    "countryCodeLong": report.countryCodeLong,
                    "dueDate": dueDate,
                    "endDate": `${report.endDate}`.substr(0, 10),
                    "startDate": `${report.startDate}`.substr(0, 10),
                    "filingName": ENUMS.fixFilingName(report.reportType),
                    "filingTypeId": ENUMS.getFilingTypeIdFromName(report.reportType),
                    "reportingStatus": report.reportingStatus,
                    "vatNumber": report.vatNumber,
                    "displayType": report.displayType
                };
            }
            return Object.assign({}, json, {
                filingObligations: filingObligations.map(reportMapper)
            });
        };
        return APIHelper.get(URL, sessionStorage.token, (json, resolve, reject) => {
            resolve(restructureOpenReturns(json));
        }, undefined, undefined, undefined, false);
    },

    getFilingValues(filterParams) {
        const { enableCorrections, enableCorrectionsTestData } = getClientConfig();
        if (enableCorrectionsTestData) {
            return getFilingBoxValuesDummy(filterParams.returnDisplayType);
        }

        let uri;
        if (enableCorrections) {
            let url = vrRestApiHost + '/api/v2/filing/types/vat';
            uri = [url, filterParams.companyId, ReportUrl, filterParams.countryCode, filterParams.dueDateBefore, filterParams.dueDateAfter, filterParams.periodicity, filterParams.returnDisplayType].join('/');
        } else {
            let url = vrRestApiHost + '/api/filing/types/vat';
            uri = [url, filterParams.companyId, ReportUrl, filterParams.countryCode, filterParams.dueDateBefore, filterParams.dueDateAfter, filterParams.periodicity].join('/');
        }

        log.debug('getFilingValues: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getBoxDescriptions(filterParams) {
        const url = vrRestApiHost + '/api/v2/filing/types';
        const uri = [url, filterParams.reportType, filterParams.companyId, 'box-descriptions', filterParams.countryCode, filterParams.periodEndDate, filterParams.frequency].join('/');
        log.debug('getBoxDescriptions: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getCorrectionPeriods(filterParams) {
        const enableVatBookCorrectionApi = getClientConfig().featureFlags?.EnableCorrectionFiltersVATBook;
        let apiVersion = 'v2';
        let filingType = 'vat';
        const paramsArray = [
            filterParams.companyId,
            CorrectionsPeriodsUrl,
            filterParams.countryCode,
            filterParams.dueDateBefore,
            filterParams.dueDateAfter,
            filterParams.periodicity];
        if (enableVatBookCorrectionApi && filterParams.type === FilingTypeEnum.VATBOOKS) {
            apiVersion = 'v4';
            filingType = 'vatbook';
            paramsArray.push(filterParams.returnDisplayType);
        }

        const url = `${vrRestApiHost}/api/${apiVersion}/filing/types/${filingType}`;
        const uri = [
            url,
            ...paramsArray
        ].join('/');
        return get(uri, sessionStorage.token);
    },

    getECCorrectionPeriods(filterParams) {
        let apiVersion = 'v4';
        let filingType = 'ECListing';
        const paramsArray = [
            filterParams.companyId,
            CorrectionsPeriodsUrl,
            filterParams.countryCode,
            filterParams.dueDateBefore,
            filterParams.dueDateAfter,
            ECListingFlowPerTransactionTypesEnum.props[filterParams.flow].value,
            filterParams.periodicity,
            filterParams.returnDisplayType];
        const url = `${vrRestApiHost}/api/${apiVersion}/filing/types/${filingType}`;
        const uri = [
            url,
            ...paramsArray
        ].join('/');
        return get(uri, sessionStorage.token);
    },

    getGroupedCorrections(filterParams) {
        if (!filterParams.selectedCorrectionPeriod) {
            return Promise.reject();
        }
        const url = vrRestApiHost + '/api/v2/filing/types/vat';
        const uri = [
            url,
            filterParams.companyId,
            CorrectionsUrl,
            filterParams.countryCode,
            filterParams.dueDateBefore,
            filterParams.dueDateAfter,
            filterParams.periodicity,
            filterParams.returnDisplayType,
            filterParams.selectedCorrectionPeriod.year || filterParams.selectedCorrectionPeriod.correctionYear,
            filterParams.selectedCorrectionPeriod.period || filterParams.selectedCorrectionPeriod.correctionPeriod
        ].join('/');
        return get(uri, sessionStorage.token);
    },

    getECGroupedCorrections(filterParams) {
        let correctionStartDate = filterParams.selectedCorrectionPeriod?.correctionStartDate;
        correctionStartDate = (correctionStartDate && dateformat(correctionStartDate, 'yyyy-mm-dd')) || '';
        let correctionEndDate = filterParams.selectedCorrectionPeriod?.correctionEndDate;
        correctionEndDate = (correctionEndDate && dateformat(correctionEndDate, 'yyyy-mm-dd')) || '';
        const url = vrRestApiHost + '/api/v4/filing/types/eclisting';
        const uri = [
            url,
            filterParams.companyId,
            CorrectionsUrl,
            filterParams.countryCode,
            filterParams.dueDateBefore,
            filterParams.dueDateAfter,
            correctionStartDate || '1899-12-30',
            correctionEndDate || '1899-12-30',
            ECListingFlowPerTransactionTypesEnum.props[filterParams.flow].value,
            filterParams.periodicity,
            filterParams.returnDisplayType,
        ].join('/');
        return get(uri, sessionStorage.token);
    },

    getLocalListingFilingValues(filterParams) {
        const url = vrRestApiHost + '/api/filing/types/LocalListing';
        log.debug('getLocalListingFilingValues(): filterParams: ', filterParams);
        if (filterParams.flow) { //convert enum to int value
            if (!LocalListingFlowEnum.props[filterParams.flow]) {
                throw new RestClientInvocationError('Unknown local listing flow type');
            }

            filterParams.listingType = LocalListingFlowEnum.props[filterParams.flow].value;
            delete filterParams.flow;
        } else {
            filterParams.listingType = LocalListingFlowEnum.props['Sales'].value;
        }
        const qs = QueryString.stringify(filterParams);
        const uri = [url, filterParams.companyId, ReportUrl, filterParams.countryCode, filterParams.dueDateBefore, filterParams.dueDateAfter, filterParams.listingType].join('/');
        log.debug('getLocalListingFilingValues: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getReverseChargeListingFilingValues(params) {
        const url = vrRestApiHost + '/api/filing/types/ReverseChargeListing';
        log.debug('getReverseChargeListingFilingValues(): params: ', params);

        if (params.flow) { //convert enum to int value
            if (!LocalListingFlowEnum.props[params.flow]) {
                throw new RestClientInvocationError('Unknown reverse charge local listing flow type');
            }
            params.listingType = LocalListingFlowEnum.props[params.flow].value;
            delete params.flow;
        }

        const qs = QueryString.stringify(params);
        const uri = [url, params.companyId, ReportUrl, params.countryCode, params.dueDateBefore, params.dueDateAfter, params.listingType].join('/');
        log.debug('getReverseChargeListingFilingValues: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    doExportVatBoxDetailsCSV(filterParams) {
        log.debug('doExportVatBoxDetailsCSV(): filterParams: ', filterParams);
        let url = vrRestApiHost + '/api/filing/types/vat/';
        url += encodeURIComponent(filterParams.companyId) + '/' + ReportURLValues + '/' + encodeURIComponent(filterParams.boxName) + '/' + ReportURLCsv;

        const { boxName, ...filterParamsWithoutBoxName } = filterParams;  //Remove box name, it does not need to be added as a query parameter
        const { companyId, ...filterParamsUpdated } = filterParamsWithoutBoxName;  //Remove companyId, it does not need to be added as a query parameter

        log.debug('doExportVatBoxDetailsCSV(): filterParams: ', filterParamsUpdated);

        const qs = QueryString.stringify(filterParamsUpdated);
        const uri = [url, qs].join('?');
        log.debug('doExportVatBoxDetailsCSV: fetching from: ', uri);
        return getBlobType(
            uri,
            sessionStorage.token,
            'txt/csv',
            (blob, resolve, reject) => {
                resolve(blob);
            },
            (error, resolve, reject) => {
                reject(error);
            });
    },

    getFilingValueDetails(apiParams = {}) {
        log.debug('getFilingValueDetails(): filterParams: ', apiParams);
        let queryParams = Object.assign({}, apiParams, {
            boxName: undefined,
            companyId: undefined
        });
        let URL = APIHelper.getURL(APIEndpoints.getFilingValueDetails, queryParams, {
            companyId: apiParams.companyId,
            boxName: apiParams.boxName
        });
        return APIHelper.get(URL, sessionStorage.token);
    },

    getLocalListingValueDetails(params) {
        log.debug('getLocalListingValueDetails(): params: ', params);
        let url = vrRestApiHost + '/api/filing/types/locallisting';

        log.debug('getLocalListingValueDetails(): params: ', params);
        var uri = [url, params.companyId, ReportURLValues, params.countryCode, params.listingLineType].join('/');

        const queryParams =
        {
            endDate: params.endDate,
            startDate: params.startDate,
            vatNumberCustomer: params.vatNumberCustomer
        };
        const qs = QueryString.stringify(queryParams);
        uri = [uri, qs].join('?');
        log.debug('getLocalListingValueDetails: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getReverseChargeListingValueDetails(params) {
        log.debug('getReverseChargeListingValueDetails(): params: ', params);
        let url = vrRestApiHost + '/api/filing/types/reversechargelisting';

        log.debug('getReverseChargeListingValueDetails(): params: ', params);
        var uri = [url, params.companyId, ReportURLValues, params.countryCode, params.listingLineType].join('/');
        const queryParams =
        {
            endDate: params.endDate,
            flowType: params.flowType,
            startDate: params.startDate,
            vatNumber: params.vatNumber
        };
        const qs = QueryString.stringify(queryParams);
        uri = [uri, qs].join('?');
        log.debug('getReverseChargeListingValueDetails: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getFilingTransactions(params) {

        const { enableCorrections, enableCorrectionsTestData } = getClientConfig();

        if (enableCorrectionsTestData) {
            return getFilingTransactionsDummy(params.returnDisplayType);
        }

        log.debug('getFilingTransactions(): params: ', params);
        let url = vrRestApiHost + '/api/filing/types/vat';
        let uri = [
            url,
            params.companyId,
            'transactions',
            params.countryCode,
            params.startDate,
            params.endDate,
            params.frequency,
            params.transactionType,
            params.page,
            params.lines].join('/');

        uri = [uri, QueryString.stringify({ returnType: enableCorrections ? params.returnDisplayType : ReturnDisplayType.SHOW_ALL.value })].join('?');

        log.debug('getFilingTransactions(): fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    async downloadFilingTransactionsCsv(params) {
        const { enableCorrections } = getClientConfig();

        let url = `${vrRestApiHost}/api/filing/types/vat`;
        let returnDisplayType = enableCorrections ? params.returnDisplayType : ReturnDisplayType.SHOW_ALL.value;

        let uri = [
            url,
            params.companyId,
            'transactions',
            params.countryCode,
            params.startDate,
            params.endDate,
            params.frequency,
            params.transactionType,
            params.page,
            params.lines].join('/')
            + "?dataFormat=" + params.dataFormat
            + "&returnType=" + returnDisplayType;

        const mimeType = getMimeTypeFromDocumentType("text/csv");
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'content-type': mimeType
                },
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('downloadFilingTransactionsCsv: success: ', typeof response, response);
                    return response.blob();
                })
                .then((blob) => {
                    log.debug('downloadFilingTransactionsCsv: got blob: ', typeof blob, blob);
                    resolve(blob)
                })
                .catch((error) => {
                    log.debug('downloadFilingTransactionsCsv: error:', error);
                    reject(error);
                });
        });
    },

    getFilingMetaData(filterParams) {
        const url = vrRestApiHost + '/api/filing/types';
        log.debug('getFilingMetaData(): filterParams: ', filterParams);
        var uri = [url, filterParams.type, filterParams.companyId, 'form', encodeURIComponent(filterParams.formXMLFileName)].join('/');
        const params =
        {
            startDate: filterParams.startDate
        }

        const qs = QueryString.stringify(params);
        uri = [uri, qs].join('?');
        log.debug('getUserQuestions: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getUserQuestions(filterParams) {
        let flowType = 0; //default value is 0 as per delphi api convention.
        const { enableCorrections, featureFlags } = getClientConfig();
        const useV4Endpoint = featureFlags?.UseV4FilingFormEndpoint;
        const enableNewMetaDataForECListing = featureFlags?.EnableNewMetaData;
        log.debug('getUserQuestions(): filterParams: ', filterParams);

        //Updated report type for VAT & VAT books to match with API expected value
        switch (filterParams.type) {
            case 'VATReturn':
                filterParams.type = 'vat';
                break;
            case 'VATBooks':
            case 'VATBook':
                filterParams.type = 'vatbook';
                break;
            case 'Intrastat':
                flowType = (filterParams.flowType) ? IntrastatFlowEnum.props[filterParams.flowType].value : flowType;
                filterParams.type = filterParams.type.toLowerCase();
                break;
            case 'Extrastat':
                flowType = (filterParams.flowType) ? ExtrastatFlowEnum.props[filterParams.flowType].value : flowType;
                filterParams.type = filterParams.type.toLowerCase();
                break;
            case 'OSSImport':
            case 'OSSUnion':
            case 'OSSNonUnion':
                flowType = ENUMS.getOSSReportTypeIdFromFilingTypeId(filterParams.type);
                filterParams.type = filterParams.type.toLowerCase();
                break;
            default:
                filterParams.type = filterParams.type.toLowerCase();
                break;
        }

        let apiVersion = null;
        let returnDisplayType = null;

        if (enableNewMetaDataForECListing && filterParams.type === 'eclisting') {
            apiVersion = 'v5';
            returnDisplayType = filterParams.returnDisplayType;
        } else if (useV4Endpoint) {
            apiVersion = 'v4';
            returnDisplayType = filterParams.returnDisplayType;
        } else if (enableCorrections && filterParams.type === 'vat') {
            apiVersion = 'v2';
            returnDisplayType = filterParams.returnDisplayType;
        }

        const url = _.chain([
            vrRestApiHost,
            'api',
            apiVersion,
            'filing/types',
            filterParams.type,
            filterParams.companyId,
            'form',
            encodeURIComponent(filterParams.formXMLFileName),
            filterParams.frequency,
            flowType,
            returnDisplayType
        ])
            .filter(el => el !== null)
            .value()
            .join('/');

        const params = {
            startDate: filterParams.startDate,
            endDate: filterParams.endDate
        };

        if (useV4Endpoint || (enableNewMetaDataForECListing && filterParams.type === 'eclisting')) {
            // keys on correctionPeriod object vary, need to check both possibilities
            const correctionPeriod = filterParams.correctionPeriod;
            params.correctionYear = lodash.defaultTo(correctionPeriod?.year, correctionPeriod?.correctionYear);
            params.correctionMonth = lodash.defaultTo(correctionPeriod?.period, correctionPeriod?.correctionPeriod);

            // normalise date format, strip off time part that is included for some return types but not others
            params.startDate = moment(params.startDate, ['YYYY-MM-DD', moment.ISO_8601]).format('YYYY-MM-DD');
            params.endDate = moment(params.endDate, ['YYYY-MM-DD', moment.ISO_8601]).format('YYYY-MM-DD');
        }

        const qs = QueryString.stringify(params);
        const uri = [url, qs].join('?');
        log.debug('getUserQuestions: fetching from: ', uri);

        return get(
            uri,
            sessionStorage.token,
            (json, resolve, reject) => {
                log.debug('getUserQuestions: success: ', json);
                let genericFormData = lodash.defaultTo(json.GenericFormData, {});
                let userQuestions = lodash.defaultTo(genericFormData.UserQuestions, []);
                userQuestions = _.sortBy(userQuestions, item => item.OrderNumber); //sort user questions by order number to display them in correct order
                let otherVatBoxes = lodash.defaultTo(genericFormData.OtherVatBoxes, []);
                otherVatBoxes = otherVatBoxes.map((entry) => {
                    return Object.assign(entry, {
                        Question: entry.BoxDescription,
                        //API don't send back any answers, it will always be empty string
                        BoxValue: '',
                        FieldType: entry.BoxType
                    });
                });

                let databaseFields = lodash.defaultTo(genericFormData.DatabaseFields, []);
                let userAndDBQuestions = restructureUserAndDbQuestions(userQuestions.concat(otherVatBoxes), databaseFields);
                resolve(userAndDBQuestions);
            },
            (error, resolve, reject) => {
                if (error.status === 500) {
                    log.setDictionaryMessage(ALL_MESSAGES.CANNOT_LOAD_REPORT_META_DATA);
                }

                log.debug('getUserQuestions: error:', error);
                reject(error);
            }
        );
    },

    getFilingDocumentName(formXmlId, filingTypeId, body, featureFlag) {
        log.debug('getFilingDocumentName(): formXmlId: %s, filingTypeId: %s, body:  ', formXmlId, filingTypeId, body);

        if ((filingTypeId === FilingTypeEnum.INTRASTAT) && (body.reportData.reportType)) {
            body.reportData.reportType = IntrastatFlowEnum.props[body.reportData.reportType].value;
        }

        if ((filingTypeId === FilingTypeEnum.EXTRASTAT) && (body.reportData.reportType)) {
            body.reportData.reportType = ExtrastatFlowEnum.props[body.reportData.reportType].value;
        }

        let filingTypeUrl = '/api/filing/types/';
        if (featureFlag?.data?.EnableAutoAdjustments && filingTypeId === FilingTypeEnum.VATRETURN) {
            filingTypeUrl = '/api/v4/filing/types/';
        }

        if (featureFlag?.data?.EnableNewMetaData && filingTypeId === FilingTypeEnum.ECLISTING) {
            filingTypeUrl = '/api/v4/filing/types/';
        }

        const url = vrRestApiHost + filingTypeUrl + ENUMS.getFilingNameForUrlFromFilingTypeId(filingTypeId) + '/' +
            encodeURIComponent(body.companyId) + '/form/' + encodeURIComponent(formXmlId);

        log.debug('getFilingDocumentName: fetching from: ', url);

        return new Promise((resolve, reject) => {

            return fetch(url, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body),
                cache: 'no-cache'
            })
                .then((response) => {
                    let checkResult = statusChecks(
                        response,
                        {
                            mapping: [{ statusCode: 409, error: ALL_MESSAGES.REPORT_ALREADY_APPROVED }],
                            default: ALL_MESSAGES.CONNECTION_ERROR
                        })
                    return checkResult;
                })
                .then((response) => {
                    return response.json();
                })
                .then((json) => {
                    var resultObject = [];
                    json.fileUrl.forEach((fileUrl) => {
                        var responseObj = {
                            fileExtension: json.fileExtension || fileUrl.split('.').pop(),
                            uri: vrRestApiHost + '/api/reports/download/' + fileUrl,
                            warningMessage: json.warningMessage,
                            manualCorrectionBoxes: json.manualCorrectionBoxes,
                            isBoxValueAutoCorrected: json.isBoxValueAutoCorrected,
                            filingDocumentFileName: (json.fileUrl.length > 1 ? fileUrl : json.fileName)
                        };
                        resultObject.push(responseObj);
                    });
                    resolve(resultObject);
                })
                .catch((error) => {
                    log.debug('getFilingDocumentName: error:', error);
                    reject(error);
                });
        });
    },

    getFilingDocumentAsBlob(type, uri) {
        log.debug('getFilingDocumentAsBlob(): type %s, uri:', type, uri);
        const mimeType = getMimeTypeFromDocumentType(type);
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'content-type': mimeType
                },
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('getFilingDocumentAsBlob: success: ', typeof response, response);
                    return response.blob();
                })
                .then((blob) => {
                    log.debug('getFilingDocumentAsBlob: got blob: ', typeof blob, blob);
                    resolve(blob)
                })
                .catch((error) => {
                    log.debug('getFilingDocumentAsBlob: error:', error);
                    reject(error);
                });
        });
    },
    /**
     * HEAD request to BFF download URL to get Content-Length before loading XML.
     * Uses same auth/correlation headers as other BFF calls.
     * @param {string} uri - Full URL (e.g. vrRestApiHost + '/api/reports/download/' + fileName)
     * @returns {Promise<Response>} - Response with headers (no body)
     */
    headFilingDocument(uri) {
        log.debug('headFilingDocument(): uri:', uri);
        return fetch(uri, { method: 'HEAD', mode: 'cors', cache: 'no-cache' });
    },

    getFilingDocumentAsText(type, uri) {
        log.debug('getFilingDocumentAsText(): type %s, uri:', type, uri);
        const mimeType = getMimeTypeFromDocumentType(type);
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'content-type': mimeType
                },
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('getFilingDocumentAsText: success: ', typeof response, response);
                    return response.text();
                })
                .then((text) => {
                    log.debug('getFilingDocumentAsText: got text: ', typeof text);
                    resolve(text)
                })
                .catch((error) => {
                    log.debug('getFilingDocumentAsText: error:', error);
                    reject(error);
                });
        });
    },
    getFilingOptions(filterParams) {
        const url = vrRestApiHost + '/api/filingOptions';
        log.debug('getFilingOptions(): filterParams: ', filterParams);
        const qs = QueryString.stringify(filterParams);
        const uri = [url, qs].join('?');
        log.debug('getFilingOptions: fetching from: ', uri);
        return new Promise((resolve, reject) => {
            //{type: "0", countryCode: "AT", activeOnDate: "2018-01-14", periodicity: 0, correctionType: 0}
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                //Filing options being a master data, we cae safely use broweser cache behaviour to improve performance
                cache: 'no-cache'
            })
                .then((response) => {
                    return response.json();
                })
                .then((json) => {
                    log.debug('getFilingOptions: success: ', json);

                    const filteredFilingOptions = filterFilingOptionsByCountryAndTypeId(
                        normaliseFilingTypeIds(json),
                        filterParams
                    );

                    log.debug('getFilingOptions: resolving: ', filteredFilingOptions);

                    return resolve(filteredFilingOptions)
                })
                .catch((error) => {
                    log.debug('getFilingTypes: error:', error);
                    reject(error);
                });
        });
    },

    fetchFilteredFilingOptionsFromAPI(filterParams) {
        const url = vrRestApiHost + '/api/v2/filteredfilingoptions';
        log.debug('fetchFilteredFilingOptionsFromAPI(): filterParams: ', filterParams);
        const qs = QueryString.stringify(filterParams);
        const uri = [url, qs].join('?');
        log.debug('fetchFilteredFilingOptionsFromAPI: fetching from: ', uri);
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    return response.json();
                })
                .then((json) => {
                    log.debug('fetchFilteredFilingOptionsFromAPI: success: ', json);
                    return resolve(json)
                })
                .catch((error) => {
                    log.debug('fetchFilteredFilingOptionsFromAPI: error:', error);
                    reject(error);
                });
        });
    },

    getContent() {
        // simply return fallbackContent until we have content api
        log.debug('getContent ');
        return new Promise((resolve, reject) => {
            resolve(fallbackContent);
        });
    },

    uploadFile(file) {
        log.debug('uploadFile ', file);
        let data = new FormData();
        data.append(file.name, file);
        let statusCode;
        return new Promise((resolve, reject) => {
            fetch(vrRestApiHost + '/api/files', {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                body: data
            })
                .then((response) => {
                    log.clearDictionaryError(ALL_MESSAGES.DUPLICATE_FILE);
                    log.clearDictionaryError(ALL_MESSAGES.LARGE_FILE);
                    statusCode = response.status;
                    log.debug('uploadFile: success: ', response, file.name);
                    switch (response.status) {
                        case 409:
                            reject({ status: response.status, statusText: 'Duplicate file', message: file.name });
                            break;
                        case 413:
                            reject({ status: response.status, statusText: 'Payload too large' });
                            break;
                        case 406:
                            reject({ status: response.status, statusText: 'Not acceptable' });
                            break;
                        case 400:
                            return response.json();
                    }
                    resolve()
                })
                .then((json) => {
                    log.debug('uploadFile: error: ', json);
                    reject({ status: statusCode, message: json.message })
                })
                .catch((error) => {
                    log.debug('uploadFile: error [%s]: ', typeof error, error instanceof TypeError, error instanceof Response, error instanceof SyntaxError, error);
                    if (error instanceof TypeError /* && error.message === "Failed to fetch" */) {
                        return reject({ status: 503, statusText: 'Connection refused', message: 'Could not connect to ' + vrRestApiHost });
                    }
                    else if (error instanceof SyntaxError) {
                        reject({ status: 400, statusText: 'Could not parse json response', message: error.status + ' ' + error.statusText });
                    }
                    else if (error instanceof Response) {
                        error.json()
                            .then((json) => {
                                log.debug('uploadFile: error: parsed JSON body: ', json);
                                reject({ status: 400, statusText: 'Bad request', message: error.status + ' ' + error.statusText + ': ' + json.message });

                            })
                            .catch((jsonError) => {
                                log.debug('uploadFile: error: failed to parse JSON body: ', jsonError);
                                reject({ status: 400, statusText: 'Bad request', message: error });

                            })
                    }
                    else {
                        reject({ status: 400, statusText: 'Unknown error', message: error.status + ' ' + error.statusText });
                    }

                });
        });
    },

    getProcessedDocuments(apiParams = {}) {
        log.debug('getProcessedDocuments() - apiParams: ', apiParams);
        const companyId = apiParams.companyId || '';
        const startDate = apiParams.startDate || '';
        const endDate = apiParams.endDate || '';
        const uri = `${vrRestApiHost}/api/files?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}`;
        log.debug('getProcessedDocuments: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    // Helper function to post bulk operation request
    async postBulkOperation(uri, requestBody, logContext = 'postBulkOperation') {
        log.debug(`${logContext}: fetching from: `, uri);
        log.debug(`${logContext}: requestBody: `, JSON.stringify(requestBody, null, 2));

        try {
            const response = await fetch(uri, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody),
            });

            log.debug(`${logContext}: response: `, response);

            if (response.status === 201) {
                return response;
            } else {
                throw {
                    status: response.status || 500,
                    message: response.data?.message || 'An error occurred.'
                };
            }
        } catch (error) {
            log.debug(`${logContext}: error: `, error);
            throw error;
        }
    },

    doActionOnProcessedDocuments(values) {
        log.debug('doActionOnProcessedDocuments: values: ', values);

        let documentIds = _.isArray(values.documentIds) ? values.documentIds : [values.documentIds];

        const entities = documentIds.map(doc => {
            return {
                id: doc.id,
                metadata: {
                    fileName: doc.fileName
                }
            }
        });

        const requestBody = {
            entities: entities,
            operationType: this.getOperationType(values.action),
            userId: values.userId,
            reason: "",  // required field for operation api
            jobName: "" // required field for operation api
        };

        const uri = APIHelper.getURL(APIEndpoints.createBulkOperation, null, { tenantId: values.customerId });
        return this.postBulkOperation(uri, requestBody, 'doActionOnProcessedDocuments');
    },

    doImportExchangeRates(operationRequest) {
        log.debug('doImportExchangeRates: values: ', operationRequest);

        let exchangeRatesProviderIds = _.isArray(operationRequest.entityIds) ? operationRequest.entityIds : [operationRequest.entityIds];

        // Build entities array - provider codes as IDs with empty metadata
        const entities = exchangeRatesProviderIds.map(providerId => ({
            id: providerId,
            metadata: {}
        }));

        // Convert parameters object to array of key-value pairs
        const parameters = [];
        if (operationRequest.parameters) {
            Object.keys(operationRequest.parameters).forEach(key => {
                parameters.push({
                    key: key,
                    value: operationRequest.parameters[key]
                });
            });
        }

        // Build the request body
        const requestBody = {
            entities: entities,
            operationType: 'ImportExchangeRates',
            userId: operationRequest.userId,
            reason: "Manual Import",
            jobName: operationRequest.jobName,
            parameters: parameters
        };

        const uri = APIHelper.getURL(APIEndpoints.createBulkOperation, null, { tenantId: operationRequest.customerId });
        return this.postBulkOperation(uri, requestBody, 'doImportExchangeRates');
    },

    getOperationType(action) {
        switch (action) {
            case 'delete':
                return 'BulkDeleteFiles';
            case 'redeliver':
                return 'BulkRedeliverFiles';
            default:
                return '';
        }
    },

    getProcessedDocumentsV2(apiParams = {}) {
        log.debug('getProcessedDocumentsV2() - apiParams: ', apiParams);
        const companyId = apiParams.companyId || '';
        const startDate = apiParams.startDate || '';
        const endDate = apiParams.endDate || '';
        const uri = `${vrRestApiHost}/api/v2/files?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}`;
        log.debug('getProcessedDocumentsV2: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getProcessedDocumentsV6(apiParams = {}) {
        log.debug('getProcessedDocumentsV6() - apiParams: ', apiParams);
        const companyId = apiParams.companyId || '';
        const startDate = apiParams.startDate || '';
        const endDate = apiParams.endDate || '';
        const statusParams = buildRepeatedQueryParams('status', apiParams.status);
        const uri = `${vrRestApiHost}/api/v6/files?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}${statusParams}`;
        log.debug('getProcessedDocumentsV6: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    deleteProcessedDocument(documentId) {
        const { featureFlags } = getClientConfig();
        const useV2DeleteFileEndpoint = featureFlags?.UseV2DeleteFileEndpoint;
        const useUserActivityLoggingEndpoints = featureFlags?.UseUserActivityLoggingEndpoints;
        // v5 takes precedence over v2
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : (useV2DeleteFileEndpoint ? '/v2' : '');
        const uri = `${vrRestApiHost}/api${apiVersion}/files/${documentId.id}`;
        log.debug('deleteProcessedDocument: fetching from: ', uri);
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'DELETE',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('deleteProcessedDocument: response: ', response);

                    if (response.status === 200) {
                        return response;
                    }
                    else {
                        reject(response);
                    }
                })
                .then((json) => {
                    resolve(json);
                })
        });
    },

    redeliverProcessedDocument(documentId) {
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const uri = `${vrRestApiHost}/api${apiVersion}/files/${documentId.id}`;
        log.debug('redeliverProcessedDocument: fetching from: ', uri);
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('redeliverProcessedDocument: response: ', response);
                    if (response.status == 202) {
                        return {};
                    }
                    return response.json();
                })
                .then((json) => {
                    log.debug('redeliverProcessedDocument: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('redeliverProcessedDocument: error:', error);
                    reject(error);
                });
        });
    },

    deleteVATCode(id) {
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const uri = `${vrRestApiHost}/api${apiVersion}/vatcodes/${id}`;
        log.debug('deleteVATCode: fetching from: ', uri);

        return fetch(uri, {
            method: 'DELETE',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then((response) => {
                return response;
            });
    },

    exportVatCodes(callback) {
        log.debug('exportVatCodes()')
        const URL = `${vrRestApiHost}/api/vatcodes/export`
        return getBlob(
            URL,
            sessionStorage.token,
            'text/plain',
            (blob, resolve, reject) => {
                resolve(blob);
            },
            (error, resolve, reject) => {
                reject(error);
            });
    },

    createCustomVATCodes(file, removeExistingVatCodes) {
        let data = new FormData();
        data.append(file.name, file);
        data.append('RemoveExistingVatCodes', removeExistingVatCodes);
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const uri = `${vrRestApiHost}/api${apiVersion}/vatcodes/Custom`;
        return new Promise((resolve, reject) => {
            fetch(uri, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                body: data
            })
                .then((response) => {
                    if (!response.ok) {
                        response.text()
                            .then((text) => text ? JSON.parse(text) : response.status + ' ' + response.statusText)
                            .then((json) => {
                                return reject({ status: response.status, message: json.message } || json)
                            });
                    }
                    else {
                        resolve();
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    createVATCode(data) {
        // POST /vatcodes
        // Still used by Duplicate action, but not assisted create
        const uri = vrRestApiHost + '/api/vatcodes';
        log.debug('createVATCode: fetching from: ', uri);

        return post(uri, sessionStorage.token, data);
    },

    importGenericVatCodes(removeExistingVatCodes) {
        // POST /vatcodes
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const uri = `${vrRestApiHost}/api${apiVersion}/vatcodes/generic?&removeExistingvatcodes=${removeExistingVatCodes}`;
        log.debug('importGenericVatCodes: fetching from: ', uri);

        return post(uri, sessionStorage.token, '');
    },

    updateVATCode(id, data) {
        // PUT /vatcodes/{id}
        const uri = vrRestApiHost + '/api/vatcodes/' + id;
        log.debug('updateVATCode: fetching from: ', uri);

        // clean up data
        if (data.reporterId > -1)  //Do send the reporter id in case of generic combinations because then we need the id to update the record
        {
            data = _.omit(data, "reporterId");
        }

        return put(uri, sessionStorage.token, data);
    },

    getVATCodes(params) {
        const uri = vrRestApiHost + '/api/vatcodes';
        const qs = QueryString.stringify({ companyId: params.companyId });
        const url = uri + '?' + qs;
        log.debug('getVATCodes: fetching from: ', url);
        return new Promise((resolve, reject) => {
            return fetch(url, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then(errorHandler)
                .then((response) => {
                    log.debug('getVATCodes: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    resolve(cleanAndFilterVatCodeResponse(json, params));
                })
                .catch((error) => {
                    log.debug('getVATCodes: error:', error);
                    reject(error);
                });
        });
    },

    getVATCodeSearchFields() {
        const uri = vrRestApiHost + '/api/content/vatCodeSearchFields';
        log.debug('getVATCodeSearchFields: fetching from: ', uri);
        return get(uri, sessionStorage.token)
    },

    getVATCodeTransactionIds(params) {
        const uri = vrRestApiHost + '/api/content/transactionIds';
        const qs = QueryString.stringify(params);
        const url = uri + '?' + qs;
        log.debug('getVATCodeTransactionIds: fetching from: ', url);
        return get(url, sessionStorage.token);
    },

    getVATCodeTransactionIdsBySearch(id) {
        const url = vrRestApiHost + `/api/v4/transactions/${id}/details`;
        log.debug('getVATCodeTransactionIdsBySearch: fetching from: ', url);
        return get(url, sessionStorage.token);
    },

    getVATCodeTransactionIdsByVatBox(countryCode, boxValue, filingPeriod, startDate) {
        const url = vrRestApiHost + `/api/v4/transactions/VATBoxes?countryCode=${countryCode}&boxes=${boxValue}&period=${filingPeriod}&startDate=${startDate}`;
        log.debug('getVATCodeTransactionIdsByVatBox: fetching from: ', url);
        return get(url, sessionStorage.token);
    },

    getVATRates(countryCode, effectiveDate) {
        const effectiveDateFormatted = dateformat(effectiveDate, 'yyyy-mm-dd');
        const uri = vrRestApiHost + '/api/vatRates/' + countryCode + '/' + effectiveDateFormatted;
        log.debug('getVATRates: fetching from: ', uri);

        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('getVATRates success:', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('getVATRates restructuring');
                    resolve(restructureRates(json.rates));
                })
                .catch((error) => {
                    log.debug('getVATRates error:', error);
                    reject()
                })
        });
    },

    getVATCodeTransactionIdDetail(id, params) {
        const uri = vrRestApiHost + '/api/content/transactionIds/' + id;
        const qs = QueryString.stringify(params);
        const url = uri + '?' + qs;
        log.debug('getVATCodeTransactionIdDetail: fetching from: ', url);
        return get(url, sessionStorage.token);
    },

    createVATCodeWithTransactionId(body) {
        const uri = vrRestApiHost + '/api/vatCodeActions/byTransactionId';
        log.debug('createVATCodeWithTransactionId: fetching from: ', uri);

        return post(uri, sessionStorage.token, body);
    },
    getReportingDocumentXML(id) {
        const uri = vrRestApiHost + '/api/TransactionActions/TransactionXML/' + id;
        log.debug('getReportingDocumentXML: fetching from: ', uri);
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('getReportingDocumentXML: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    resolve(restructureReportingDocumentXML(json));
                })
                .catch((error) => {
                    log.debug('getReportingDocumentXML: error:', error);
                    reject(error);
                });

        });
    },

    getErrorDocument(id, featureFlags = []) {
        ///transaction/errors/{errorId}
        const uri = vrRestApiHost + '/api/transaction/errors/' + id;
        log.debug('getErrorDocument: fetching from: ', uri);
        log.clearAllDictionaryErrors();
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('getErrorDocument: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    let errorJson = new ErrorDocumentUtil(json).transform(featureFlags);
                    resolve(errorJson);
                })
                .catch((error) => {
                    log.debug('getErrorDocument: error:', error);
                    reject(error);
                });

        });
    },

    doErrorReplaceXML(id, data) {
        // POST /transaction/errors/{id}/replaceXml
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const uri = `${vrRestApiHost}/api${apiVersion}/transaction/errors/${id}/replaceXml`;
        log.debug('doErrorReplaceXML: fetching from: ', uri);

        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                cache: 'no-cache',
                body: JSON.stringify(data)
            })
                .then((response) => {
                    log.debug('doErrorReplaceXML: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('doErrorReplaceXML: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('doErrorReplaceXML: error:', error);
                    reject(error);
                });
        });
    },

    getProcessedDocumentDetails(documentId) {
        const uri = vrRestApiHost + '/api/files/' + encodeURIComponent(documentId);
        log.debug('getProcessedDocumentDetails: fetching from: ', uri);
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then(errorHandler)
                .then((response) => {
                    log.debug('getProcessedDocumentDetails: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('getProcessedDocumentDetails: success: ', json);

                    resolve(json);
                })
                .catch((error) => {
                    log.debug('getProcessedDocumentDetails: error:', error);
                    reject(error);
                });
        });
    },

    getUploadedDocumentErrorDetails(fileId) {
        const uri = vrRestApiHost + '/file-status-service/api/file-status/' + encodeURIComponent(fileId) + '/errors';
        log.debug('getUploadedDocumentErrorDetails: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getErrors(apiParams = {}) {
        log.debug('getErrors(): apiParams: ', apiParams);
        const ALL_COMPANIES = 0;
        const ALL_RECORDS = 0;
        const INVOICE_NUMBER_FILTER = '';
        const modifiedFilterParams = Object.assign({}, apiParams, {
            companyId: apiParams?.companyId !== undefined ? apiParams.companyId : ALL_COMPANIES,
            countryCode: apiParams?.countryCode || 'XX',
            maxErrorCount: apiParams?.maxErrorCount !== undefined ? apiParams.maxErrorCount : ALL_RECORDS,
            pageNumber: apiParams?.pageNumber !== undefined ? apiParams.pageNumber : ALL_RECORDS,
            invoiceNumberFilter: apiParams?.invoiceNumberFilter !== undefined ? apiParams.invoiceNumberFilter : INVOICE_NUMBER_FILTER
        });
        log.debug('getErrors(): modifiedFilterParams: ', modifiedFilterParams);
        let URL = APIHelper.getURL(APIEndpoints.getErrors, modifiedFilterParams);
        log.debug('getErrors(): URL: ', URL);
        return get(URL, sessionStorage.token);
    },

    getWarnings(apiParams = {}) {
        log.debug('getWarnings(): apiParams: ', apiParams);
        const ALL_COMPANIES = 0;
        const ALL_RECORDS = 0;
        const modifiedFilterParams = Object.assign({}, apiParams, {
            companyId: apiParams?.companyId !== undefined ? apiParams.companyId : ALL_COMPANIES,
            countryCode: apiParams?.countryCode || 'XX',
            maxErrorCount: apiParams?.maxErrorCount !== undefined ? apiParams.maxErrorCount : ALL_RECORDS,
            pageNumber: apiParams?.pageNumber !== undefined ? apiParams.pageNumber : ALL_RECORDS
        });
        log.debug('getWarnings(): modifiedFilterParams: ', modifiedFilterParams);
        let URL = APIHelper.getURL(APIEndpoints.getWarnings, modifiedFilterParams);
        log.debug('getWarnings(): URL: ', URL);
        return get(URL, sessionStorage.token);
    },

    getErrorCategories(apiParams = {}) {
        log.debug('getErrorCategories(): apiParams: ', apiParams);
        let URL = APIHelper.getURL(APIEndpoints.getErrorCategories, apiParams);
        log.debug('getErrorCategories(): URL: ', URL);
        return get(URL, sessionStorage.token);
    },

    getErrorSubCategories(apiParams = {}) {
        log.debug('getErrorSubCategories(): apiParams: ', apiParams);
        let URL = APIHelper.getURL(APIEndpoints.getErrorSubCategories, apiParams);
        log.debug('getErrorSubCategories(): URL: ', URL);
        return get(URL, sessionStorage.token);
    },

    getWarningCategories(apiParams = {}) {
        log.debug('getWarningCategories(): apiParams: ', apiParams);
        let URL = APIHelper.getURL(APIEndpoints.getWarningCategories, apiParams);
        log.debug('getWarningCategories(): URL: ', URL);
        return get(URL, sessionStorage.token);
    },

    getWarningSubCategories(apiParams = {}) {
        log.debug('getWarningSubCategories(): apiParams: ', apiParams);
        let URL = APIHelper.getURL(APIEndpoints.getWarningSubCategories, apiParams);
        log.debug('getWarningSubCategories(): URL: ', URL);
        return get(URL, sessionStorage.token);
    },

    doMoveWarningsToError(body) {
        const uri = vrRestApiHost + '/api/erroractions/MoveWarningsToError';
        log.debug('doMoveWarningsToError: fetching from: ', uri);

        return post(uri, sessionStorage.token, body);
    },

    doErrorsActionDelete(body) {
        const uri = vrRestApiHost + '/api/erroractions/Delete';
        log.debug('doErrorsActionDelete: fetching from: ', uri);

        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body),
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('doErrorsActionDelete: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('doErrorsActionDelete: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('doErrorsActionDelete: error:', error);
                    reject(error);
                });
        });

    },

    doErrorsActionMove(body) {
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const uri = `${vrRestApiHost}/api${apiVersion}/erroractions/ReprocessErrorDocuments`;
        log.debug('doErrorsActionMove: fetching from: ', uri);
        log.debug('doErrorsActionMove: with body ', body);
        log.clearAllDictionaryErrors();
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body),
                cache: 'no-cache'
            })
                .then(errorHandler)
                .then((response) => {
                    log.debug('doErrorsActionMove: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('doErrorsActionMove: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('doErrorsActionMove: error:', error);
                    if (error && (!error.status || error.status === 503 || error.status === 504)) {
                        log.setDictionaryMessage(ALL_MESSAGES.CONNECTION_ERROR);
                    }
                    reject(error);
                });
        });
    },

    doErrorsActionDisableCheck(body) {
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const uri = `${vrRestApiHost}/api${apiVersion}/erroractions/ReprocessErrorDocuments`;
        // TODO: consider moving this body into the errorsWarnings controller.

        log.debug('doErrorsActionDisableCheck: fetching from: ', uri);
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body),
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('doErrorsActionDisableCheck: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('doErrorsActionDisableCheck: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('doErrorsActionDisableCheck: error:', error);
                    reject(error);
                });
        });
    },

    doRescheduleError(uri, requestBody) {
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'PUT',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody),
            })
                .then((response) => {
                    if (response.status == 200 || response.status == 204) {
                        return resolve(response);
                    }
                    response.text()
                        .then((responseText) => {
                            return reject({ statusText: responseText });
                        });
                })
                .catch((error) => {
                    log.debug('reschedule: error:', error);
                    reject(error);
                });
        });
    },

    doRescheduleCorrection(errorId, newTransactionDate) {
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '/v2';
        const uri = `${vrRestApiHost}/api${apiVersion}/transaction/errors/RescheduleCorrective`;
        const requestBody = useUserActivityLoggingEndpoints ?
            { errorIds: [errorId], newTransactionDate: newTransactionDate } : // v5 expects multiple 'errorIds'  
            { errorId: errorId, newTransactionDate: newTransactionDate };     // v2 expects a single 'errorId'
        return this.doRescheduleError(uri, requestBody);
    },

    doRescheduleCorrectionLatest(errorIds, newTransactionDate) {
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '/v3';
        const uri = `${vrRestApiHost}/api${apiVersion}/transaction/errors/RescheduleCorrective`;
        const requestBody = { errorIds: errorIds, newTransactionDate: newTransactionDate };
        return this.doRescheduleError(uri, requestBody);
    },

    doRescheduleNonCorrection(errorId, newTransactionDate) {
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '/v2';
        const uri = `${vrRestApiHost}/api${apiVersion}/transaction/errors/RescheduleNonCorrective`;
        const requestBody = useUserActivityLoggingEndpoints ?
            { errorIds: [errorId], newTransactionDate: newTransactionDate } : // v5 expects multiple 'errorIds'
            { errorId: errorId, newTransactionDate: newTransactionDate };     // v2 expects a single 'errorId'
        return this.doRescheduleError(uri, requestBody);
    },

    doRescheduleNonCorrectionLatest(errorIds, newTransactionDate) {
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '/v4';
        const uri = `${vrRestApiHost}/api${apiVersion}/transaction/errors/RescheduleNonCorrective`;
        const requestBody = { errorIds: errorIds, newTransactionDate: newTransactionDate }
        return this.doRescheduleError(uri, requestBody);
    },

    doEnterCreditNoteCertificateDate(errorIds, newTransactionDate) {
        log.debug('doEnterCreditNoteCertificateDate(): errorIds:', errorIds, 'newTransactionDate:', newTransactionDate);
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiEndpoint = useUserActivityLoggingEndpoints ? APIEndpoints.creditNoteCertificateV5 : APIEndpoints.creditNoteCertificate;
        const uri = APIHelper.getURL(apiEndpoint);
        log.debug('doEnterCreditNoteCertificateDate(): uri:', uri)
        const requestBody = { errorIds, transactionDate: newTransactionDate };
        return new Promise(
            (resolve, reject) => {
                return fetch(uri, {
                    method: 'PUT',
                    mode: 'cors',
                    cache: 'no-cache',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestBody),
                })
                    .then((response) => {
                        if (response.status === 204) {
                            return resolve(response);
                        }

                        response.text().then(responseText => reject({ statusText: responseText }));
                    })
                    .catch((error) => {
                        log.debug('doEnterCreditNoteCertificateDate(): error:', error);
                        reject(error);
                    });
            }
        );
    },

    getEcListingDetails(params) {
        const url = vrRestApiHost + '/api/filing/types/eclisting';
        log.debug('getEcListingDetails(): params: ', params);

        var uri = [url, params.companyId, ReportURLValues, params.vatNumber, params.listingLineType, params.listingType].join('/');
        const queryParams =
        {
            countryCode: params.countryCode,
            endDate: params.endDate,
            startDate: params.startDate
        };
        const qs = QueryString.stringify(queryParams);
        uri = [uri, qs].join('?');
        log.debug('getEcListingDetails: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getEcListingLineDetails(params) {
        const url = vrRestApiHost + '/api/v4/filing/types/ECListing';
        log.debug('getEcListingLineDetails(): params: ', params);
        const uri = [url,
            params.companyId,
            LineDetailsUrl,
            params.countryCode,
            params.startDate,
            params.endDate,
            params.vatNumber,
            params.listingType,
            params.listingLineType,
            params.correctionType,
            params.correctionKey,
            dateformat(params.correctionDate, 'yyyy-mm-dd')
        ].join('/');
        log.debug('getEcListingLineDetails: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getOnlineVATNumberCheck(vatNumber) {
        const useVatUtilitiesService = getClientConfig().featureFlags?.UseVatUtilitiesService;
        const vatUtilityUrl = `${vrRestApiHost}/api/vatnumbers/${vatNumber}/onlineCheck`;
        const vatUtilityV2Url = `${vrRestApiHost}/vat-utilities/api/v2/vatNumbers/${vatNumber}/validation`;
        const url = useVatUtilitiesService ? vatUtilityV2Url : vatUtilityUrl;
        return get(url, sessionStorage.token);
    },

    getOnlineVATNumberCheckWithRequestId(vatNumber, requesterVATNumber) {
        const useVatUtilitiesService = getClientConfig().featureFlags?.UseVatUtilitiesService;
        const vatUtilityUrl = `${vrRestApiHost}/api/vatnumbers/${vatNumber}/onlineCheck/${requesterVATNumber}`;
        const vatUtilityV2Url = `${vrRestApiHost}/vat-utilities/api/v2/vatNumbers/${vatNumber}/validationWithRequester/${requesterVATNumber}`;
        const url = useVatUtilitiesService ? vatUtilityV2Url : vatUtilityUrl;
        return get(url, sessionStorage.token);
    },

    getEcListingReports(params) {
        const url = vrRestApiHost + '/api/filing/types/eclisting';
        log.debug('getEcListingReports(): params: ', params);
        if (params.flow) { //convert enum to int value
            params.listingType = ECListingFlowPerTransactionTypesEnum.props[params.flow].value;
            delete params.flow;
        }
        else
            params.listingType = ECListingFlowPerTransactionTypesEnum.props['Sales'].value;
        const uri = [url, params.companyId, ReportUrl, params.countryCode, params.dueDateBefore, params.dueDateAfter, params.periodicity, params.listingType].join('/');
        log.debug('getEcListingReports: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getIntrastatReports(params) {
        const url = vrRestApiHost + '/api/filing/types/intrastat';
        log.debug('getIntrastatReports(): params: ', params);
        if (params.reportType) { //convert enum to int value
            params.reportType = IntrastatFlowEnum.props[params.reportType].value;
        }
        var uri = [url, params.companyId, ReportUrl, params.country, params.dueDateBefore, params.dueDateAfter, params.periodicity, params.reportType].join('/');

        const queryParams =
        {
            returnDisplayType: params.returnDisplayType,
            simplifiedIntrastat: params.simplifiedIntrastat
        }
        const qs = QueryString.stringify(queryParams);

        uri = [uri, qs].join('?');

        log.debug('getIntrastatReports: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },
    getExtrastatReports(params) {
        const url = vrRestApiHost + '/api/filing/types/extrastat';
        log.debug('getExtrastatReports(): params: ', params);
        if (params.reportType) {
            params.reportType = ExtrastatFlowEnum.props[params.reportType].value;
        }
        let uri = [url, params.companyId, ReportUrl, params.country, params.dueDateBefore, params.dueDateAfter, params.periodicity, params.reportType].join('/');
        log.debug('getExtrastatReports: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getIntrastatDetails(lineNumbers) {
        const URL = APIHelper.getURL(APIEndpoints.getIntrastatDetails, null, null);
        log.debug('getIntrastatDetails() - URL: ', URL);
        return APIHelper.post(URL, sessionStorage.token, lineNumbers);
    },

    getBulkFindReplaceNodes() {
        const useAllNodesForNodeWizard = getClientConfig().featureFlags?.UseAllNodesForNodeWizard;
        if (useAllNodesForNodeWizard) {
            return this.getReportingDocumentNodes(); // returns all nodes from the "Replace and move to inbox" feature in VR Desktop
        }
        return this.getNodeWizardNodes(); // returns only limited nodes from the "Node Wizard" feature in VR Desktop; to be removed later along with feature flag
    },

    getNodeWizardNodes() {
        log.debug("getNodeWizardNodes()");
        const endpoint = vrRestApiHost + '/api/Content/NodeWizardNodes';
        log.debug("getNodeWizardNodes() - endpoint: " + endpoint);
        log.clearAllDictionaryErrors();
        const requestConfig = {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
        };
        return new Promise((resolve, reject) => {
            fetch(endpoint, requestConfig)
                .then((response) => {
                    log.debug('getNodeWizardNodes() - response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('getNodeWizardNodes() - success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('getNodeWizardNodes() - error: ', error);
                    reject(error);
                });
        });
    },

    getReportingDocumentNodes() {
        log.debug("getReportingDocumentNodes()");
        const endpoint = vrRestApiHost + '/api/Content/ReportingDocumentNodes';
        log.debug("getReportingDocumentNodes() - endpoint: " + endpoint);
        log.clearAllDictionaryErrors();
        const requestConfig = {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
        };

        return new Promise((resolve, reject) => {
            fetch(endpoint, requestConfig)
                .then((response) => {
                    log.debug('getReportingDocumentNodes() - response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('getReportingDocumentNodes() - success: ', json);
                    // Transform the array to keep only nodeName property and remove duplicates. Using Set has better perfromance over map.reduce() here.
                    const transformedJson = [...new Set(json.map(item => item.nodeName))].map(name => ({ nodeName: name }));
                    resolve(transformedJson);
                })
                .catch((error) => {
                    log.debug('getReportingDocumentNodes() - error: ', error);
                    reject(error);
                });
        });
    },

    searchTransactionIDs(filterParams) {
        log.debug('searchTransactionsIDs: filterParams: ', filterParams);
        //Construct url
        const uri = vrRestApiHost + '/api/Transactions/TransactionDetailIDs';
        const qs = QueryString.stringify(filterParams);
        const url = uri + '?' + qs;
        log.debug('searchTransactionIDs - url: ', url);
        //Fetch data, create promise
        return new Promise((resolve, reject) => {
            fetch(url, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then(errorHandler)
                .then((response) => {
                    log.debug('searchTransactionIDs - response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('searchTransactionIDs - success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('searchTransactionIDs - error: ', error);
                    reject(error);
                });
        });
    },

    searchTransactionsPage(body) {
        log.debug('searchTransactionsPage: ', body);
        const url = vrRestApiHost + '/api/Transactions/TransactionSearch/';
        log.debug('searchTransactionsPage - url: ', url);
        return new Promise((resolve, reject) => {
            return fetch(url, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                cache: 'no-cache',
                body: JSON.stringify(body)
            })
                .then(errorHandler)
                .then((response) => {
                    log.debug('searchTransactionsPage: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('searchTransactionsPage: success: ', json);
                    resolve(restructureSearchResults(json));
                })
                .catch((error) => {
                    log.debug('searchTransactionsPage: error: ', error);
                    reject(error);
                });
        });
    },

    searchTransactions(filterParams) {
        log.debug('searchTransactions: filterParams: ', filterParams);
        //Construct url
        const uri = vrRestApiHost + '/api/Transactions';
        const qs = QueryString.stringify(filterParams);
        const url = uri + '?' + qs;
        log.debug('searchTransactions - url: ', url);
        //Fetch data, create promise
        return new Promise((resolve, reject) => {
            fetch(url, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('searchTransactions - response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('searchTransactions - success: ', json);
                    resolve(restructureSearchResults(json));
                })
                .catch((error) => {
                    log.debug('searchTransactions - error: ', error);
                    reject(error);
                });
        });
    },


    saveTransactionXMLMultiple(ids) {
        log.debug('saveTransactionXMLMultiple: ', ids);

        const url = vrRestApiHost + '/api/transactionActions/TransactionXML/';
        const list = {
            TransactionIdList: ids
        };
        log.debug('saveTransactionXMLMultiple - url: ', url, list);

        return new Promise((resolve, reject) => {
            return fetch(url, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                cache: 'no-cache',
                body: JSON.stringify(list)
            })
                .then((response) => {
                    log.debug('saveTransactionXMLMultiple: response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('saveTransactionXMLMultiple: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('saveTransactionXMLMultiple: error: ', error);
                    reject(error);
                });
        });

    },

    doActionOnTransactionWithChanges(action, id, body) {
        // POST /transactionActions/forcetoerror/{id}
        // POST /transactionActions/redeliver/{id}

        log.debug('doActionOnTransactionWithChanges: ', action, id, body);

        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const url = `${vrRestApiHost}/api${apiVersion}/transactionActions/${action}/${id}`;
        log.debug('doActionOnTransactionWithChanges - url: ', url);

        return new Promise((resolve, reject) => {
            fetch(url, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                cache: 'no-cache',
                body: JSON.stringify(body)
            })
                .then(errorHandler)
                .then((response) => {
                    log.debug('doActionOnTransactionWithChanges - response: ', response);
                    return response.json();
                })
                .then(transactionErrorHandler)
                .then((json) => {
                    log.debug('doActionOnTransactionWithChanges - success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('doActionOnTransactionWithChanges- error: ', error);
                    reject(error);
                });
        });
    },


    doActionOnTransactions(action, ids, reason) {
        log.debug('doActionOnTransactions:  ', action, ids, reason);
        //Construct url
        const useUserActivityLoggingEndpoints = getClientConfig().featureFlags?.UseUserActivityLoggingEndpoints;
        const apiVersion = useUserActivityLoggingEndpoints ? '/v5' : '';
        const url = `${vrRestApiHost}/api${apiVersion}/transactionActions/${action}`;
        const body = {
            transactionIds: ids,
            reason: reason
        };
        log.debug('doActionOnTransactions - url: ', url, body);

        return new Promise((resolve, reject) => {
            fetch(url, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                cache: 'no-cache',
                body: JSON.stringify(body)
            })
                .then(errorHandler)
                .then((response) => {
                    log.debug('doActionOnTransactions - response: ', response);
                    return response.json();
                })
                .then(transactionErrorHandler)
                .then((json) => {
                    log.debug('doActionOnTransactions - success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('doActionOnTransactions - error: ', error);
                    reject(error);
                });
        });
    },

    redeliverAllVATCodes(apiParams) {
        log.debug('redeliverAllVATCodes() - apiParams: ', apiParams);
        const companyId = apiParams.companyId || '';
        const countryCode = apiParams.countryCode || '';
        const periodStartDate = apiParams.periodStartDate || '';
        const periodEndDate = apiParams.periodEndDate || '';
        const analysisType = apiParams.analysisType;
        const body = { vatCodes: apiParams.vatCodes || [] };
        const uri = vrRestApiHost + `/api/TransactionActions/redeliverAllVATCodes/${companyId}/${countryCode}/${periodStartDate}/${periodEndDate}/${analysisType}`;
        log.debug('redeliverAllVATCodes() - uri: ', uri);
        return post(uri, sessionStorage.token, body);
    },

    getHULRTransmissions(params) {
        log.debug("doGetHULRTransmissions() - params : " + params);
        let pageCount = typeof params.count === 'undefined' ? 500 : params.count;
        let startAt = params.startAt || 1;
        const queryParams = {
            Count: pageCount,
            StartAt: startAt,
            StartDate: params.startDate || undefined,
            EndDate: params.endDate || undefined,
            Status: params.status || TransmissionStatusEnum.UNKNOWN,
            TaxNumber: params.taxNumber || undefined,
            InvoiceNumber: params.invoiceNumber || undefined
        };
        const QUERY_STRING = QueryString.stringify(queryParams);
        const URL = vrRestApiHost + '/api/Transmissions/HULRTransmissions?' + QUERY_STRING;
        log.debug("doGetHULRTransmissions() - URL : " + URL);
        const REQUEST_INIT = {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache'
        };
        return new Promise((resolve, reject) => {
            fetch(URL, REQUEST_INIT)
                .then((response) => {
                    log.debug("doGetHULRTransmissions() - repsonse: " + response);
                    return response.json();
                })
                .then((json) => {
                    log.debug("doGetHULRTransmissions() - json: " + json);
                    let hulrTransmissions = ((json && json.hulrTransmissions) || []);
                    const totalCount = (json && json.totalCount) || hulrTransmissions.length;
                    asyncjs.parallelLimit(
                        {
                            dataSuppliedJSON: (cb) => {
                                asyncjs.map(hulrTransmissions, (entry, cb1) => {
                                    parseString(entry.dataSupplied, { explicitArray: false }, (err, result) => {
                                        if (err) {
                                            return cb1(null, '');
                                        }
                                        cb1(null, result.ManageInvoiceRequest || {});
                                    });
                                }, (err, results) => {
                                    if (err) {
                                        return cb(err);
                                    }
                                    cb(null, results);
                                })
                            },
                            submissionResponseJSON: (cb) => {
                                asyncjs.map(hulrTransmissions, (entry, cb1) => {
                                    parseString(entry.submissionResponse, { explicitArray: false }, (err, result) => {
                                        if (err) {
                                            return cb1(null, '');
                                        }
                                        result = result || {};
                                        result.isErrorResponse = (typeof result.GeneralErrorResponse !== 'undefined');
                                        cb1(null, result);
                                    });
                                }, (err, results) => {
                                    if (err) {
                                        return cb(err);
                                    }
                                    cb(null, results);
                                })
                            },
                            dataSentExtended: (cb) => {
                                asyncjs.map(hulrTransmissions, (entry, cb1) => {
                                    parseString(entry.dataSentEncoded, { explicitArray: false }, (err, result) => {
                                        if (err) {
                                            return cb1(null, '');
                                        }
                                        //Decode invoices
                                        let reqInfo = result.ManageInvoiceRequest || {};

                                        let invoiceOperations = (reqInfo.invoiceOperations && reqInfo.invoiceOperations.invoiceOperation) || [];
                                        invoiceOperations = Array.isArray(invoiceOperations) ? invoiceOperations : [invoiceOperations];
                                        let decodedInvoiceOperationsJSON = invoiceOperations.map((entry) => {

                                            let decodedInvoice = entry.invoice != undefined ? encodeDecode.base64Decode(entry.invoice) : encodeDecode.base64Decode(entry.invoiceData);
                                            if (entry.invoice != undefined)
                                                entry.invoice = decodedInvoice;
                                            else
                                                entry.invoiceData = decodedInvoice;
                                            return entry;
                                        });

                                        //Covert to decoded JSON to XML
                                        let decodedDataSentJSON = Object.assign({}, result);
                                        let decodedManagedInvoiceRequest = decodedDataSentJSON.ManageInvoiceRequest || {}
                                        decodedManagedInvoiceRequest.invoiceOperations = decodedManagedInvoiceRequest.invoiceOperations || {};
                                        decodedManagedInvoiceRequest.invoiceOperations.invoiceOperation = decodedInvoiceOperationsJSON || [];
                                        const decodedDataSent = xmlBuilder.buildObject(decodedDataSentJSON);

                                        // Prepare new result
                                        let dataSentExtendedResult = {};
                                        dataSentExtendedResult.encoded = {
                                            json: reqInfo
                                        };
                                        dataSentExtendedResult.decoded = {
                                            xml: decodedDataSent,
                                            json: decodedDataSentJSON
                                        }
                                        cb1(null, dataSentExtendedResult);
                                    });
                                }, (err, results) => {
                                    if (err) {
                                        return cb(err);
                                    }
                                    cb(null, results);
                                })
                            }
                        },
                        100,
                        (err, results) => {
                            let hulrTransmissionsWithJSON = [];
                            if (err) {
                                hulrTransmissionsWithJSON = hulrTransmissions;
                            }
                            else {
                                hulrTransmissionsWithJSON = hulrTransmissions.map((entry, index) => {
                                    entry.dataSuppliedJSON = results.dataSuppliedJSON[index];
                                    entry.dataSentExtended = results.dataSentExtended[index];
                                    entry.submissionResponseJSON = results.submissionResponseJSON[index];
                                    return entry;
                                });
                            }
                            return resolve({
                                totalCount: totalCount,
                                hulrTransmissions: hulrTransmissionsWithJSON
                            });
                        }
                    );
                })
                .catch((error) => {
                    log.debug("doGetHULRTransmissions() - error: " + error);
                    reject(error);
                });
        });
    },

    getLegalEntities(params) {
        log.debug('getLegalEntities(): params: ' + params);
        const QUERY_STRING = QueryString.stringify(params);

        const URL = vrRestApiHost + '/api/LegalEntities' + (QUERY_STRING === '' ? '' : '?' + QUERY_STRING);
        log.debug("getLegalEntities() - URL : " + URL);
        const REQUEST_INIT = {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache'
        };

        return new Promise((resolve, reject) => {

            fetch(URL, REQUEST_INIT)
                .then((response) => {
                    log.debug("getLegalEntities() - response: " + response);
                    return response.json();
                })
                .then((json) => {
                    resolve(json);
                })
                .catch((error) => {
                    log.debug("getLegalEntities() - error: " + error);
                    reject(error);
                });

        });
    },

    addLegalEntity(params) {
        log.debug("addLegalEntity(): params: ", params);
        const url = vrRestApiHost + '/api/LegalEntities';
        log.debug("addLegalEntity(): url: ", url);
        const body = {
            login: params.login || '',
            password: btoa(params.password) || '',
            taxNumber: params.taxNumber || '',
            xmlSignatureKey: btoa(params.xmlSignatureKey) || '',
            xmlExchangeKey: btoa(params.xmlExchangeKey) || '',
            sellerName: params.sellerName || ''
        };
        const requestInit = {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache',
            body: JSON.stringify(body)
        };
        log.debug("addLegalEntity(): body: ", body);
        return new Promise((resolve, reject) => {
            fetch(url, requestInit)
                .then((response) => {
                    log.debug('addLegalEntity - response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('addLegalEntity - success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('addLegalEntity - error: ', error);
                    reject(error);
                });
        });
    },

    updateLegalEnity(taxNumber, params) {
        log.debug('updateLegalEnity(): params: ', params);
        const uri = vrRestApiHost + '/api/LegalEntities/' + encodeURIComponent(taxNumber);
        const body = JSON.stringify(params);
        log.debug('updateLegalEnity(): PUT %s, body: ', uri, body);
        const requestInit = {
            method: 'PUT',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache',
            body: body
        };
        return new Promise((resolve, reject) => {
            fetch(uri, requestInit)
                .then((response) => {
                    return response.json();
                })
                .then((json) => {
                    log.debug('updateLegalEnity: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('updateLegalEnity: error:', error);
                    reject(error);
                });
        });
    },

    deleteLegalEnity(taxNumber) {
        log.debug('deleteLegalEnity(): taxNumber: ', taxNumber);
        const uri = vrRestApiHost + '/api/LegalEntities/' + encodeURIComponent(taxNumber);
        const requestInit = {
            method: 'DELETE',
            mode: 'cors',
            cache: 'no-cache'
        };

        return new Promise((resolve, reject) => {
            fetch(uri, requestInit)
                .then((response) => {
                    log.debug('deleteLegalEnity OK' + response);
                    resolve({ delete: 'Ok' });
                })
                .catch((error) => {
                    log.debug('deleteLegalEnity: error:', error);
                    reject(error);
                });
        });
    },

    getClosedReturns(params) {
        log.debug('getClosedReturns: params: ', params);

        if (params.companyId === 'All' || !params.companyId) {
            params.companyId = -1;
        }

        if (!params.countryCode) {
            params.countryCode = 'All';
        }

        if (params.filingType) {
            log.debug('getClosedReturns: one return type: ', params.filingType);
            return getClosedReturn(params.filingType, params)
        } else {
            log.debug('getClosedReturns: all return types');
            const getAllClosedReturnsArray = [
                getClosedReturn(FilingTypeEnum.VATRETURN, params),
                getClosedReturn(FilingTypeEnum.ECLISTING, params),
                getClosedReturn(FilingTypeEnum.LOCALLISTING, params),
                getClosedReturn(FilingTypeEnum.INTRASTAT, params),
                getClosedReturn(FilingTypeEnum.OSSIMPORT, params),
                getClosedReturn(FilingTypeEnum.OSSUNION, params),
                getClosedReturn(FilingTypeEnum.OSSNONUNION, params)
            ];

            const { featureFlags } = getClientConfig();
            if (featureFlags?.EnableReopenVATBook) {
                getAllClosedReturnsArray.push(getClosedReturn(FilingTypeEnum.VATBOOKS, params));
            }
            // Get a response from all endpoints using Promise.allSettled to handle partial failures
            return Promise.allSettled(getAllClosedReturnsArray)
                .then((results) => {
                    const successfulResponses = results
                        .filter(result => result.status === PromiseStatus.FULFILLED)
                        .map(result => result.value)
                        .flat();

                    const failedResponses = results.filter(result => result.status === PromiseStatus.REJECTED);

                    log.debug('getClosedReturns: successfulResponses: ', successfulResponses.length);
                    log.debug('getClosedReturns: failedResponses: ', failedResponses.length);

                    // Only reject if ALL requests failed
                    if (successfulResponses.length === 0 && failedResponses.length > 0) {
                        throw failedResponses[0].reason;
                    }
                    return successfulResponses;
                }).catch((error) => {
                    log.debug('getClosedReturns: All requests failed: ', error);
                    throw error;
                })
        }
    },

    reopenReturn(params, suppressError) {
        suppressError = suppressError || false;
        log.debug("reopenReturn: params: ", params);
        return new Promise(function (resolve, reject) {
            const config = getRequestConfig(params);
            if (!config) {
                return reject();
            }
            const uri = buildRequestUri(config, params);
            const requestInit = buildRequestInit(config.requestBody);
            fetch(uri, requestInit)
                .then(errorHandler)
                .then(function () {
                    log.debug("reopenReturn OK");
                    resolve({});
                })
                .catch(function (error) {
                    log.debug("reopenReturn: error:", error);
                    handleError(error, suppressError);
                    reject(error);
                });
        });
    },

    getVATBooks(params) {
        log.debug('getVATBooks() : params: ', params);
        const reqParam = {
            companyId: params.companyId,
            frequency: params.periodicity,
            displayType: params.displayType || '0',
            startDate: params.startDate,
            endDate: params.endDate,
            countryCode: params.countryCode
        };

        if (params.flow) { //convert enum to int value
            reqParam.returnType = VATBookFlowEnum.props[params.flow].value;
        }

        const url = vrRestApiHost + '/api/filing/types/VATBook';
        const uri = [url, reqParam.companyId, ReportUrl, reqParam.countryCode, reqParam.startDate, reqParam.endDate, reqParam.frequency, reqParam.returnType].join('/');

        log.debug("getVATBooks() - URL : " + uri);
        return get(uri, sessionStorage.token);
    },

    getVATCodeSimulation(params) {
        log.debug('getVATCodeSimulation(): params: ' + params);
        const QUERY_STRING = QueryString.stringify(params);

        const URL = vrRestApiHost + '/api/vatcodesimulator/simulate' + (QUERY_STRING === '' ? '' : '?' + QUERY_STRING);
        log.debug("getVATCodeSimulation() - URL : " + URL);

        const REQUEST_INIT = {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache'
        };

        return new Promise((resolve, reject) => {
            fetch(URL, REQUEST_INIT)
                .then(errorHandler)
                .then((response) => {
                    log.debug("getVATCodeSimulation() - response: " + response);
                    return response.json();
                })
                .then((json) => {
                    resolve(restructureVATCodeSimulatorResponse(json));
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    getCompanyVATCodes(params) {
        const uri = vrRestApiHost + '/api/VATCodes';
        const qs = QueryString.stringify({ companyId: params.companyId });
        const url = uri + '?' + qs;
        log.debug("getCompanyVATCodes() - URL : " + url);
        const REQUEST_INIT = {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache'
        };

        return new Promise((resolve, reject) => {
            fetch(url, REQUEST_INIT)
                .then((response) => {
                    log.debug("getCompanyVATCodes() - response: " + response);
                    return response.json();
                })
                .then((json) => {
                    resolve(json);
                })
                .catch((error) => {
                    log.debug("getCompanyVATCodes() - error: " + error);
                    reject(error);
                });
        });
    },
    getInvoiceLinesData(params) {
        const QUERY_STRING = QueryString.stringify(params);
        const URL = vrRestApiHost + '/api/DataMining/InvoiceData' + (QUERY_STRING === '' ? '' : '?' + QUERY_STRING);
        log.debug("getInvoiceData() - URL : " + URL);
        const REQUEST_INIT = {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache'
        };

        return new Promise((resolve, reject) => {
            fetch(URL, REQUEST_INIT)
                .then(errorHandler)
                .then((response) => {
                    log.debug("getInvoiceData() - response: " + response);
                    return response.json();
                })
                .then((json) => {
                    resolve(json);
                })
                .catch((error) => {
                    log.debug("getInvoiceData() - error: " + error);
                    reject(error);

                });
        });
    },
    getIntrastatLinesData(params) {
        const QUERY_STRING = QueryString.stringify(params);
        const URL = vrRestApiHost + '/api/DataMining/IntrastatData' + (QUERY_STRING === '' ? '' : '?' + QUERY_STRING);
        log.debug("getIntrastatLinesData() - URL : " + URL);
        const REQUEST_INIT = {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache'
        };

        return new Promise((resolve, reject) => {
            fetch(URL, REQUEST_INIT)
                .then(errorHandler)
                .then((response) => {
                    log.debug("getIntrastatLinesData() - response: " + response);
                    return response.json();
                })
                .then((json) => {
                    resolve(json);
                })
                .catch((error) => {
                    log.debug("getIntrastatLinesData() - error: " + error);
                    reject(error);

                });
        });
    },
    getDeleteLogData(params) {
        const URL = vrRestApiHost + '/api/DataMining/DeleteLog' + '/' + encodeURIComponent(params.companyId) + '/' + encodeURIComponent(params.periodStartDate) + '/' + encodeURIComponent(params.periodEndDate) + '/' + encodeURIComponent(params.startAt) + '/' + encodeURIComponent(params.count) + '/' + encodeURIComponent(params.sortAscending);
        log.debug("getDeleteLogData() - URL : " + URL);
        return get(URL, sessionStorage.token);
    },
    getVAtCodesHistory(params) {
        const URL = vrRestApiHost + '/api/VATCodes/History/' + encodeURIComponent(params.companyId) + '/' + encodeURIComponent(params.periodStartDate) + '/' + encodeURIComponent(params.periodEndDate) + '/' + encodeURIComponent(params.startAt) + '/' + encodeURIComponent(params.count);
        log.debug("getVAtCodesHistory() - URL : " + URL);
        return get(URL, sessionStorage.token, (json, resolve, reject) => {
            resolve(filterGenericReportingCombinationHistory(json));
        });
    },
    getGLStatusOveriewData(params) {
        log.debug("getGLStatusOveriewData() - params : ", params);
        const updatedParams =
        {
            companyCode: params.companyCode,
            periodEndDate: params.periodEndDate,
            periodStartDate: params.periodStartDate,
            reportType: params.reportType,
            startAt: params.startAt,
            pageSize: params.pageSize,
            includeChildCompanies: params.includeChildCompanies ? 1 : 0
        };
        const QUERY_STRING = QueryString.stringify(updatedParams);
        var URL = '';
        if (params.overviewType === 1) //GL Side
            URL = vrRestApiHost + '/api/DataMining/GLStatusOverviewGL' + (QUERY_STRING === '' ? '' : '?' + QUERY_STRING);
        else //VR Side
            URL = vrRestApiHost + '/api/DataMining/GLStatusOverviewVR' + (QUERY_STRING === '' ? '' : '?' + QUERY_STRING);
        log.debug("getGLStatusOveriewData() - URL : " + URL);
        return get(URL, sessionStorage.token, (json, resolve, reject) => {
            resolve(json);
        });
    },
    doGLReconciliation() {
        const url = vrRestApiHost + '/api/DataMining/DoGLReconciliation';
        log.debug("DoGLReconciliation() - URL : " + URL);
        const requestInit = {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-cache',
            body: ""
        };
        return new Promise((resolve, reject) => {
            fetch(url, requestInit)
                .then((response) => {
                    log.debug('doGLReconciliation() - response: ', response);
                    return response.json();
                })
                .then((json) => {
                    log.debug('doGLReconciliation() - success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('doGLReconciliation() - error: ', error);
                    reject(error);
                });
        });
    },

    getAnalysis(apiParams = {}) {
        log.debug('getAnalysis() - apiParams: ', apiParams);
        const companyId = apiParams.companyId;
        const countryCode = apiParams.countryCode;
        const periodStartDate = apiParams.periodStartDate;
        const periodEndDate = apiParams.periodEndDate;
        const analysisType = apiParams.analysisType;
        const uri = vrRestApiHost + `/api/filing/types/analysis/${companyId}/data/${countryCode}/${periodStartDate}/${periodEndDate}/${analysisType}`;
        return get(uri, sessionStorage.token);
    },

    getAnalysisDetails(apiParams = {}) {
        log.debug('getAnalysis() - apiParams: ', apiParams);
        const companyId = apiParams.companyId || '';
        const countryCode = apiParams.countryCode || '';
        const periodStartDate = apiParams.periodStartDate || '';
        const periodEndDate = apiParams.periodEndDate || '';
        const analysisType = apiParams.analysisType;
        const startAt = apiParams.startAt || 1;
        const count = apiParams.count || 10;
        const vatCode = apiParams.vatCode || '';
        const queryParams = QueryString.stringify({
            currency: apiParams.currency || ''
        });
        let uri = `${vrRestApiHost}/api/filing/types/analysis/details/${companyId}/data/${countryCode}/${periodStartDate}/${periodEndDate}/${encodeURIComponent(vatCode)}/${analysisType}/${startAt}/${count}`;
        uri = queryParams ? `${uri}?${queryParams}` : uri;
        return get(uri, sessionStorage.token);
    },

    getManualDocuments(apiParams = {}) {
        log.debug('getManualDocuments() - apiParams: ', apiParams);
        const companyId = apiParams.companyId || '';
        const startDate = apiParams.startDate || '';
        const endDate = apiParams.endDate || '';
        const uri = vrRestApiHost + `/api/files/Manual?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}`;
        return get(uri, sessionStorage.token);
    },

    getManualDocumentsV2(apiParams = {}) {
        log.debug('getManualDocumentsV2() - apiParams: ', apiParams);
        const companyId = apiParams.companyId || '';
        const startDate = apiParams.startDate || '';
        const endDate = apiParams.endDate || '';
        const statusParams = buildRepeatedQueryParams('status', apiParams.status);
        const uri = vrRestApiHost + `/api/v2/files/Manual?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}${statusParams}`;
        return get(uri, sessionStorage.token);
    },

    createManualDocument(apiParams = {}) {
        log.debug('createManualDocument() - apiParams: ', apiParams);
        const uri = `${vrRestApiHost}/api/files/TransactionData`;
        return post(uri, sessionStorage.token, apiParams);
    },

    createManualDocumentLatest(apiParams = {}) {
        log.debug('createManualDocument-Latest() - apiParams: ', apiParams);
        const uri = `${vrRestApiHost}/api/v4/files/TransactionData`;
        return post(uri, sessionStorage.token, apiParams);
    },

    getUKMTDAuthorizationurl() {
        const URL = vrRestApiHost + '/api/efiling/uk/authorizationurl';
        log.debug("getUKMTDAuthorizationurl() - URL : ", URL);
        return get(URL, sessionStorage.token);
    },

    getUKMTDAccesstoken(authorizationCode, vatnumber, companyCode) {
        const URL = vrRestApiHost + '/api/efiling/uk/accesstoken/' + encodeURIComponent(authorizationCode) + '/' + encodeURIComponent(companyCode) + '/' + encodeURIComponent(vatnumber);
        log.debug("getUKMTDAccesstoken() - URL : ", URL);
        return get(URL, sessionStorage.token);
    },

    unauthenticateVATNumber(vatnumber) {
        const URL = vrRestApiHost + '/api/efiling/uk/unauthenticate/' + encodeURIComponent(vatnumber);
        log.debug("Unauthenticate() - URL : ", URL);
        return post(URL, sessionStorage.token, '');
    },
    getUKMTVATLiabilities(params) {
        log.debug("getUKMTVATLiabilities() - params : ", params);
        const URL = vrRestApiHost + '/api/efiling/uk/types/vat/liabilities/' + encodeURIComponent(params.startDate) + '/' + encodeURIComponent(params.endDate) + '/' + encodeURIComponent(params.companyCode) + '/' + encodeURIComponent(params.vatnumber);
        log.debug("getUKMTVATLiabilities() - URL : ", URL);
        var header = HeaderDetails.getFraudPreventionHeaders(sessionStorage.token);
        var statusCode = 0;
        return header.then((headerValue) => {
            return new Promise((resolve, reject) => {
                fetch(URL, {
                    method: 'GET',
                    mode: 'cors',
                    headers: headerValue,
                    cache: 'no-cache',
                })
                    .then((response) => {
                        log.debug('getUKMTVATLiabilities - response: ', response);
                        statusCode = response.status;
                        return response.json();
                    })
                    .then((json) => {
                        log.debug('getUKMTVATLiabilities - success: ', json, statusCode);
                        if (statusCode === 200)
                            resolve(json);
                        else {
                            if (json.errors !== undefined && json.errors.length > 0)
                                reject({ message: json.errors[0].message })
                            else
                                reject({ message: json.message })
                        }
                    })
                    .catch((error) => {
                        log.debug('getUKMTVATLiabilities- error: ', error);
                        reject(error);
                    });
            });
        });
    },
    getUKMTVATPayments(params) {
        const URL = vrRestApiHost + '/api/efiling/uk/types/vat/payments/' + encodeURIComponent(params.startDate) + '/' + encodeURIComponent(params.endDate) + '/' + encodeURIComponent(params.companyCode) + '/' + encodeURIComponent(params.vatnumber);
        log.debug("getUKMTVATPayments() - URL : ", URL);
        var statusCode = 0;
        var header = HeaderDetails.getFraudPreventionHeaders(sessionStorage.token);
        return header.then((headerValue) => {
            return new Promise((resolve, reject) => {
                fetch(URL, {
                    method: 'GET',
                    mode: 'cors',
                    headers: headerValue,
                    cache: 'no-cache',
                })
                    .then((response) => {
                        log.debug('getUKMTVATPayments - response: ', response);
                        statusCode = response.status;
                        return response.json();
                    })
                    .then((json) => {
                        log.debug('getUKMTVATPayments - success: ', json, statusCode);
                        if (statusCode === 200)
                            resolve(json);
                        else {
                            if (json.errors !== undefined && json.errors.length > 0)
                                reject({ message: json.errors[0].message })
                            else
                                reject({ message: json.message })
                        }
                    })
                    .catch((error) => {
                        log.debug('getUKMTVATPayments- error: ', error);
                        reject(error);
                    });
            });
        });
    },
    getUKMTObligations(params) {
        const URL = vrRestApiHost + '/api/efiling/uk/types/vat/obligations/' + encodeURIComponent(params.startDate) + '/' + encodeURIComponent(params.endDate) + '/' + encodeURIComponent(params.companyCode) + '/' + encodeURIComponent(params.vatnumber);
        log.debug("getUKMTObligations() - URL : ", URL);
        var statusCode = 0;
        var header = HeaderDetails.getFraudPreventionHeaders(sessionStorage.token);
        return header.then((headerValue) => {
            return new Promise((resolve, reject) => {
                fetch(URL, {
                    method: 'GET',
                    mode: 'cors',
                    headers: headerValue,
                    cache: 'no-cache',
                })
                    .then((response) => {
                        log.debug('getUKMTObligations - response: ', response);
                        statusCode = response.status;
                        return response.json();
                    })
                    .then((json) => {
                        log.debug('getUKMTObligations - success: ', json, statusCode);
                        if (statusCode === 200)
                            resolve(json);
                        else {
                            if (json.errors !== undefined && json.errors.length > 0)
                                reject({ message: json.errors[0].message })
                            else
                                reject({ message: json.message })
                        }
                    })
                    .catch((error) => {
                        log.debug('getUKMTObligations- error: ', error);
                        reject(error);
                    });
            });
        });
    },
    getUKMTVATReport(params) {
        const URL = vrRestApiHost + '/api/efiling/uk/types/vat/report/' + encodeURIComponent(params.startdate) + '/' + encodeURIComponent(params.enddate) + '/' + encodeURIComponent(params.companyCode) + '/' + encodeURIComponent(params.vatnumber);
        log.debug("getUKMTVATReport() - URL : ", URL);
        var statusCode = 0;
        var header = HeaderDetails.getFraudPreventionHeaders(sessionStorage.token);
        return header.then((headerValue) => {
            return new Promise((resolve, reject) => {
                fetch(URL, {
                    method: 'GET',
                    mode: 'cors',
                    headers: headerValue,
                    cache: 'no-cache',
                })
                    .then((response) => {
                        log.debug('getUKMTVATReport - response: ', response);
                        statusCode = response.status;
                        return response.json();
                    })
                    .then((json) => {
                        log.debug('getUKMTVATReport - success: ', json);
                        if (statusCode === 200)
                            resolve(json);
                        else {
                            if (json.errors !== undefined && json.errors.length > 0)
                                reject({ message: json.errors[0].message })
                            else
                                reject({ message: json.message })
                        }

                    })
                    .catch((error) => {
                        log.debug('getUKMTVATReport- error: ', error);
                        reject(error);
                    });
            });
        });
    },
    submitUKMTDVATReport(reportjson, params) {
        const URL = vrRestApiHost + '/api/efiling/uk/types/vat/report/' + encodeURIComponent(params.startdate) + '/' + encodeURIComponent(params.enddate) + '/' + encodeURIComponent(params.companyCode) + '/' + encodeURIComponent(params.vatnumber);
        log.debug("submitUKMTDVATReport() - URL : ", URL);
        var statusCode = 0;
        var header = HeaderDetails.getFraudPreventionHeaders(sessionStorage.token);
        return header.then((headerValue) => {
            //Due to errorHandler in post method, can't use that. Need to get error message text to show on UI
            return new Promise((resolve, reject) => {
                fetch(URL, {
                    method: 'POST',
                    mode: 'cors',
                    headers: headerValue,
                    cache: 'no-cache',
                    body: JSON.stringify(reportjson)
                })
                    .then((response) => {
                        log.debug('submitUKMTDVATReport - response: ', response);
                        statusCode = response.status;
                        return response.json();
                    })
                    .then((json) => {
                        log.debug('submitUKMTDVATReport - success: ', json, statusCode);
                        if (statusCode === 200)
                            resolve(json);
                        else {
                            const { error, errorCode, warning } = classifyMtdSubmissionResponse(json);
                            reject({
                                message: error || (warning ? '' : String(statusCode)),
                                status: statusCode,
                                errorCode,
                                ...(warning ? { warning } : {})
                            })
                        }
                    })
                    .catch((error) => {
                        log.debug('submitUKMTDVATReport- error: ', error);
                        reject(error);
                    });
            });
        });
    },
    // TODO refactor - not been done as part of SVR-5675
    // No utc required, they are passed already converted
    // Naming should be changed as this is getting the return file from s3
    getHistoryReport(params) {
        return new Promise((resolve, reject) => {
            if (params.reportType === FilingTypeEnum.VATBOOKS) {
                const { featureFlags } = getClientConfig();
                if (!featureFlags?.EnableReopenVATBook) {
                    return reject();
                }
            }
            log.debug("getHistoryReport() - params : ", params);
            let startDate = moment.utc((params.startDate + '').substring(0, 10)).format('YYYY-MM-DD');
            let endDate = moment.utc((params.endDate + '').substring(0, 10)).format('YYYY-MM-DD');
            const URL = vrRestApiHost + '/api/filing/types/' + encodeURIComponent(params.reportType || 0) + '/' + encodeURIComponent(params.companyId)
                + '/history/' + encodeURIComponent(params.fileName) + '/' + encodeURIComponent(params.country) + '/' + encodeURIComponent(startDate) + '/' + encodeURIComponent(endDate)
            // /2018-09-01/2018-09-30?IsFromS3=true';
            var contentType, fileName, reportType;
            log.debug("getHistoryReport() - URL : ", URL);


            return fetch(URL, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    if (response.status !== 200)
                        reject({ status: response.status });
                    log.debug('getHistoryReport: success: ', response.headers.get("content-type"));
                    log.debug('getHistoryReport: success: ', typeof response, response);
                    contentType = response.headers.get("content-type");
                    reportType = params.reportType;
                    fileName = ENUMS.getFilingNameFromFilingType(params.filingType) + ' ' + params.country + ' (' + startDate + '-' + endDate + ') ';
                    if (contentType === 'application/pdf' || contentType === 'application/zip') {
                        return response.blob();
                    }
                    else {
                        return response.text();
                    }
                })
                .then((data) => {
                    log.debug('getHistoryReport: got text: ', typeof data);
                    resolve({ data, contentType, fileName, reportType });
                })
                .catch((error) => {
                    log.debug('getHistoryReport: error:', error);
                    reject(error);
                });
        });
    },

    getTransactionComment(transactionId) {
        const URL = vrRestApiHost + '/api/transaction/comments/' + encodeURIComponent(transactionId);
        log.debug("getTransactionComment() - URL : ", URL);
        return get(URL, sessionStorage.token);
    },
    updateTransactionComment(transactionId, comment) {
        const URL = vrRestApiHost + '/api/transaction/comments/' + encodeURIComponent(transactionId);
        log.debug("updateTransactionComment() - URL : ", URL);
        return put(URL, sessionStorage.token, { comment });
    },
    deleteTransactionComment(transactionId) {
        const URL = vrRestApiHost + '/api/transaction/comments/' + encodeURIComponent(transactionId);
        log.debug("deleteTransactionComment() - URL : ", URL);
        return deletePromise(URL, sessionStorage.token);
    },
    getTransactionAttachment(transactionId) {
        const URL = vrRestApiHost + '/api/transaction/attachments/' + encodeURIComponent(transactionId);
        log.debug("getTransactionAttachment() - URL : ", URL);
        return get(URL, sessionStorage.token, (json, resolve, reject) => {
            resolve(addAttachmentUri(json));
        });
    },
    uploadTransactionAttachment(attachmentData) {
        const URL = vrRestApiHost + '/api/transaction/attachments';
        let data = new FormData();
        data.append(attachmentData.attachmentFile.name, attachmentData.attachmentFile);
        data.append('id', attachmentData.id);
        data.append('attachmentName', attachmentData.attachmentName);
        data.append('attachmentDescription', attachmentData.attachmentDescription);
        log.debug("uploadTransactionAttachment() - URL : ", URL);
        let statusCode;
        return new Promise((resolve, reject) => {

            return fetch(URL, {
                method: 'POST',
                mode: 'cors',
                body: data,
                cache: 'no-cache'
            })
                .then((response) => {
                    statusCode = response.status;
                    if (response.status === 406) {
                        reject({ status: response.status, statusText: 'Not acceptable' });
                    }
                    return response.json();
                })
                .then((json) => {
                    log.debug('uploadTransactionAttachment: success: ', URL, json);
                    if (statusCode === 200)
                        resolve(addAttachmentUri(json));
                    else
                        reject({ status: statusCode, message: json.message });
                })
                .catch((error) => {
                    log.debug('uploadTransactionAttachment: error:', URL, error);
                    reject(error);
                });
        });
    },
    downloadTransactionAttachment(type, uri) {
        log.debug('downloadTransactionAttachment(): type %s, uri:', type, uri);
        const mimeType = getMimeTypeFromDocumentType(type);
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'content-type': mimeType
                },
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('downloadTransactionAttachment: success: ', typeof response, response);
                    return response.blob();
                })
                .then((blob) => {
                    log.debug('downloadTransactionAttachment: got blob: ', typeof blob, blob);
                    resolve(blob)
                })
                .catch((error) => {
                    log.debug('downloadTransactionAttachment: error:', error);
                    reject(error);
                });
        });
    },
    deleteTransactionAttachment(transactionId) {
        const URL = vrRestApiHost + '/api/transaction/attachments/' + encodeURIComponent(transactionId);
        log.debug("deleteTransactionAttachment() - URL : ", URL);
        return deletePromise(URL, sessionStorage.token);
    },

    getVATBoxes(apiParams = {}) {
        log.debug('getAnalysis() - apiParams: ', apiParams);
        const params = {
            companyID: apiParams.companyId,
            countryCode: apiParams.countryCode,
            onDate: apiParams.onDate
        };
        const qs = QueryString.stringify(params);
        const uri = `${vrRestApiHost}/api/content/vatboxes?${qs}`;
        return get(uri, sessionStorage.token);
    },

    getChangesLog(params) {
        const URL = vrRestApiHost + '/api/DataMining/ChangesLog' + '/' + encodeURIComponent(params.companyId) + '/' + encodeURIComponent(params.periodStartDate) + '/' + encodeURIComponent(params.periodEndDate) + '/' + encodeURIComponent(params.startAt) + '/' + encodeURIComponent(params.count);
        log.debug("getChangesLog() - URL : " + URL);
        return get(URL, sessionStorage.token);
    },

    exchangeRatesRetrieveOnlineAndStore(apiParams = {}) {
        log.debug('exchangeRatesRetrieveOnlineAndStore(): apiParams: ', apiParams);
        const startDate = apiParams.startDate || '';
        const endDate = apiParams.endDate || '';
        const countries = Array.isArray(apiParams.countries) ? apiParams.countries : [apiParams.countries];
        const parallelLimit = Number.isNaN(apiParams.parallelLimit) ? 1 : apiParams.parallelLimit;
        log.debug('exchangeRatesRetrieveOnlineAndStore(): parallelLimit: ', parallelLimit);
        const urls = countries.map(countryCode => `${vrRestApiHost}/api/ExchangeRates/RetrieveOnline/${startDate}/${endDate}/${countryCode}`);
        log.debug('exchangeRatesRetrieveOnlineAndStore(): urls: ', urls);
        return new Promise((resolve, reject) => {
            asyncjs.parallelLimit(urls.map(url => {
                return (callback) => {
                    fetch(url, {
                        method: 'POST',
                        mode: 'cors',
                        cache: 'no-cache',
                        body: null
                    })
                        .then(errorHandler)
                        .then((response) => {
                            return response.json();
                        })
                        .then((json) => {
                            log.debug('get: success: ', url, json);
                            callback(null, json);
                        })
                        .catch((error) => {
                            log.debug('get: error:', url, error);
                            callback(error, null);
                        });
                };
            }), parallelLimit, (error, data) => {
                if (error) {
                    log.debug('exchangeRatesRetrieveOnlineAndStore().parallelLimit.callback: error: ', error);
                    return reject(error);
                }
                log.debug('exchangeRatesRetrieveOnlineAndStore().parallelLimit.callback: data: ', data);
                const exchangeRateResponse = Array.isArray(data) ? data : [];
                const allExchangeRates = exchangeRateResponse.reduce((accumulator, currentValue) => accumulator.concat(currentValue.exchangeRates), []);
                log.debug('exchangeRatesRetrieveOnlineAndStore().parallelLimit.callback: allExchangeRates: ', allExchangeRates);
                return resolve(allExchangeRates);
            });
        });
    },

    deleteExchangeRates() {
        const URL = `${vrRestApiHost}/api/ExchangeRates`;
        log.debug('deleteExchangeRates() - URL: ', URL);
        return deletePromise(URL, sessionStorage.token);
    },

    getExchangeRates(params = {}) {
        const URL = APIHelper.getURL(APIEndpoints.getExchangeRates, params);
        log.debug('getExchangeRates() - URL: ', URL);
        return APIHelper.get(URL, sessionStorage.token);
    },

    getApiLicenseKey() {
        const URL = APIHelper.getURL(APIEndpoints.getApiLicenseKey);
        log.debug('getApiLicenseKey() - URL: ', URL);
        return APIHelper.get(URL, sessionStorage.token);
    },

    getExchangeRatesFiltered(params = {}) {
        const URL = APIHelper.getURL(APIEndpoints.getExchangeRatesFiltered, params);
        log.debug('getExchangeRatesFiltered() - URL: ', URL);
        return APIHelper.get(URL, sessionStorage.token);
    },

    async downloadExchangeRatesCsv(params) {
        const url = APIHelper.getURL(APIEndpoints.getExchangeRatesExportCsv, params);

        log.debug('downloadExchangeRatesCsv(): fetching from: ', url);

        return getBlob(
            url,
            sessionStorage.token,
            'text/csv',
            (blob, resolve, reject) => {
                resolve(blob);
            },
            (error, resolve, reject) => {
                reject(error);
            });
    },

    getProcessedFileStatus(fileId) {
        const URL = vrRestApiHost + '/api/files/' + encodeURIComponent(fileId) + '/status';
        log.debug('getProcessedFileStatus() - URL: ', URL);
        return get(URL, sessionStorage.token);
    },
    getProcessedDocumentData(params) {
        const URL = vrRestApiHost + '/api/DataMining/ProcessingLog' + '/' + encodeURIComponent(params.periodStartDate) + '/' + encodeURIComponent(params.periodEndDate) + '/' + encodeURIComponent(params.startAt) + '/' + encodeURIComponent(params.count);
        log.debug("getProcessedDocumentData() - URL : " + URL);
        return get(URL, sessionStorage.token);
    },
    getSIILog(params) {
        const URL = vrRestApiHost + '/api/DataMining/siilogs' + '/' + encodeURIComponent(params.startDate) + '/' + encodeURIComponent(params.endDate) + '/' + encodeURIComponent(params.startAt) + '/' + encodeURIComponent(params.pageSize) + '/' + encodeURIComponent(params.sortAscending);;
        log.debug("getSIILog() - URL : " + URL);
        return get(URL, sessionStorage.token);
    },

    getSIITransactions(params) {
        // TODO remove fallback when API is available
        const URL = vrRestApiHost + '/api/datamining/siitransactions/' +
            encodeURIComponent(params.startDate) + '/' + encodeURIComponent(params.endDate) + '/' +
            encodeURIComponent(params.startAt) + '/' + encodeURIComponent(params.pageSize) + '/' +
            encodeURIComponent(params.sortAscending);

        log.debug('getSIITransactions: url ', URL);
        return get(URL, sessionStorage.token);
    },

    exportAccountSettings(callback) {
        log.debug('exportAccountSettings()')
        const URL = `${vrRestApiHost}/api/settings/export`
        return getBlobType(
            URL,
            sessionStorage.token,
            'application/xml',
            (blob, resolve, reject) => {
                resolve(blob);
            },
            (error, resolve, reject) => {
                reject(error);
            });
    },

    importAccountSettings(AccountSettings) {
        log.debug('importAccountSettings(): name ', AccountSettings.name);
        log.debug('importAccountSettings(): ', AccountSettings);
        log.clearAllDictionaryErrors();
        let data = new FormData();
        data.append(AccountSettings.name, AccountSettings);
        log.debug("importAccountSettings(): data", JSON.stringify(data));
        return new Promise((resolve, reject) => {
            fetch(vrRestApiHost + '/api/settings/import', {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                body: data
            })
                .then((response) => {
                    log.debug("importAccountSettings: response", response)
                    return response
                })
                .then((json) => {
                    if (json.status === 200) {
                        log.setDictionaryMessage(ALL_MESSAGES.FILE_IMPORT_SUCCESS);
                    } else {
                        json.text()
                            .then((errorMessage) => {
                                let validationMessage = FileValidationHelper.getFileValidationMessage(errorMessage);
                                if (validationMessage) {
                                    log.setDictionaryMessage(validationMessage);
                                } else {
                                    log.setDictionaryMessage(ALL_MESSAGES.FILE_IMPORT_ERROR);
                                }
                            });
                    }
                    log.debug('importAccountSettings: success', json);
                    resolve(json);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    // Submits a DE VAT filing to ERIC/Elster. The endpoint version is decided
    // upstream by the controller because it depends on Redux feature-flag
    // state, which the client layer intentionally does not read. `version`
    // is the URL version segment:
    //   - 'v6' for the async open-return-page flow
    //   - ''   for the legacy unversioned (sync) endpoint
    // Both endpoints share an identical multipart request/response contract.
    SubmitDEReport(file, reportUri, certificatePin, version = '') {
        log.debug('SubmitDEReport ', file);
        reportUri = reportUri.split("/").pop();
        let data = new FormData();
        data.append(file.name, file);
        data.append('fileURL', reportUri);
        data.append('PIN', btoa(certificatePin));
        const versionSegment = version ? `/${version}` : '';
        const URL = `${vrRestApiHost}/api${versionSegment}/efiling/de/types/vat/report`;
        log.debug('SubmitDEReport - version: %s, URL: %s', version, URL);
        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                body: data
            })
                .then((response) => {
                    log.debug('SubmitDEReport - success: ', response);
                    if (response.ok)
                        return resolve(response.blob());
                    else {
                        response.json()
                            .then((response) => {
                                log.debug('SubmitDEReport - error: ', response);
                                log.setDictionaryMessage(ALL_MESSAGES.VAT_RETURN_SUBMISSION_FAILED, response.message);
                                return reject(response);
                            })
                    }
                })
                .catch((error) => {
                    return reject(error);
                });
        });
    },
    // Submits an NL filing (VAT Return or EC Listing) to Digipoort.
    // The endpoint version is decided upstream by the controller because it
    // depends on Redux feature-flag state, which the client layer
    // intentionally does not read. `version` is the URL version segment:
    //   - 'v6' for the async open-return-page flow
    //   - ''   for the legacy unversioned (sync) endpoint
    // Both endpoints share an identical request/response contract.
    SubmitNLReport(filingParamsNL, reportType, version = '') {
        log.debug('SubmitNLReport - filingParamsNL ', filingParamsNL);
        const versionSegment = version ? `/${version}` : '';
        const URL = `${vrRestApiHost}/api${versionSegment}/efiling/nl/types/${reportType}/report`;
        log.debug('SubmitNLReport - version: %s, URL: %s', version, URL);
        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(filingParamsNL)
            })
                .then((response) => {
                    log.debug('SubmitNLReport - success: ', response);
                    return resolve(response.json());
                })
                .catch((error) => {
                    log.setDictionaryMessage(ALL_MESSAGES.SUBMIT_FILING_NL_ERROR, error);
                    return reject(error);
                });
        });
    },
    getNLSubmissionStatus(reportType, vatNumber, fromDate, toDate) {
        const URL = `${vrRestApiHost}/api/efiling/nl/types/${reportType}/status/${vatNumber}/${fromDate}/${toDate}`;
        log.debug('getNLSubmissionStatus() - URL: ', URL);
        return new Promise((resolve, reject) => {
            return fetch(URL, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'content-type': 'application/json'
                },
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug("getNLSubmissionStatus: response", response)
                    if (response.ok) {
                        return response.json();
                    } else {
                        return response;
                    }
                })
                .then((result) => {
                    log.debug('getNLSubmissionStatus: success', result);
                    resolve(result);
                })
                .catch((error) => {
                    log.debug('getNLSubmissionStatus error', error);
                    reject(error);
                });
        });
    },
    // Logs in to Digipoort with the given credentials, caching the resulting session
    // server-side (keyed by username+password) for subsequent generateXBRL/send calls.
    // Resolves with no payload on success (200); rejects with `{ message }` on failure.
    loginToDigipoort(loginParams) {
        const URL = `${vrRestApiHost}/api/v6/efiling/nl/loginToDigipoort`;
        log.debug('loginToDigipoort - URL: ', URL);
        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(loginParams)
            })
                .then((response) => {
                    if (response.ok) {
                        return resolve();
                    }
                    return response.json()
                        .catch(() => ({}))
                        .then((json) => reject(json));
                })
                .catch((error) => {
                    log.debug('loginToDigipoort - error: ', error);
                    reject(error);
                });
        });
    },
    // Generates the XBRL document (and accompanying audit report) for a previously
    // logged-in Digipoort session. Resolves with `{ xbrlDocument, auditReportContent }`,
    // both base64-encoded; rejects with `{ message }` on failure.
    generateXBRL(generateXbrlParams) {
        const URL = `${vrRestApiHost}/api/v6/efiling/nl/generateXBRL`;
        log.debug('generateXBRL - URL: ', URL, generateXbrlParams);
        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(generateXbrlParams)
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json().then((json) => resolve(json));
                    }
                    return response.json()
                        .catch(() => ({}))
                        .then((json) => reject(json));
                })
                .catch((error) => {
                    log.debug('generateXBRL - error: ', error);
                    reject(error);
                });
        });
    },
    // Deposits the previously generated XBRL document with Digipoort using the
    // logged-in session cached by loginToDigipoort. Resolves with `{ kenmerk, statusRows }`
    // (statusRows entries: `statusCode, statusDescription, errorCode, errorDescription, timestamp`);
    // rejects with `{ message }` on failure.
    sendToDigipoort(sendToDigipoortParams) {
        const URL = `${vrRestApiHost}/api/v6/efiling/nl/sendToDigipoort`;
        log.debug('sendToDigipoort - URL: ', URL, sendToDigipoortParams);
        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(sendToDigipoortParams)
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json().then((json) => resolve(json));
                    }
                    return response.json()
                        .catch(() => ({}))
                        .then((json) => reject(json));
                })
                .catch((error) => {
                    log.debug('sendToDigipoort - error: ', error);
                    reject(error);
                });
        });
    },
    // Retrieves the Digipoort user submission history for the given credentials.
    // Resolves with `{ rows }` (row entries: `kenmerk, vatNumber, documentName, periodStartDate,
    // periodEndDate, createdDatetime, statusCode, statusDescription, errorCode, errorDescription,
    // statusTimestamp`); rejects with `{ message }` on failure.
    getUserSubmissionHistory(userSubmissionHistoryParams) {
        const URL = `${vrRestApiHost}/api/v6/efiling/nl/userSubmissionHistory`;
        log.debug('getUserSubmissionHistory - URL: ', URL, userSubmissionHistoryParams);
        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userSubmissionHistoryParams)
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json().then((json) => resolve(json));
                    }
                    return response.json()
                        .catch(() => ({}))
                        .then((json) => reject(json));
                })
                .catch((error) => {
                    log.debug('getUserSubmissionHistory - error: ', error);
                    reject(error);
                });
        });
    },
    // Refreshes the status for a single transaction (kenmerk) using the cached Digipoort session.
    // Resolves with an object: { transactionId, conditions, statusCode, statusDescription, errorCode, errorDescription, timestamp }
    refreshTransactionStatus(refreshParams) {
        const URL = `${vrRestApiHost}/api/v6/efiling/nl/refreshTransactionStatus`;
        log.debug('refreshTransactionStatus - URL: ', URL, refreshParams);
        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(refreshParams)
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json().then((json) => resolve(json));
                    }
                    return response.json()
                        .catch(() => ({}))
                        .then((json) => reject(json));
                })
                .catch((error) => {
                    log.debug('refreshTransactionStatus - error: ', error);
                    reject(error);
                });
        });
    },
    // Closes the cached Digipoort session for the given credentials. Resolves with no payload on success.
    closeDigipoortSubmission(closeParams) {
        const URL = `${vrRestApiHost}/api/v6/efiling/nl/closeDigipoortSubmission`;
        log.debug('closeDigipoortSubmission - URL: ', URL, closeParams);
        return new Promise((resolve, reject) => {
            fetch(URL, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(closeParams)
            })
                .then((response) => {
                    if (response.ok) {
                        return resolve();
                    }
                    return response.json()
                        .catch(() => ({}))
                        .then((json) => reject(json));
                })
                .catch((error) => {
                    log.debug('closeDigipoortSubmission - error: ', error);
                    reject(error);
                });
        });
    },
    getReliabilityCheckVATNumberCheck(vatNumbers) {
        let requestJson = JSON.stringify(Object.assign({ VATNumbers: vatNumbers }));
        log.debug('getReliabilityCheckVATNumberCheck', requestJson);
        const useVatUtilitiesService = getClientConfig().featureFlags?.UseVatUtilitiesService;
        const vatNumbersUrl = `${vrRestApiHost}/api/vATNumbers/reliabilitycheck`;
        const vatUtilityV2Url = `${vrRestApiHost}/vat-utilities/api/v2/vatNumbers/reliability`;
        const url = useVatUtilitiesService ? vatUtilityV2Url : vatNumbersUrl;

        return new Promise((resolve, reject) => {
            return fetch(url, {
                method: 'POST',
                mode: 'cors',
                cache: 'no-cache',
                headers: {
                    'content-type': 'application/json'
                },
                body: requestJson
            })
                .then((response) => {
                    log.debug("getReliabilityCheckVATNumberCheck: response", response)
                    return response.json()
                })
                .then((json) => {
                    log.debug('getReliabilityCheckVATNumberCheck: success', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('getReliabilityCheckVATNumberCheck error', error);
                    reject(error);
                });
        });
    },

    /**
     * Returns processing stats
     */
    getProcessingStats({ showOnHoldInvoiceNumbers = false, startAt = 1, count = 10 } = {}) {
        const queryParams = showOnHoldInvoiceNumbers
            ? {
                showOnHoldInvoiceNumbers: 'true',
                startAt: startAt,
                count: count
            } : null;
        const URL = APIHelper.getURL(APIEndpoints.getProcessingStats, queryParams);
        log.debug('getProcessingStats() - URL: ', URL);
        return APIHelper.get(URL, sessionStorage.token);
    },

    /**
     * Returns a list of countries on hold
     */
    getCountriesOnHold(startAt, count, sortAscending) {
        const params = {
            startAt: startAt,
            count: count,
            sortAscending: sortAscending
        };

        const qs = QueryString.stringify(params);
        const endpointUrl = APIHelper.getURL(APIEndpoints.getCountriesOnHold);
        const url = `${endpointUrl}?${qs}`;

        log.debug('getCountriesOnHold() - URL: ', url);

        return APIHelper.get(url, sessionStorage.token);
    },

    /**
     * Removes the lock/s from a single "On Hold" country
     * @param {string} countryCode The ISO Country Code of the country to be "unlocked"
     * @param {string} lockType The type of lock to be removed, "Logistical", "Financial" or "Both"
     */
    removeCountryOnHoldLock(countryCode, lockType) {
        let operations = [];

        if (lockType === 'Financial' || lockType === 'Both') {
            operations.push({ "op": "replace", "path": "/onHold/financialDataOnHoldFrom", "value": "" });
        }

        if (lockType === 'Logistical' || lockType === 'Both') {
            operations.push({ "op": "replace", "path": "/onHold/logisticalDataOnHoldFrom", "value": "" });
        }

        if (!operations) {
            throw new Error('No valid locktype specified');
        }

        const endpointUrl = APIHelper.getURL(APIEndpoints.unlockCountryOnHold);
        const url = `${endpointUrl}/${countryCode}`;

        log.debug('removeCountryOnHoldLock() - URL: ', url);

        let requestJson = JSON.stringify(operations);

        return fetch(url, {
            method: 'PATCH',
            mode: 'cors',
            cache: 'no-cache',
            body: requestJson,
            completed: true,
            headers: {
                'content-type': 'application/json'
            }
        });
    },

    /**
     * Adds a lock/s for a single "On Hold" country
     * @param {string} countryCode The ISO Country Code of the country to be "locked"
     * @param {string} lockType The type of lock to be added, "Logistical", "Financial" or "Both"
     * @param {string} lockDate The date from which the lock will apply in the format yyyy-mm-dd
     */
    addCountryOnHoldLock(countryCode, lockType, lockDate) {
        let operations = [];

        if (lockType === 'Financial' || lockType === 'Both') {
            operations.push({ "op": "replace", "path": "/onHold/financialDataOnHoldFrom", "value": lockDate });
        }

        if (lockType === 'Logistical' || lockType === 'Both') {
            operations.push({ "op": "replace", "path": "/onHold/logisticalDataOnHoldFrom", "value": lockDate });
        }

        if (!operations) {
            throw new Error('No valid locktype specified');
        }

        const endpointUrl = APIHelper.getURL(APIEndpoints.unlockCountryOnHold);
        const url = `${endpointUrl}/${countryCode}`;

        log.debug('addCountryOnHoldLock() - URL: ', url);

        let requestJson = JSON.stringify(operations);

        return fetch(url, {
            method: 'PATCH',
            mode: 'cors',
            cache: 'no-cache',
            body: requestJson,
            completed: true,
            headers: {
                'content-type': 'application/json'
            }
        });
    },

    /**
     * Makes a request for a return to be generated in the background
     *
     * @param {object} returnDetails
     */
    generateReturns(returnDetails = {}) {
        const URL = APIHelper.getURL(APIEndpoints.createReturns);
        log.debug('getReturns() - URL: ', URL);
        return APIHelper.post(URL, sessionStorage.token, returnDetails);
    },

    /**
  * Gets the return generation location.
  * In case of returns generation is <in progress> it returns response with location, extension with operation details /api/operations/:uid
  * In case of returns generation is <completed> it returns response location, extension with returns details /api/returns/:uid.
  * In case of returns not found (404) is returned.
  *
  * @param {object} options
  * @returns {Promise}
  */
    getReturns({ countyCode = '', companyId = '', periodStartDate = '', periodEndDate = '', frequency = '', returnDefinition = '', language = '', reportType } = {}) {
        const { enableAvalaraIdentity, enableAvalaraIdentityJwt } = getClientConfig();

        const queryParams = {
            CountryCode: countyCode,
            CompanyId: companyId,
            PeriodStartDate: periodStartDate,
            PeriodEndDate: periodEndDate,
            Frequency: frequency,
            ReturnDefinition: returnDefinition,
            Language: language
        };
        if (reportType !== undefined && reportType !== '') {
            queryParams.ReportType = reportType;
        }

        const URL = APIHelper.getURL((enableAvalaraIdentity || enableAvalaraIdentityJwt) ? APIEndpoints.getReturnsForAI : APIEndpoints.getReturns, queryParams);
        log.debug('getReturns() - URL: ', URL);
        return APIHelper.get(URL, sessionStorage.token, (response, resolve, reject) => {
            //Not found
            if (response.status === 404) {
                reject({
                    notFound: true
                });
            } else {
                let location = response.headers.get("location");
                log.debug('getReturns() - location: ', location);
                if (location === null) {
                    reject({
                        missingHeader: true
                    });
                } else {
                    response.json().then((json) => {
                        json.location = location;
                        json.mimeType = getMimeTypeFromDocumentType(json?.extension?.slice(1).toLowerCase());
                        resolve(json);
                    });
                    log.debug('getReturns - 200: ', response);
                }
            }
        }, (response, resolve, reject) => {
            //let location = "/api/returns";
            // resolve({
            //   location: location,
            //   locationType: location.includes("/returns") ? "returns": "opertaions"
            // });
            let error = response;
            if (response.status === 404) {
                error.notFound = true
            }
            reject(error);
        }, true);
    },

    /**
     * Sets the IsBoxValueAutoCorrected flag on an existing return so the
     * backend can asynchronously apply box-value auto-corrections.
     *
     * Mirrors the parameter shape used by getReturns; isBoxValueAutoCorrected
     * is supplied by the caller so this method can be reused for both opt-in
     * (true) and revert (false) flows.
     *
     * @param {object} params
     * @param {string} params.countryCode
     * @param {string|number} params.companyId
     * @param {string} params.periodStartDate ISO date
     * @param {string} params.periodEndDate ISO date
     * @param {number|string} params.frequency
     * @param {string} params.returnDefinition
     * @param {boolean} params.isBoxValueAutoCorrected
     * @param {string} params.language backend identity language; blank is forwarded as ''
     * @returns {Promise}
     */
    updateBoxValueAutoCorrectFlag({
        countryCode = '',
        companyId = '',
        periodStartDate = '',
        periodEndDate = '',
        frequency = '',
        returnDefinition = '',
        isBoxValueAutoCorrected = false,
        language = '',
    } = {}) {
        const url = vrRestApiHost + APIEndpoints.updateBoxValueAutoCorrectFlag;
        log.debug('updateBoxValueAutoCorrectFlag() - URL: ', url);

        const body = JSON.stringify({
            countryCode,
            companyId,
            periodStartDate,
            periodEndDate,
            frequency,
            returnDefinition,
            isBoxValueAutoCorrected: !!isBoxValueAutoCorrected,
            language,
        });

        return fetch(url, {
            method: 'PATCH',
            mode: 'cors',
            cache: 'no-cache',
            body,
            completed: true,
            headers: {
                'content-type': 'application/json-patch+json'
            }
        }).then((response) => {
            if (!response.ok) {
                return Promise.reject(response);
            }
            return response;
        });
    },

    /**
     * Downloads the returns documment as a blob
     */
    getReturnsDocument(documentType = '', url = '') {
        log.debug('getReturnsDocument() - URL: ', url);
        const mimeType = getMimeTypeFromDocumentType(documentType);
        const enableSIVatbookZipDownload = getClientConfig().featureFlags?.EnableSIVatbookZipDownload;
        const versionedUrl = enableSIVatbookZipDownload && url.includes('/api/returns/') && !url.includes('/preview')
            ? url.replace('/api/returns/', '/api/v2/returns/')
            : url;
        return APIHelper.getBlob(versionedUrl, sessionStorage.token, mimeType);
    },

    getRawFormXmlForReturn(guid = '') {
        const url = APIHelper.getURL(APIEndpoints.getReturnRawFormXml, {}, { guid });
        log.debug('getRawFormXmlForReturn() - URL: ', url);
        return APIHelper.getBlob(url, sessionStorage.token, 'application/xml');
    },

    /**
     * Returns a list of frequencies
     */
    getContentData() {
        const endpointUrl = APIHelper.getURL(APIEndpoints.getContentEnum);
        log.debug('getFrequency() - URL: ', endpointUrl);
        return APIHelper.get(endpointUrl, sessionStorage.token);
    },
    getOSSReports(params) {
        const url = APIHelper.getURL(APIEndpoints.getReport);
        log.debug('getOSSReports(): params: ', params);
        var uri = [url, params.reportType, params.companyId, ReportUrl, params.country, params.dueDateBefore, params.dueDateAfter, params.returnDisplayType, params.grouped === "1" ? true : false].join('/');

        log.debug('getOSSReports: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    getOSSReportsLatest(params) {
        const url = APIHelper.getURL(APIEndpoints.getReportLatest);
        log.debug('getOSSReportsLatest(): params: ', params);
        const uri = [url, params.reportType, params.companyId, ReportUrl, params.country, params.dueDateBefore, params.dueDateAfter, params.returnDisplayType, params.grouped].join('/');

        log.debug('getOSSReportsLatest: fetching from: ', uri);
        return get(uri, sessionStorage.token);
    },

    saveToWorkflow(params) {
        const URL = APIHelper.getURL(APIEndpoints.saveToWorkflow);
        log.debug('saveToWorkflow() - URL: ', URL);
        log.debug('saveToWorkflow() - Params: ', params);
        return APIHelper.post(URL, sessionStorage.token, params);
    },

    /**
     * Initiates auto submission workflow for returns
     * Calls the BFF endpoint which generates a unique key and sends the nservicebus message
     */
    initiateAutoSubmission(params) {
        const URL = APIHelper.getURL(APIEndpoints.initiateAutoSubmission);
        log.debug('initiateAutoSubmission() - URL: ', URL);
        log.debug('initiateAutoSubmission() - Params: ', params);

        return APIHelper.post(URL, sessionStorage.token, params)
            .then(response => {
                log.debug('initiateAutoSubmission() - Response: ', response);
                return {
                    data: {
                        Configuration: {
                            message: response.message,
                            workflowId: response.workflowId,
                            processDefinitionKey: response.processDefinitionKey,
                            tenantId: response.tenantId,
                            processDetails: response.processDetails
                        }
                    }
                };
            })
            .catch(error => {
                log.error('initiateAutoSubmission() - Error: ', error);
                throw error;
            });
    },

    /**
     * Checks whether a direct-submission workflow is already in progress for the
     * given company/period/return, so the UI can disable the initiate button.
     */
    checkWorkflowInProgress(params) {
        const queryParams = {
            companyId: params.companyId,
            countryCode: params.countryCode,
            startDate: params.startDate,
            endDate: params.endDate,
            frequency: params.frequency,
            returnType: params.returnType
        };
        const URL = APIHelper.getURL(APIEndpoints.checkWorkflowInProgress, queryParams);
        log.debug('checkWorkflowInProgress() - URL: ', URL);
        log.debug('checkWorkflowInProgress() - Params: ', params);

        return APIHelper.get(URL, sessionStorage.token)
            .then(response => {
                log.debug('checkWorkflowInProgress() - Response: ', response);
                return response;
            })
            .catch(error => {
                log.warn('checkWorkflowInProgress() - Error: ', error);
                throw error;
            });
    },

    getCanUserSaveToWorkflow() {
        const URL = APIHelper.getURL(APIEndpoints.getCanUserSaveToWorkflow);
        log.debug('getCanUserSaveToWorkflow() - URL: ', URL);
        return APIHelper.get(URL, sessionStorage.token);
    },

    getReopenableClosedReturns() {
        const URL = APIHelper.getURL(APIEndpoints.getReopenableClosedReturns);
        log.debug('getReopenableClosedReturns() - URL: ', URL);

        // Temp code until correctives modal has api endpoint
        const { enableCorrectiveReturnsTestData } = getClientConfig();
        if (enableCorrectiveReturnsTestData) return APIHelper.dummy(URL, sessionStorage.token);

        return APIHelper.get(URL, sessionStorage.token);
    },

    /**
     * Reads which paid features the caller's tenant is entitled to.
     */
    getEntitlements() {
        const URL = APIHelper.getURL(APIEndpoints.getEntitlements);
        log.debug('getEntitlements() - URL: ', URL);

        return APIHelper.get(URL, sessionStorage.token);
    },

    getFeatureFlags() {
        const { enableV3FeatureFlags } = getClientConfig();
        const version = enableV3FeatureFlags ? 'v3' : 'v2';
        const url = vrRestApiHost + `/bffapi/${version}/FeatureFlags`;
        return get(url, sessionStorage.token).then((response) =>
            enableV3FeatureFlags ? response.value : response
        );
    },

    exportAllToCSV(params) {
        log.debug(`restClient.exportAllToCSV(${JSON.stringify(params)})`);
        const uri = `${vrRestApiHost}/reporting/api/v1/tenants/${params.customerId}/reports/transactions`;
        return makeRequestForExport(uri, 'POST', params, 'exportAllToCSV');
    },

    exportAllDynamicReport(params) {
        log.debug(`restClient.exportAllDynamicReport(${JSON.stringify(params)})`);
        const useDynamicReportingExportApi = getClientConfig().featureFlags?.UseDynamicReportingExportApi;
        if (useDynamicReportingExportApi) {
            return this.exportAllDynamicReportViaReportingApi(params);
        }
        const uri = `${vrRestApiHost}/reporting/api/v1/tenants/${params.customerId}/reports/dynamic`;
        return makeRequestForExport(uri, 'POST', params, 'exportAllDynamicReport');
    },

    exportAllDynamicReportViaReportingApi(params) {
        log.debug(`restClient.exportAllDynamicReportViaReportingApi(${JSON.stringify(params)})`);
        const { dynamicReportingExportApiHost, enableAvalaraIdentity, enableAvalaraIdentityJwt } = getClientConfig();
        const uri = `${dynamicReportingExportApiHost}${APIEndpoints.getLatestDynamicReportExport}`;
        const payload = buildDynamicReportExportPayload(params);
        const headers = buildDynamicReportExportHeaders(enableAvalaraIdentity, enableAvalaraIdentityJwt);

        return fetchWithOnlyJwtAuthHeader(uri, {
            method: 'POST',
            mode: 'cors',
            headers,
            body: JSON.stringify(payload),
            cache: 'no-cache',
        })
            .then(async (response) => {
                const json = await response.json();
                log.debug('exportAllDynamicReportViaReportingApi: response:', json);
                if (!response.ok) {
                    throw new Error(getSuperApiErrorMessage(json));
                }
                return json;
            })
            .catch((error) => {
                log.debug('exportAllDynamicReportViaReportingApi: error:', error);
                throw error;
            });
    },

    exportReportToWorkflow(params) {
        log.debug(`restClient.exportReportToWorkflow (${JSON.stringify(params)})`);
        const uri = `${vrRestApiHost}/reporting/api/v1/tenants/${params.tenantId}/reports/saveToWorkflow`;
        return makeRequestForExport(uri, 'POST', params, 'exportReportToWorkflow ');
    },

    exportTransactions(params) {
        const uri = `${vrRestApiHost}/reporting/api/v1/tenants/${params.tenantId}/reports/returns`
        log.debug('exportTransactions: fetching from: ', uri);
        return makeRequestForExport(uri, 'POST', params, 'exportTransactions');
    },

    exportErrorWarningToCSV(params) {
        const enableErrorsAndWarningsExportV2 = getClientConfig().featureFlags?.EnableErrorsAndWarningsExportV2;
        if (enableErrorsAndWarningsExportV2) {
            const uri = `${vrRestApiHost}/reporting/api/v1/tenants/${params.tenantId}/ErrorsAndWarningsReport`;
            log.debug('exportErrorWarningToCSV: fetching from: ', uri);
            return makeRequestForExportV2(uri, 'POST', params, 'exportErrorWarningToCSV');
        } else {
            const uri = `${vrRestApiHost}/reporting/api/v1/tenants/${params.tenantId}/reports/errorswarnings`;
            log.debug('exportErrorWarningToCSV: fetching from: ', uri);
            return makeRequestForExport(uri, 'POST', params, 'exportErrorWarningToCSV');
        }
    },

    exportSelectedErrorWarningToCSV(params) {
        const uri = `${vrRestApiHost}/reporting/api/v2/tenants/${params.tenantId}/ErrorsAndWarningsReport`;
        log.debug('exportSelectedErrorWarningToCSV: fetching from: ', uri);
        return makeRequestForExportV2(uri, 'POST', params, 'exportSelectedErrorWarningToCSV');
    },

    exportAllDataForLedger(params) {
        const uri = `${vrRestApiHost}/reporting/api/v1/tenants/${params.tenantId}/reports/ledger`;
        log.debug('exportAllDataForLedger: fetching from: ', uri);
        return makeRequestForExport(uri, 'POST', params, 'exportAllDataForLedger');
    },

    getSalesAndPurchaseLedger(params) {
        log.debug('getSalesAndPurchaseLedger() - Params: ', params);
        let URL = APIHelper.getURL(APIEndpoints.getSalesPurchaseLedgers, {
            CompanyId: params.companyId,
            CountryCode: params.countryCode,
            StartDate: params.startDate,
            EndDate: params.endDate,
            LedgerType: params.ledgerType,
            Translated: params.translated,
            PageNumber: params.pageNumber,
            PageSize: params.pageSize
        });
        return makeRequestForExport(URL, 'GET', sessionStorage.token);
    },

    getReportSpecification(enableDetailedSFLogging) {
        log.debug('getReportSpecification() - Params: ', enableDetailedSFLogging);
        const endpoint = enableDetailedSFLogging ? APIEndpoints.getReportSpecificationsV4 : APIEndpoints.getReportSpecifications;
        log.debug('getReportSpecification() - EnableAdditionalLoggingInSF: ', enableDetailedSFLogging);
        const URL = APIHelper.getURL(endpoint);
        return makeRequestForExport(URL, 'GET', sessionStorage.token);
    },

    getReportSpecificationV5() {
        return makeRequestForExport(
            APIHelper.getURL(APIEndpoints.getReportSpecificationsV5),
            'GET',
            sessionStorage.token
        );
    },

    getLatestDynamicReportDefinitions(userName, tenantId) {
        log.debug('getLatestDynamicReportsData()');
        const params = {
            userName,
            tenantId
        };
        return makeRequestForExport(
            APIHelper.getURL(APIEndpoints.getLatestDynamicReportDefinitions, {
                'api-version': 1
            }),
            'POST',
            params,
            'getLatestDynamicReportDefinitions',
            {
                "x-avalara-client": "svrui"
            }
        );
    },

    getLatestDynamicReportsData(params) {
        log.debug('getLatestDynamicReportsData() - Params: ', params);
        const endpoint = APIEndpoints.getLatestDynamicReportsData;
        let URL = APIHelper.getURL(endpoint, {
            'api-version': 1
        });

        return makeRequestForExport(
            URL,
            'POST',
            params,
            'getLatestDynamicReportsData',
            {
                "x-avalara-client": "svrui"
            }
        );
    },

    getDynamicReportsData(params, enableDetailedSFLogging) {
        log.debug('getDynamicReportsData() - Params: ', params);
        const endpoint = enableDetailedSFLogging ? APIEndpoints.getDynamicReportsDataV4 : APIEndpoints.getDynamicReportsData;
        log.debug('getDynamicReportsData() - EnableAdditionalLoggingInSF: ', enableDetailedSFLogging);
        let URL = APIHelper.getURL(endpoint, null, {
            reportType: params.reportType
        });
        return APIHelper.post(URL, sessionStorage.token, params,
            (response, resolve, reject) => {
                if (response.statuCode && response.statuCode !== 200) {
                    reject(response);
                } else {
                    resolve(response);
                }
            },
            (response, resolve, reject) => {
                reject(response);
            }
        );
    },

    closeVatBook(requestBody) {
        log.debug('closeVatBook(): requestBody:', requestBody);
        const uri = APIHelper.getURL(APIEndpoints.closeVatBook);
        log.debug('closeVatBook(): uri:', uri);

        return new Promise(
            (resolve, reject) => {
                return fetch(uri, {
                    method: 'POST',
                    mode: 'cors',
                    cache: 'no-cache',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestBody),
                })
                    .then((response) => {
                        if (response.status === 200) {
                            return resolve(response);
                        }

                        response.text().then(responseText => reject({ status: response.status, statusText: responseText }));
                    });
            }
        );
    },

    async closeReturn(requestBody) {
        log.debug('closeReturn(): requestBody:', requestBody);
        const uri = APIHelper.getURL(APIEndpoints.closeReturn);
        log.debug('closeReturn(): uri:', uri);

        const response = await fetch(uri, {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody),
        });

        if (response.status === 200) {
            return response;
        }

        const responseText = await response.text();
        const error = new Error(`closeReturn failed with status ${response.status}: ${responseText}`);
        error.status = response.status;
        error.statusText = responseText;
        throw error;
    },

    // Raw fetch (not APIHelper) so a 503 doesn't raise the generic connection toast and
    // the error body ({code,message} or [{field,message}]) reaches the caller.
    async parseReturnApprovalResponse(response) {
        const responseText = await response.text();
        let body = responseText;
        try { body = responseText ? JSON.parse(responseText) : null; } catch (e) { /* keep the raw text */ }
        if (response.ok) return body;
        throw Object.assign(new Error(`ReturnApprovals failed with status ${response.status}`), { status: response.status, body });
    },

    async getReturnApprovalStatus(params) {
        const uri = APIHelper.getURL(APIEndpoints.getReturnApprovalStatus, params);
        log.debug('getReturnApprovalStatus(): uri:', uri);
        return this.parseReturnApprovalResponse(await fetch(uri, { method: 'GET', mode: 'cors', cache: 'no-cache' }));
    },

    async sendReturnForApproval(requestBody) {
        log.debug('sendReturnForApproval(): requestBody:', requestBody);
        return this.parseReturnApprovalResponse(await fetch(APIHelper.getURL(APIEndpoints.sendReturnForApproval), {
            method: 'POST', mode: 'cors', cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        }));
    },

    loadIntrastatCommercialSummary(companyId, countryCode, startDate, endDate, intrastatReportType) {
        const params = {
            companyId,
            countryCode,
            startDate,
            endDate,
            intrastatReportType
        };
        log.debug('loadIntrastatCommercialSummary() - Params: ', params);
        const uri = APIHelper.getURL(APIEndpoints.getIntrastatCommercialSummary, {}, params);
        log.debug('loadIntrastatCommercialSummary(): uri:', uri);

        return new Promise(
            (resolve, reject) => {
                return fetch(uri, {
                    method: 'GET',
                    mode: 'cors',
                    cache: 'no-cache'
                })
                    .then((response) => {
                        if (response.status === 200) {
                            return resolve(response.json());
                        }

                        response.text().then(responseText => reject({ status: response.status, statusText: responseText }));
                    });
            }
        );
    },

    getItalyLookups() {
        const uri = `${vrRestApiHost}/api/v4/Companies/italyLookups`;
        log.debug('getItalyLookups: fetching from: ', uri);

        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => {
                    log.debug('getItalyLookups: response: ', response);
                    if (!response.ok) {
                        reject({ status: response.status });
                        return;
                    }
                    return response.json();
                })
                .then((json) => {
                    log.debug('getItalyLookups: success: ', json);
                    resolve(json);
                })
                .catch((error) => {
                    log.debug('getItalyLookups: error:', error);
                    reject(error);
                });
        });
    },

    getDynamicMenu(enableSideNavigation) {
        const { enableV3FeatureFlags } = getClientConfig();
        let version = 'v1';
        if (enableSideNavigation) {
            version = enableV3FeatureFlags ? 'v3' : 'v2';
        }
        const url = vrRestApiHost + `/bffapi/${version}/Navigation`;
        log.debug('getDynamicMenu: fetching from: ', url);
        return get(url, sessionStorage.token);
    },

    async getCompanyPermissions(tenantId) {
        const url = APIHelper.getURL(APIEndpoints.getCompanyPermissions, null, { tenantId });
        log.debug('getCompanyPermissions() - Fetching from: ', url);
        try {
            const response = await fetch(url, {
                method: 'GET',
                mode: 'cors'
            });

            log.debug('getCompanyPermissions() - Response: ', response);
            if (!response.ok) {
                throw new Error({ status: response.status });
            }

            const json = await response.json();
            log.debug('getCompanyPermissions() - Success: ', json);
            return json;
        }
        catch (error) {
            log.debug('getCompanyPermissions() - Error:', error);
            throw error; // Re-throw the error to be handled by the controller
        }
    },

    async updateCompanyPermissionsForUser(tenantId, userId, userCompanyIds) {
        const url = APIHelper.getURL(APIEndpoints.updateCompanyPermissionsForUser, null, { tenantId, userId });
        log.debug('updateCompanyPermissionsForUser() - Updating company grants for user: ', userId);

        try {
            const response = await fetch(url, {
                method: 'PUT',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userCompanyIds)
            });

            log.debug('updateCompanyPermissionsForUser() - Response: ', response);
            if (!response.ok) {
                throw new Error({ status: response.status });
            }

            log.debug('updateCompanyPermissionsForUser() - Success: ', response);
            return response;
        }
        catch (error) {
            log.debug('updateCompanyPermissionsForUser() - Error:', error);
            throw error; // Re-throw the error to be handled by the controller
        }
    },

    async getIamUsers(iamSystemId, iamTenantId, top, skip) {
        const baseUrl = `${iamApiHost}/iam/tenants/${iamTenantId}/users`;

        // Returns only the first 'top' users, skipping the first 'skip' from those users that have access to the SVR system
        const queryParams = {
            $grantFilter: `system/identifier eq '${iamSystemId}'`,
            top: top,
            skip: skip
        };

        const queryString = QueryString.stringify(queryParams);
        const url = `${baseUrl}?${queryString}`;
        log.debug('getIamUsers() - Fetching users from: ', url);

        try {
            const response = await fetchWithOnlyJwtAuthHeader(url, {
                method: 'GET',
                mode: 'cors'
            });

            log.debug('getIamUsers() - Response: ', response);
            if (!response.ok) {
                throw new Error({ status: response.status });
            }

            const json = await response.json();
            log.debug('getIamUsers() - Success: ', json);
            return json;
        }
        catch (error) {
            log.debug('getIamUsers() - Error:', error);
            throw error; // Re-throw the error to be handled by the controller
        }
    },

    async getIamSystemId(iamNamespace) {
        const url = `${iamApiHost}/iam/systems?filter=namespace eq '${iamNamespace}'`;
        log.debug('getIamSystemId() - Fetching system id from: ', url);

        try {
            const response = await fetchWithOnlyJwtAuthHeader(url, {
                method: 'GET',
                mode: 'cors'
            });

            log.debug('getIamSystemId() - Response: ', response);
            if (!response.ok) {
                throw new Error({ status: response.status });
            }

            const json = await response.json();
            log.debug('getIamSystemId() - Success: ', json);
            return json;
        }
        catch (error) {
            log.debug('getIamSystemId() - Error:', error);
            throw error; // Re-throw the error to be handled by the controller
        }
    },

    async getIamGrants(iamSystemId) {
        const url = `${iamApiHost}/iam/grants?filter=system/identifier eq '${iamSystemId}'&top=200`;
        log.debug('getIamGrants() - Fetching grants from: ', url);

        try {
            const response = await fetchWithOnlyJwtAuthHeader(url, {
                method: 'GET',
                mode: 'cors'
            });

            log.debug('getIamGrants() - Response: ', response);
            if (!response.ok) {
                throw new Error(`getIamGrants failed with status ${response.status}`);
            }

            const json = await response.json();
            log.debug('getIamGrants() - Success: ', json);
            return json;
        }
        catch (error) {
            log.debug('getIamGrants() - Error:', error);
            throw error;
        }
    },

    async createIamGrant(grantBody) {
        const url = `${iamApiHost}/iam/grants`;
        log.debug('createIamGrant() - Creating custom grant: ', url);

        try {
            const response = await fetchWithOnlyJwtAuthHeader(url, {
                method: 'POST',
                mode: 'cors',
                headers: { 'Content-Type': 'application/json', 'avalara-version': '1.0' },
                body: JSON.stringify(grantBody)
            });

            log.debug('createIamGrant() - Response status: ', response.status);
            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`createIamGrant failed (${response.status}): ${errorBody}`);
            }

            const json = await response.json();
            log.debug('createIamGrant() - Success, id: ', json.id);
            return json;
        }
        catch (error) {
            log.debug('createIamGrant() - Error:', error);
            throw error;
        }
    },

    async assignGrantToIamUser(iamTenantId, userId, grantId) {
        const url = `${iamApiHost}/iam/tenants/${iamTenantId}/users/${encodeURIComponent(userId)}/grants/${grantId}`;
        log.debug('assignGrantToIamUser() - Assigning grant: ', url);

        try {
            const response = await fetchWithOnlyJwtAuthHeader(url, {
                method: 'PUT',
                mode: 'cors'
            });

            log.debug('assignGrantToIamUser() - Response: ', response);
            if (!response.ok) {
                throw new Error(`assignGrantToIamUser failed with status ${response.status}`);
            }

            log.debug('assignGrantToIamUser() - Success');
            return response;
        }
        catch (error) {
            log.debug('assignGrantToIamUser() - Error:', error);
            throw error;
        }
    },

    async removeGrantFromIamUser(iamTenantId, userId, grantId) {
        const url = `${iamApiHost}/iam/tenants/${iamTenantId}/users/${encodeURIComponent(userId)}/grants/${grantId}`;
        log.debug('removeGrantFromIamUser() - Removing grant: ', url);

        try {
            const response = await fetchWithOnlyJwtAuthHeader(url, {
                method: 'DELETE',
                mode: 'cors'
            });

            log.debug('removeGrantFromIamUser() - Response: ', response);
            if (!response.ok && response.status !== 404) {
                throw new Error(`removeGrantFromIamUser failed with status ${response.status}`);
            }

            log.debug('removeGrantFromIamUser() - Success');
            return response;
        }
        catch (error) {
            log.debug('removeGrantFromIamUser() - Error:', error);
            throw error;
        }
    },

    async getIamUserGrants(iamTenantId, userId) {
        const url = `${iamApiHost}/iam/tenants/${iamTenantId}/users/${encodeURIComponent(userId)}/grants`;
        log.debug('getIamUserGrants() - Fetching user grants: ', url);

        try {
            const response = await fetchWithOnlyJwtAuthHeader(url, {
                method: 'GET',
                mode: 'cors'
            });

            log.debug('getIamUserGrants() - Response: ', response);
            if (!response.ok) {
                throw new Error(`getIamUserGrants failed with status ${response.status}`);
            }

            const json = await response.json();
            log.debug('getIamUserGrants() - Success: ', json);
            return json;
        }
        catch (error) {
            log.debug('getIamUserGrants() - Error:', error);
            throw error;
        }
    },

    async updateIamGrant(grantId, grantBody) {
        const url = `${iamApiHost}/iam/grants/${grantId}`;
        log.debug('updateIamGrant() - Updating grant: ', url);

        try {
            const response = await fetchWithOnlyJwtAuthHeader(url, {
                method: 'PUT',
                mode: 'cors',
                headers: { 'Content-Type': 'application/json', 'avalara-version': '1.0' },
                body: JSON.stringify(grantBody)
            });

            log.debug('updateIamGrant() - Response status: ', response.status);
            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`updateIamGrant failed (${response.status}): ${errorBody}`);
            }

            log.debug('updateIamGrant() - Success');
            return response;
        }
        catch (error) {
            log.debug('updateIamGrant() - Error:', error);
            throw error;
        }
    },
};

export default API_CLIENT;
